import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ShoppingCart, Search, LogOut, Receipt, LayoutGrid, Plus, Minus, Trash2, X, Check, AlertCircle, Loader2, FileText, Euro, RefreshCw, Eye, Zap, Package, User, CreditCard, Banknote, ArrowLeft, Percent, Edit3, Printer, Download, Mail, RotateCcw, DollarSign, Wallet, TrendingUp, BarChart3, Settings, XCircle, CheckCircle, Archive, BookOpen, Save, PlusCircle, Building, Users, Clock, Send, Coffee, Move, Square, Circle, Armchair, ChevronRight, GripVertical, Maximize2, ToggleLeft, ToggleRight, Hash, Utensils, Home, Menu, WifiOff, Hotel, Bed, Camera, Upload, Shield, Globe, MapPin, DoorOpen, DoorClosed, Star, Wrench, UserPlus, UserCheck, UserX, ChevronDown, ChevronUp, Filter, Calendar } from "lucide-react";

const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port, pr = window.location.protocol;
  // Electron desktop (file:// protocol)
  if (pr === "file:") return "http://localhost/fiscalization/api";
  // Browser dev (localhost with port)
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  // Capacitor native app (localhost, no port)
  if (h === "localhost" && !p) {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "http://10.0.2.2/fiscalization/api";
    return "http://localhost/fiscalization/api"; // iOS simulator
  }
  // Production — API always on main domain
  return "https://efis.pro/api";
})();

// ═══ OFFLINE CACHE SYSTEM ═══
const CACHE_PREFIX = "pos_cache_";
const OFFLINE_DRAFTS_KEY = "pos_offline_drafts";
function cacheSet(key, data) { try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() })); } catch(e) {} }
function cacheGet(key) { try { const raw = localStorage.getItem(CACHE_PREFIX + key); if (!raw) return null; return JSON.parse(raw).data; } catch { return null; } }
function getOfflineDrafts() { try { return JSON.parse(localStorage.getItem(OFFLINE_DRAFTS_KEY) || "[]"); } catch { return []; } }
function saveOfflineDraft(payload) {
  const drafts = getOfflineDrafts();
  drafts.push({ id: Date.now(), payload, created_at: new Date().toISOString() });
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
  return drafts.length;
}
function removeOfflineDraft(id) {
  const drafts = getOfflineDrafts().filter(d => d.id !== id);
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
}

async function api(path, opts = {}) {
  const token = localStorage.getItem("pos_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const fetchPromise = fetch(`${API_BASE}${path}`, { headers: h, ...opts });
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000));
    const r = await Promise.race([fetchPromise, timeoutPromise]);
    if (r.status === 401) { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); window.location.reload(); return { success: false }; }
    const data = await r.json();
    // Cache GET responses
    if (!opts.method || opts.method === "GET") {
      if (path === "/products" && data.success) cacheSet("products", data.data);
      if (path.includes("/categories") && data.success) cacheSet("categories", data.data);
      if (path === "/partners" && data.success) cacheSet("partners", data.data);
      if (path === "/auth/company-config" && data.success) cacheSet("config", data.data);
      if (path === "/tables/settings" && data.success) cacheSet("tables_settings", data.data);
      if (path === "/tables" && data.success) cacheSet("tables", data.data);
    }
    return data;
  } catch (e) {
    // Offline: return cached data for GET requests
    if (!opts.method || opts.method === "GET") {
      if (path === "/products") { const c = cacheGet("products"); if (c) return { success: true, data: c, _offline: true }; }
      if (path.includes("/categories")) { const c = cacheGet("categories"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/partners") { const c = cacheGet("partners"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/auth/company-config") { const c = cacheGet("config"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/tables/settings") { const c = cacheGet("tables_settings"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/tables") { const c = cacheGet("tables"); if (c) return { success: true, data: c, _offline: true }; }
    }
    return { success: false, message: "Network error: " + e.message, _offline: true };
  }
}
function fmt(n) { return parseFloat(n || 0).toFixed(2); }
function fmtDate(d) { if (!d) return "-"; return d.split(" ")[0].split("-").reverse().join("."); }
function fmtDateTime(d) { if (!d) return "-"; const [dt, tm] = d.split(" "); return dt.split("-").reverse().join(".") + " " + (tm || "").slice(0, 5); }
function getTcr() { return localStorage.getItem("pos_tcr") || ""; }

// ─── Print / PDF / Email helpers ───
// ═══ UNIFIED PRINT SYSTEM ═══
// Detects platform: Electron (native printer) / Capacitor (system print) / Browser (popup print)
const isElectron = !!(window.electronAPI?.isElectron);
const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());

async function openPdf(path) {
  const token = localStorage.getItem("pos_token");
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: { "Authorization": `Bearer ${token}` } });
    if (!r.ok) throw new Error("PDF error");
    const ct = r.headers.get("content-type") || "";
    if (ct.includes("text/html")) {
      const html = await r.text();
      const win = window.open("", "_blank", "width=400,height=700");
      if (win) { win.document.write(html); win.document.close(); }
    } else {
      const blob = await r.blob();
      if (isElectron) {
        // Electron: show PDF in full-screen overlay with iframe
        const reader = new FileReader();
        reader.onload = function() {
          const dataUrl = reader.result;
          const overlay = document.createElement("div");
          overlay.id = "pdf-overlay";
          overlay.style.cssText = "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.9);display:flex;flex-direction:column";
          const bar = document.createElement("div");
          bar.style.cssText = "display:flex;justify-content:space-between;align-items:center;padding:10px 16px;background:#1e293b;flex-shrink:0";
          bar.innerHTML = '<span style="color:white;font-weight:600;font-size:15px">PDF Pregled</span>';
          const btnDiv = document.createElement("div");
          btnDiv.style.cssText = "display:flex;gap:8px";
          // Download button
          const dlBtn = document.createElement("a");
          dlBtn.href = dataUrl;
          dlBtn.download = path.includes("openingPdf") ? "otvaranje.pdf" : path.includes("closingPdf") ? "zatvaranje.pdf" : "racun.pdf";
          dlBtn.textContent = "⬇ Sačuvaj";
          dlBtn.style.cssText = "padding:8px 16px;background:#3b82f6;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;text-decoration:none";
          btnDiv.appendChild(dlBtn);
          // Close button
          const closeBtn = document.createElement("button");
          closeBtn.textContent = "✕ Zatvori";
          closeBtn.style.cssText = "padding:8px 16px;background:#ef4444;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer";
          closeBtn.onclick = function() { document.getElementById("pdf-overlay")?.remove(); };
          btnDiv.appendChild(closeBtn);
          bar.appendChild(btnDiv);
          overlay.appendChild(bar);
          const iframe = document.createElement("iframe");
          iframe.src = dataUrl;
          iframe.style.cssText = "flex:1;width:100%;border:none;background:white";
          overlay.appendChild(iframe);
          document.body.appendChild(overlay);
        };
        reader.readAsDataURL(blob);
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    }
  } catch (e) { return false; }
  return true;
}

// Print receipt — uses best available method per platform
async function printReceipt(invoiceId) {
  const token = localStorage.getItem("pos_token");

  // Method 1: Electron — native ESC/POS via IPC
  if (isElectron && window.electronAPI) {
    try {
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        const text = await r.text();
        const result = await window.electronAPI.printText(text);
        return result.success;
      }
    } catch (e) { console.log("Electron print failed:", e); }
  }

  // Method 2: Network print server (for mobile/tablet)
  const printerType = localStorage.getItem("pos_printer_type");
  const printerAddr = localStorage.getItem("pos_printer_addr");
  if (printerType === "network" && printerAddr) {
    try {
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        const text = await r.text();
        const printUrl = (printerAddr || "").endsWith("/") ? printerAddr.slice(0,-1) + "/print/text" : printerAddr + "/print/text";
        const pr = await fetch(printUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text })
        });
        const result = await pr.json();
        return result.success;
      }
    } catch (e) { console.log("Network print failed:", e); }
  }

  // Method 3: All platforms — HTML receipt popup with system print dialog
  return printReceiptHTML(invoiceId);
}

// HTML receipt printing — works everywhere (browser, Capacitor, Electron fallback)
async function printReceiptHTML(invoiceId) {
  const token = localStorage.getItem("pos_token");
  try {
    const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=html`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) return false;
    const html = await r.text();

    if (isElectron && window.electronAPI?.printHTML) {
      await window.electronAPI.printHTML(html);
      return true;
    }

    // Browser / Capacitor — open print-styled popup
    const width = parseInt(localStorage.getItem("pos_paper_width") || localStorage.getItem("minipos_paper_width") || "80");
    const printWidth = width <= 58 ? "58mm" : "80mm";
    const win = window.open("", "print_receipt", `width=350,height=600`);
    if (win) {
      win.document.write(`<html><head><style>@page{size:${printWidth} auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;width:${printWidth};}@media print{body{width:${printWidth};}}</style></head><body>${html}<script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
      win.document.close();
    }
    return true;
  } catch (e) { return false; }
}

// Print cash register report (opening/closing)
async function printRegisterReport(sessionId, type) {
  const token = localStorage.getItem("pos_token");
  let endpoint;
  if (type === "opening") endpoint = "openingPdf";
  else if (type === "closing") endpoint = "closingPdf";
  else if (type === "internal_closing") endpoint = "closingPdf";
  else endpoint = "closingPdf";

  // Method 1: Electron — ESC/POS thermal via text endpoint
  if (isElectron && window.electronAPI) {
    try {
      const r = await fetch(`${API_BASE}/cashregister/${endpoint}/${sessionId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        const text = await r.text();
        await window.electronAPI.printText(text);
        return true;
      }
    } catch (e) { console.log("Thermal print failed:", e); }
  }

  // Method 2: HTML print fallback
  try {
    const r = await fetch(`${API_BASE}/cashregister/${endpoint}/${sessionId}?type=html`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) return false;
    const html = await r.text();
    const win = window.open("", "print_report", "width=350,height=500");
    if (win) {
      win.document.write(`<html><head><style>@page{size:80mm auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;}</style></head><body>${html}<script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
      win.document.close();
    }
    return true;
  } catch { return false; }
}

// Legacy compatibility — used throughout the codebase
async function thermalPrint(endpoint, data) {
  // If Electron, try native print for register reports
  if (isElectron && window.electronAPI && endpoint === "/print/text" && data.text) {
    try { const r = await window.electronAPI.printText(data.text); return r.success; } catch {}
  }
  // Fallback: try old print server if configured
  const addr = localStorage.getItem("pos_printer_addr") || "http://localhost:9100";
  try {
    const r = await fetch(`${addr}${endpoint}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    const d = await r.json();
    return d.success;
  } catch { return false; }
}

async function checkPrintServer() {
  if (isElectron && window.electronAPI) {
    try { const r = await window.electronAPI.testConnection(); return r.success; } catch {}
  }
  const addr = localStorage.getItem("pos_printer_addr") || "http://localhost:9100";
  try {
    const r = await fetch(`${addr}/status`, { signal: AbortSignal.timeout(2000) });
    const d = await r.json();
    return d.success;
  } catch { return false; }
}

const S = {
  font: "'DM Sans', -apple-system, sans-serif", mono: "'JetBrains Mono', monospace",
  bg: "#f6f6f7", dark: "#1a1a2e", accent: "#4f46e5", green: "#059669",
  red: "#dc2626", orange: "#ea580c", border: "#e5e7eb", muted: "#6b7280",
  input: { width: "100%", padding: "12px 14px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 15, outline: "none", background: "#f9fafb", fontFamily: "'DM Sans', sans-serif" },
  btn: (bg, color = "white") => ({ padding: "12px 20px", background: bg, color, border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif" }),
  btnSm: (bg, color = "white") => ({ padding: "9px 16px", background: bg, color, border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'DM Sans', sans-serif" }),
};
const th = { padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 13, borderBottom: "1px solid #e5e7eb" };
const td = { padding: "12px 14px", color: "#1a1a2e", fontSize: 14 };

function Row({ label, value, last }) {
  return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: last ? 0 : 10 }}><span style={{ color: S.muted, fontSize: 14 }}>{label}</span><span style={{ fontSize: 14, color: S.dark }}>{value}</span></div>;
}
function CatTab({ active, onClick, label }) {
  return <button onClick={onClick} style={{ padding: "8px 18px", border: "1px solid", borderColor: active ? S.accent : S.border, background: active ? S.accent : "white", color: active ? "white" : S.muted, borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: S.font }}>{label}</button>;
}
function StatCard({ icon: Icon, label, value, color, hidden }) {
  return (
    <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={17} color={color} /></div>
        <span style={{ fontSize: 14, color: S.muted, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: S.dark }}>{hidden ? "••••••" : value}</div>
    </div>
  );
}
function Stat({ label, value }) { return <div><div style={{ fontSize: 13, color: S.muted }}>{label}</div><div style={{ fontSize: 16, fontWeight: 600, color: S.dark }}>{value}</div></div>; }
function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: 14, width: 440, maxHeight: "80vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}><X size={18} color={S.muted} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
function Pagination({ pg, onChange }) {
  return (
    <div style={{ padding: "10px 20px", borderTop: `1px solid ${S.border}`, background: "white", display: "flex", justifyContent: "center", gap: 4 }}>
      {Array.from({ length: Math.min(pg.total_pages, 10) }, (_, i) => i + 1).map(p => (
        <button key={p} onClick={() => onChange(p)} style={{ width: 32, height: 32, border: "1px solid", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, borderColor: pg.page === p ? S.dark : S.border, background: pg.page === p ? S.dark : "white", color: pg.page === p ? "white" : S.muted, fontFamily: S.font }}>{p}</button>
      ))}
    </div>
  );
}
function Badge({ status }) {
  const m = { fiscalized: { bg: "#f0fdf4", c: "#15803d", l: "Fiskalizovano" }, draft: { bg: "#fefce8", c: "#a16207", l: "Draft" }, failed: { bg: "#fef2f2", c: S.red, l: "Neuspjelo" }, storno: { bg: "#f3f4f6", c: S.muted, l: "Storno" }, cancelled: { bg: "#f3f4f6", c: S.muted, l: "Otkazano" } };
  const s = m[status] || m.draft;
  return <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 12, fontWeight: 600, background: s.bg, color: s.c }}>{s.l}</span>;
}
function DiscountForm({ current, onApply, onClose }) {
  const [type, setType] = useState(current.type === "none" ? "percentage" : current.type);
  const [value, setValue] = useState(current.value || "");
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => setType("percentage")} style={{ ...S.btn(type === "percentage" ? S.accent : "#f3f4f6", type === "percentage" ? "white" : S.muted), flex: 1, justifyContent: "center" }}><Percent size={15} />Procenat</button>
        <button onClick={() => setType("fixed")} style={{ ...S.btn(type === "fixed" ? S.accent : "#f3f4f6", type === "fixed" ? "white" : S.muted), flex: 1, justifyContent: "center" }}><Euro size={15} />Iznos</button>
      </div>
      <input type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)} placeholder={type === "percentage" ? "npr. 10" : "npr. 5.00"} style={{ ...S.input, marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => onApply({ type, value: parseFloat(value) || 0 })} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>Primijeni</button>
      </div>
    </div>
  );
}
function StornoForm({ onStorno, onClose }) {
  const [reason, setReason] = useState("");
  const reasons = [{ id: "STORNO_OP", l: "Greška operatera" }, { id: "STORNO_BUYER", l: "Zahtjev kupca" }, { id: "STORNO_RETURN", l: "Povrat robe" }];
  return (
    <div>
      <p style={{ fontSize: 15, color: S.muted, marginBottom: 14 }}>Izaberite razlog storniranja:</p>
      {reasons.map(r => (
        <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 5, cursor: "pointer", background: reason === r.id ? "#f0fdf4" : "#f9fafb", border: `1px solid ${reason === r.id ? S.green : S.border}` }}>
          <input type="radio" name="reason" checked={reason === r.id} onChange={() => setReason(r.id)} style={{ accentColor: S.green, width: 16, height: 16 }} />
          <span style={{ fontSize: 15 }}>{r.l}</span>
        </label>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => reason && onStorno(reason)} disabled={!reason} style={{ ...S.btn(S.red), flex: 1, justifyContent: "center", opacity: reason ? 1 : 0.5 }}><RotateCcw size={15} />Storniraj</button>
      </div>
    </div>
  );
}
function EmailForm({ onSend, onClose }) {
  const [email, setEmail] = useState("");
  return (
    <div>
      <label style={{ fontSize: 14, fontWeight: 600 }}>Email adresa</label>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kupac@email.com" style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => email && onSend(email)} disabled={!email} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center", opacity: email ? 1 : 0.5 }}><Mail size={15} />Pošalji</button>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    const data = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password, app: "pos" }) });
    setLoading(false);
    data.success ? (() => { localStorage.removeItem("pos_tcr"); onLogin(data.data.token, data.data.operator); })() : setError(data.message || "Login failed");
  };
  return (
    <div style={{ minHeight: "100vh", background: S.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: S.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{ width: 420, background: "white", borderRadius: 16, boxShadow: "0 2px 20px rgba(0,0,0,0.06)", padding: "44px 36px", border: `1px solid ${S.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: S.dark, margin: 0 }}>EFIS.PRO <span style={{ color: "#8b5cf6", fontWeight: 500 }}>Hotel POS</span></h1>
          <p style={{ color: S.muted, fontSize: 15, marginTop: 4 }}>Prijavite se na vaš nalog</p>
        </div>
        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: S.dark, marginBottom: 5 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="operator@company.me" style={{ ...S.input, marginBottom: 16 }} />
          <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: S.dark, marginBottom: 5 }}>Lozinka</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && submit(e)} style={{ ...S.input, marginBottom: 22 }} />
          {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><AlertCircle size={15} color={S.red} /><span style={{ color: S.red, fontSize: 14 }}>{error}</span></div>}
          <button type="submit" disabled={loading} style={{ ...S.btn(S.dark), width: "100%", padding: "13px", fontSize: 16, justifyContent: "center", opacity: loading ? 0.7 : 1 }}>{loading ? "Prijava..." : "Prijavi se"}</button>
        </form>
      </div>
    </div>
  );
}

function POSApp({ user, onLogout }) {
  const [view, setView] = useState("home");
  const [toast, setToast] = useState(null);
  const [tablesEnabled, setTablesEnabled] = useState(false);
  const [tableForOrder, setTableForOrder] = useState(null); // {sessionId, tableName} when ordering for a table
  const [online, setOnline] = useState(navigator.onLine !== false);
  const [syncing, setSyncing] = useState(false);
  const suppressToastRef = useRef(false);
  const showToast = (msg, type = "success") => {
    if (suppressToastRef.current && msg.includes("Offline mod")) return; // Don't overwrite draft toast
    setToast({ message: msg, type }); setTimeout(() => setToast(null), msg.includes("Draft") || msg.includes("lokalno") ? 6000 : 4000);
  };

  // Online/offline detection + auto-sync
  const wasOfflineRef = useRef(false);
  const onlineRef = useRef(true);
  const syncDrafts = async () => {
    const drafts = getOfflineDrafts();
    if (drafts.length === 0) return;
    setSyncing(true);
    let synced = 0;
    for (const draft of drafts) {
      try {
        const d = await api("/invoices", { method: "POST", body: JSON.stringify(draft.payload) });
        if (d.success) { removeOfflineDraft(draft.id); synced++; }
      } catch(e) {}
    }
    setSyncing(false);
    const remaining = getOfflineDrafts().length;
    if (synced > 0) showToast(`Ponovo online! ${synced} draft račun${synced > 1 ? "a" : ""} sačuvan${synced > 1 ? "o" : ""} u bazi.`, "success");
    if (remaining > 0) showToast(`${remaining} draft nije uspjelo sinhronizovati.`, "error");
  };
  useEffect(() => {
    const goOnline = () => { setOnline(true); onlineRef.current = true; };
    const goOffline = () => { setOnline(false); onlineRef.current = false; wasOfflineRef.current = true; showToast("Offline mod - podaci iz keša", "error"); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const poll = setInterval(async () => {
      const token = localStorage.getItem("pos_token");
      if (!token) return;
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        const r = await fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, signal: controller.signal });
        clearTimeout(timeout);
        if (r.status !== 0) {
          if (!onlineRef.current || wasOfflineRef.current) {
            setOnline(true); onlineRef.current = true;
            if (wasOfflineRef.current) {
              wasOfflineRef.current = false;
              const drafts = getOfflineDrafts();
              if (drafts.length > 0) { showToast(`Ponovo online! Sinhronizujem ${drafts.length} draft${drafts.length > 1 ? "ova" : ""}...`, "success"); setTimeout(syncDrafts, 1000); }
              else showToast("Ponovo online!", "success");
            }
          }
        }
      } catch(e) {
        if (onlineRef.current) { setOnline(false); onlineRef.current = false; wasOfflineRef.current = true; showToast("Offline mod - podaci iz keša", "error"); }
      }
    }, 8000);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); clearInterval(poll); };
  }, []);

  // Check tables setting on mount
  useEffect(() => {
    api("/tables/settings").then(r => { if (r.success) setTablesEnabled(!!r.data?.tables_enabled); });
  }, []);

  const navTop = [
    { id: "home", icon: Home, label: "Početna" },
    ...(tablesEnabled ? [{ id: "stolovi", icon: Coffee, label: "Stolovi" }] : []),
    { id: "pos", icon: LayoutGrid, label: "Nova Prodaja" },
    { id: "invoices", icon: FileText, label: "Računi" },
    { id: "hotel", icon: Hotel, label: "Hotel" },
  ];
  const navBottom = [
    { id: "blagajna", icon: Wallet, label: "Blagajna" },
    { id: "virman", icon: Building, label: "Virman" },
    { id: "dashboard", icon: BarChart3, label: "Izvještaji" },
    { id: "settings", icon: Settings, label: "Podešavanja" },
  ];

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // When returning from table order, go back to stolovi
  const handleTableOrderDone = (invoiceData) => {
    setTableForOrder(null);
    setView("stolovi");
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: S.font, background: S.bg, paddingBottom: (!window.location.port && window.location.hostname === "localhost") ? 10 : 0 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}*{box-sizing:border-box}input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}::-webkit-scrollbar{width:6px}::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}`}</style>

      {/* Sidebar overlay */}
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 900, animation: "fadeIn 0.2s" }} />}

      {/* Sidebar drawer */}
      <div style={{ position: "fixed", left: sidebarOpen ? 0 : -280, top: 0, bottom: 0, width: 280, background: S.dark, zIndex: 950, transition: "left 0.25s ease", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, background: "rgba(255,255,255,0.1)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}><Receipt size={17} color="#a5b4fc" /></div>
            <div><div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>EFIS.PRO</div><div style={{ color: "#a78bfa", fontSize: 12 }}>Hotel POS</div></div>
          </div>
          <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}><X size={18} color="#8c8c9a" /></button>
        </div>

        {/* Top nav items */}
        <nav style={{ flex: 1, padding: "10px 8px", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1 }}>
            {navTop.map(item => (
              <button key={item.id} onClick={() => { setView(item.id); setSidebarOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 14px", border: "none", borderRadius: 8, cursor: "pointer", marginBottom: 3, fontSize: 15, fontWeight: 500, background: view === item.id ? "rgba(165,180,252,0.12)" : "transparent", color: view === item.id ? "#a5b4fc" : "#8c8c9a", fontFamily: S.font }}>
                <item.icon size={19} />{item.label}
              </button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 8 }}>
            {navBottom.map(item => (
              <button key={item.id} onClick={() => { setView(item.id); setSidebarOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 14px", border: "none", borderRadius: 8, cursor: "pointer", marginBottom: 3, fontSize: 15, fontWeight: 500, background: view === item.id ? "rgba(165,180,252,0.12)" : "transparent", color: view === item.id ? "#a5b4fc" : "#8c8c9a", fontFamily: S.font }}>
                <item.icon size={19} />{item.label}
              </button>
            ))}
          </div>
        </nav>

        {/* User + logout */}
        <div style={{ padding: "14px 12px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 30, height: 30, background: "#2d2d44", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}><User size={14} color="#a5b4fc" /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "white", fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
              <div style={{ color: "#6b6b80", fontSize: 11 }}>{user.company_name}</div>
            </div>
          </div>
          <button onClick={onLogout} style={{ ...S.btnSm("rgba(220,38,38,0.1)", "#f87171"), width: "100%", justifyContent: "center" }}><LogOut size={13} />Odjavi se</button>
        </div>
      </div>

      {/* Main content area — full width */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative", display: "flex", flexDirection: "column" }}>
        {/* Safe area spacer for Capacitor native apps */}
        {!window.location.port && window.location.hostname === "localhost" && <div style={{ height: 28, background: "white", flexShrink: 0 }} />}
        {/* Top bar with hamburger */}
        <div style={{ height: 48, background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 12, flexShrink: 0 }}>
          <button onClick={() => setSidebarOpen(true)} style={{ background: "none", border: "none", cursor: "pointer", padding: 10, borderRadius: 8, display: "flex", alignItems: "center" }}>
            <Menu size={26} color={S.dark} />
          </button>
          <div style={{ fontWeight: 700, fontSize: 16, color: S.dark }}>
            {view === "home" ? "EFIS.PRO" : (navTop.concat(navBottom).find(n => n.id === view)?.label || "EFIS.PRO")}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 12, color: S.muted }}>{user.name}</div>
        </div>

        {/* Offline indicator */}
        {(!online || syncing) && <div style={{ background: !online ? "#ef4444" : S.accent, color: "white", textAlign: "center", padding: "5px 12px", fontSize: 12, fontWeight: 600, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {syncing ? <><RefreshCw size={13} className="spin" />Sinhronizacija draft računa...</> : <><WifiOff size={13} />Offline — podaci iz keša</>}
        </div>}

        {/* Views */}
        <div style={{ flex: 1, overflow: "hidden" }}>
        {view === "home" && <HomeView user={user} onNavigate={(v) => setView(v)} />}
        {view === "stolovi" && <StoloviView user={user} showToast={showToast} onOrderForTable={(s) => { setTableForOrder(s); setView("pos"); }} />}
        {view === "pos" && <POSView user={user} showToast={showToast} tableForOrder={tableForOrder} onTableOrderDone={handleTableOrderDone} online={online} />}
        {view === "invoices" && <InvoicesView user={user} showToast={showToast} />}
        {view === "hotel" && <HotelView user={user} showToast={showToast} />}
        {view === "blagajna" && <BlagajnaView user={user} showToast={showToast} />}
        {view === "dashboard" && <DashboardView user={user} showToast={showToast} />}
        {view === "virman" && <VirmanView user={user} showToast={showToast} online={online} />}
        {view === "settings" && <SettingsView user={user} showToast={showToast} onTablesToggle={setTablesEnabled} />}
        </div>
        {toast && (<div style={{ position: "fixed", bottom: 24, right: 24, padding: "14px 20px", borderRadius: 10, background: toast.type === "success" ? S.green : toast.type === "error" ? S.red : S.dark, color: "white", fontSize: 15, fontWeight: 500, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.2)", zIndex: 9999, animation: "slideUp 0.3s ease" }}>{toast.type === "success" ? <Check size={16} /> : <AlertCircle size={16} />}{toast.message}</div>)}
      </div>
    </div>
  );
}


// ═══ HOME VIEW (Landing/Standby) ═══
function HomeView({ user, onNavigate }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(iv); }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes logoGlow { 0%,100% { filter: drop-shadow(0 0 20px rgba(59,130,246,0.3)); } 50% { filter: drop-shadow(0 0 40px rgba(59,130,246,0.6)) drop-shadow(0 0 80px rgba(59,130,246,0.2)); } }
        @keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes orbitSlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Animated background elements */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {/* Orbiting ring */}
        <div style={{ position: "absolute", width: 500, height: 500, border: "1px solid rgba(59,130,246,0.06)", borderRadius: "50%", top: "50%", left: "50%", marginTop: -250, marginLeft: -250, animation: "orbitSlow 60s linear infinite" }}>
          <div style={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", background: "#3b82f6", top: 0, left: "50%", marginLeft: -3, opacity: 0.4 }} />
        </div>
        <div style={{ position: "absolute", width: 700, height: 700, border: "1px solid rgba(59,130,246,0.03)", borderRadius: "50%", top: "50%", left: "50%", marginTop: -350, marginLeft: -350, animation: "orbitSlow 90s linear infinite reverse" }}>
          <div style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: "#60a5fa", top: "50%", right: 0, marginRight: -2, opacity: 0.3 }} />
        </div>
        {/* Gradient blobs */}
        <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)", top: "20%", right: "10%", animation: "pulse 6s ease-in-out infinite" }} />
        <div style={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)", bottom: "15%", left: "15%", animation: "pulse 8s ease-in-out infinite 2s" }} />
      </div>

      {/* Logo — large, animated, no background box */}
      <div style={{ animation: "floatUp 4s ease-in-out infinite, logoGlow 3s ease-in-out infinite", marginBottom: 20 }}>
        <img src={process.env.PUBLIC_URL + "/efis-logo.png"} alt="EFIS.PRO" style={{ width: "min(500px, 80vw)", height: "auto", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
      </div>

      {/* Tagline */}
      <p style={{ color: "rgba(139,92,246,0.5)", fontSize: 13, letterSpacing: 4, textTransform: "uppercase", marginTop: 0, fontFamily: "'DM Sans', sans-serif" }}>Hotel Management System • Crna Gora</p>

      {/* Clock */}
      <div style={{ marginTop: 40, textAlign: "center" }}>
        <div style={{ color: "white", fontSize: 56, fontWeight: 200, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 4, background: "linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite" }}>
          {time.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
        <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>
          {time.toLocaleDateString("sr-Latn-ME", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Quick action buttons */}
      <div style={{ display: "flex", gap: 12, marginTop: 50, flexWrap: "wrap", justifyContent: "center" }}>
        {[
          { id: "pos", icon: LayoutGrid, label: "Nova Prodaja", color: "#3b82f6" },
          { id: "stolovi", icon: Coffee, label: "Stolovi", color: "#059669" },
          { id: "invoices", icon: FileText, label: "Računi", color: "#d97706" },
          { id: "hotel", icon: Hotel, label: "Hotel", color: "#8b5cf6" },
        ].map(btn => (
          <button key={btn.id} onClick={() => onNavigate(btn.id)}
            style={{ padding: "12px 28px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, color: "rgba(255,255,255,0.7)", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, fontFamily: "'DM Sans', sans-serif", transition: "all 0.3s ease", backdropFilter: "blur(10px)" }}
            onMouseEnter={e => { e.currentTarget.style.background = btn.color + "22"; e.currentTarget.style.borderColor = btn.color; e.currentTarget.style.color = "white"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
          >
            <btn.icon size={18} />{btn.label}
          </button>
        ))}
      </div>

      {/* Company info */}
      <div style={{ position: "absolute", bottom: 20, color: "rgba(255,255,255,0.15)", fontSize: 12, textAlign: "center" }}>
        {user.company_name} {user.location_name ? `• ${user.location_name}` : ""}
      </div>
    </div>
  );
}


// ═══ POS VIEW ═══
function POSView({ user, showToast, tableForOrder, onTableOrderDone, online }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("brzi");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => { loadProducts(); loadCategories(); }, []);

  const loadProducts = async () => {
    setLoading(true);
    const data = await api("/products");
    if (data.success) setProducts(data.data?.products || []);
    setLoading(false);
  };
  const loadCategories = async () => {
    const data = await api("/invoices/categories");
    if (data.success) setCategories(data.data?.categories || []);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === product.id);
      if (ex) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: product.id, name: product.name, price: parseFloat(product.price), quantity: 1, vat_rate: parseFloat(product.vat_rate || 21), unit: product.unit || "kom", discount_type: "none", discount_value: 0, custom_price: null }];
    });
  };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearCart = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setPaymentMethod("cash"); setSplitCash(""); setSplitCard(""); };

  // Brzi meni from localStorage
  const fastMenuIds = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("pos_fast_menu") || "[]"); } catch { return []; }
  }, [products]);

  const cartCalc = useMemo(() => {
    let netTotal = 0, totalItemDiscount = 0;
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value;
      totalItemDiscount += disc;
      netTotal += lineNet - disc;
    });
    let invDisc = 0;
    if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100);
    else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    // Calculate VAT per item rate (not hardcoded 21%)
    let vatAmount = 0;
    const vatGroups = {};
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value || 0;
      const afterDisc = lineNet - disc;
      const rate = parseFloat(item.vat_rate || 21);
      const vat = afterDisc * (rate / 100);
      vatAmount += vat;
      if (!vatGroups[rate]) vatGroups[rate] = { base: 0, vat: 0 };
      vatGroups[rate].base += afterDisc;
      vatGroups[rate].vat += vat;
    });
    // Apply invoice discount proportionally to VAT
    if (invDisc > 0 && netTotal > 0) {
      const ratio = (netTotal - invDisc) / netTotal;
      vatAmount = vatAmount * ratio;
      Object.keys(vatGroups).forEach(r => { vatGroups[r].base *= ratio; vatGroups[r].vat *= ratio; });
    }
    const total = subtotal + vatAmount;
    return { netTotal, totalItemDiscount, invDisc, subtotal, vatAmount, total, vatGroups };
  }, [cart, invoiceDiscount]);

  const buildPayload = (fiscNow) => ({
    location_id: user.location_id || 1,
    tcr_code: getTcr(),
    payment_method: paymentMethod,
    fiscalize_now: fiscNow,
    discount_type: invoiceDiscount.type,
    discount_value: invoiceDiscount.value,
    ...(paymentMethod === "split" && { split_cash: parseFloat(splitCash) || 0, split_card: parseFloat(splitCard) || 0 }),
    items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, unit_price: item.custom_price !== null ? item.custom_price : item.price, vat_rate: item.vat_rate, unit: item.unit, discount_type: item.discount_type, discount_value: item.discount_value }))
  });

  const handleFiscalize = async () => {
    setShowConfirm(false); setFiscalizing(true);
    // Check internal day gate (cash invoices — virman is separate view)
    const tcr = getTcr();
    if (tcr) {
      try {
        const idCheck = await api("/internalday/settings");
        if (idCheck.success && idCheck.data?.internal_day_enabled) {
          const idStatus = await api(`/internalday/status/${tcr}`);
          if (idStatus.success && !idStatus.data?.day) {
            setFiscalizing(false);
            showToast("Interni dan nije otvoren! Otvorite dan u Blagajni prije izdavanja racuna.", "error");
            return;
          }
        }
      } catch(e) {}
    }
    // Quick network check first
    let isReachable = false;
    try {
      const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]);
      isReachable = test && test.ok;
    } catch(e) { isReachable = false; }
    if (!isReachable) {
      setFiscalizing(false);
      const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}). Fiskalizujte kad budete online.`, "error");
      return;
    }
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) });
      setFiscalizing(false);
      if (data.success) {
        setLastFiscalized({ fic: data.data.fic, invoice: data.data.invoice, total: cartCalc.total }); clearCart(); showToast("Fiskalizacija uspješna!");
        // Auto-print if printer configured (non-blocking)
        const invoiceId = data.data.invoice?.id;
        if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
          printReceipt(invoiceId).catch(() => {});
        }
      }
      else {
        const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
        showToast(`Greška! Draft sačuvan lokalno (${cnt}).`, "error");
      }
    } catch(e) {
      setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Offline! Draft sačuvan lokalno (${cnt}).`, "error");
    }
  };
  const handleSaveDraft = async () => {
    if (!cart.length) return;
    // Quick network check
    let isReachable = false;
    try {
      const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]);
      isReachable = test && test.ok;
    } catch(e) { isReachable = false; }
    if (!isReachable) {
      const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error");
      return;
    }
    setSaving(true);
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) });
      setSaving(false);
      if (data.success) { clearCart(); showToast("Draft sačuvan! #" + (data.data.invoice?.invoice_number || "")); }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Greška! Draft sačuvan lokalno (${cnt})`, "error"); }
    } catch(e) { setSaving(false); const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "error"); }
  };

  const filtered = products.filter(p => {
    if (search) return p.name.toLowerCase().includes(search.toLowerCase());
    if (activeCategory === "all") return true;
    if (activeCategory === "brzi") return fastMenuIds.includes(p.id);
    return String(p.category_id) === String(activeCategory);
  });

  if (lastFiscalized) {
    // Auto-dismiss success — just reset to new invoice
    setTimeout(() => setLastFiscalized(null), 100);
    return null;
  }

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* PRODUCTS 57% */}
      <div style={{ flex: "0 0 57%", display: "flex", flexDirection: "column", borderRight: `1px solid ${S.border}` }}>
        <div style={{ padding: "10px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={17} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži proizvode..." style={{ ...S.input, paddingLeft: 38 }} />
          </div>
          <button onClick={loadProducts} style={{ padding: 10, background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", display: "flex" }}><RefreshCw size={17} color={S.muted} /></button>
        </div>
        <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 5, overflowX: "auto" }}>
          <CatTab active={activeCategory === "brzi"} onClick={() => setActiveCategory("brzi")} label="⚡ Brzi Meni" />
          <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
          {categories.map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12, background: S.bg }}>
          {loading ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Loader2 size={30} className="spin" /><p style={{ marginTop: 10, fontSize: 15 }}>Učitavanje...</p></div>
          : filtered.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Package size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>{activeCategory === "brzi" ? "Brzi meni je prazan. Konfigurišite u Podešavanjima." : "Nema proizvoda"}</p></div>
          : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 10 }}>
            {filtered.map(p => {
              const inCart = cart.find(c => c.id === p.id);
              const gross = parseFloat(p.price) * (1 + parseFloat(p.vat_rate || 21) / 100);
              return (
                <button key={p.id} onClick={() => addToCart(p)} style={{ position: "relative", padding: "18px 10px", background: inCart ? "#f0fdf4" : "white", border: inCart ? `2px solid ${S.green}` : `1px solid ${S.border}`, borderRadius: 10, cursor: "pointer", textAlign: "center", fontFamily: S.font }}>
                  {inCart && <div style={{ position: "absolute", top: -7, right: -7, width: 24, height: 24, background: S.green, borderRadius: "50%", color: "white", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{inCart.quantity}</div>}
                  <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, marginBottom: 6, lineHeight: 1.3 }}>{p.name}</div>
                  <div style={{ fontWeight: 700, fontSize: 17, color: S.green }}>{fmt(gross)} €</div>
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>neto {fmt(p.price)}</div>
                </button>
              );
            })}
          </div>}
        </div>
      </div>

      {/* CART 43% */}
      <div style={{ flex: "0 0 43%", display: "flex", flexDirection: "column", background: "white" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingCart size={18} color={S.dark} />
            <span style={{ fontWeight: 700, fontSize: 17, color: S.dark }}>Račun</span>
            <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 5, fontSize: 13, fontWeight: 600, color: S.muted }}>{cart.length}</span>
          </div>
          {cart.length > 0 && <button onClick={clearCart} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={13} />Obriši</button>}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
          {cart.length === 0 ? <div style={{ textAlign: "center", padding: "50px 16px", color: "#9ca3af" }}><ShoppingCart size={40} strokeWidth={1.5} /><p style={{ marginTop: 12, fontSize: 15 }}>Dodajte proizvode</p></div>
          : cart.map(item => {
            const up = item.custom_price !== null ? item.custom_price : item.price;
            const lineNet = item.quantity * up;
            let disc = 0;
            if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
            else if (item.discount_type === "fixed") disc = item.discount_value;
            const lineGross = (lineNet - disc) * (1 + item.vat_rate / 100);
            return (
              <div key={item.id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>{fmt(up)} × {item.quantity}{disc > 0 ? ` − ${fmt(disc)}` : ""}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => updateCartItem(item.id, { quantity: Math.max(1, item.quantity - 1) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={16} /></button>
                    <span style={{ fontSize: 15, fontWeight: 600, minWidth: 26, textAlign: "center" }}>{item.quantity}</span>
                    <button onClick={() => updateCartItem(item.id, { quantity: item.quantity + 1 })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: S.dark, minWidth: 60, textAlign: "right" }}>{fmt(lineGross)} €</div>
                  <button onClick={() => setEditingItem(item.id === editingItem ? null : item.id)} style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: editingItem === item.id ? S.accent : S.muted }}><Edit3 size={15} /></button>
                  <button onClick={() => removeFromCart(item.id)} style={{ padding: 6, background: "#fef2f2", border: "none", borderRadius: 6, cursor: "pointer", color: "#dc2626" }}><X size={16} /></button>
                </div>
                {editingItem === item.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 90px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Cijena (neto)</label>
                      <input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 70px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Količina</label>
                      <input type="number" value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 110px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Popust</label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, flex: 1 }}><option value="none">Bez</option><option value="percentage">%</option><option value="fixed">€</option></select>
                        {item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, width: 60 }} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ borderTop: `2px solid ${S.border}`, padding: "14px 16px" }}>
            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
              <button onClick={() => setShowDiscountModal(true)} style={{ ...S.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : S.muted), flex: 1, justifyContent: "center" }}>
                <Percent size={13} />{invoiceDiscount.type !== "none" ? `Popust: ${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Popust na račun"}
              </button>
              {invoiceDiscount.type !== "none" && <button onClick={() => setInvoiceDiscount({ type: "none", value: 0 })} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.muted }}>Neto</span><span>{fmt(cartCalc.subtotal)} €</span></div>
            {cartCalc.vatGroups && Object.entries(cartCalc.vatGroups).map(([rate, g]) => (
              <div key={rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 13 }}><span style={{ color: S.muted }}>PDV {rate}%</span><span>{fmt(g.vat)} €</span></div>
            ))}
            {cartCalc.invDisc > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.orange }}>Popust</span><span style={{ color: S.orange }}>-{fmt(cartCalc.invDisc)} €</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: S.green }}>{fmt(cartCalc.total)} €</span>
            </div>
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "cash", label: "Gotovina", icon: Banknote }, { id: "card", label: "Kartica", icon: CreditCard }, { id: "split", label: "Podijeli", icon: Wallet }].map(m => (
                <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ flex: 1, padding: "12px 8px", border: "2px solid", borderColor: paymentMethod === m.id ? S.green : S.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: S.font }}><m.icon size={14} />{m.label}</button>
              ))}
            </div>
            {paymentMethod === "split" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Gotovina €</label><input type="number" step="0.01" value={splitCash} onChange={e => { setSplitCash(e.target.value); setSplitCard(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...S.input, padding: "7px 10px" }} /></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Kartica €</label><input type="number" step="0.01" value={splitCard} onChange={e => { setSplitCard(e.target.value); setSplitCash(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...S.input, padding: "7px 10px" }} /></div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowConfirm(true)} disabled={fiscalizing} style={{ ...S.btn(fiscalizing ? "#6b7280" : S.green), flex: "0 0 63%", justifyContent: "center", padding: "13px", fontSize: 17 }}>
                {fiscalizing ? <Loader2 size={19} className="spin" /> : <Zap size={19} />}{fiscalizing ? "..." : "FISKALIZUJ"}
              </button>
              <button onClick={handleSaveDraft} disabled={saving} style={{ ...S.btn("#3b82f6"), flex: "0 0 35%", justifyContent: "center", padding: "13px", fontSize: 15 }}>
                {saving ? <Loader2 size={15} className="spin" /> : <Archive size={15} />}{saving ? "..." : "SAČUVAJ"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda fiskalizacije">
        <p style={{ color: S.muted, fontSize: 15, marginBottom: 16 }}>Da li ste sigurni da želite fiskalizovati ovaj račun?</p>
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700 }}><span>Ukupno:</span><span style={{ color: S.green }}>{fmt(cartCalc.total)} €</span></div>
          <div style={{ fontSize: 14, color: S.muted, marginTop: 6 }}>Stavke: {cart.length} | Plaćanje: {paymentMethod === "cash" ? "Gotovina" : paymentMethod === "card" ? "Kartica" : "Podijeljeno"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setShowConfirm(false)} style={S.btn("#f3f4f6", S.muted)}>Otkaži</button>
          <button onClick={handleFiscalize} style={{ ...S.btn(S.green), padding: "14px 24px", fontSize: 16 }}><Zap size={18} />Fiskalizuj</button>
        </div>
      </Modal>}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust na račun">
        <DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} />
      </Modal>}
    </div>
  );
}

// ═══ INVOICES VIEW ═══
function InvoicesView({ user, showToast }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pg, setPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [invTab, setInvTab] = useState("racuni");
  // Korekcije state
  const [corrSearch, setCorrSearch] = useState("");
  const [corrInvoice, setCorrInvoice] = useState(null);
  const [corrItems, setCorrItems] = useState([]);
  const [corrPayment, setCorrPayment] = useState("cash");
  const [corrBuyerName, setCorrBuyerName] = useState("");
  const [corrBuyerTin, setCorrBuyerTin] = useState("");
  const [corrReason, setCorrReason] = useState("CORRECTIVE");
  const [corrSubmitting, setCorrSubmitting] = useState(false);
  // Avansni state
  const [advMode, setAdvMode] = useState("create"); // create or finalize
  const [advItems, setAdvItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advPayment, setAdvPayment] = useState("cash");
  const [advBuyerName, setAdvBuyerName] = useState("");
  const [advBuyerTin, setAdvBuyerTin] = useState("");
  const [advSubmitting, setAdvSubmitting] = useState(false);
  const [advSearch, setAdvSearch] = useState("");
  const [advInvoice, setAdvInvoice] = useState(null);
  const [advFinalItems, setAdvFinalItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advFinalPayment, setAdvFinalPayment] = useState("cash");

  useEffect(() => { loadInvoices(); }, [pg.page, statusFilter, dateFrom, dateTo]);

  const loadInvoices = async () => {
    setLoading(true);
    const tcr = getTcr();
    const params = new URLSearchParams({ page: pg.page, per_page: 20 });
    if (statusFilter) params.set("status", statusFilter);
    if (tcr) params.set("tcr_code", tcr);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const data = await api(`/invoices?${params}`);
    if (data.success) { setInvoices(data.data.invoices || []); setPg(p => ({ ...p, ...data.data.pagination })); }
    setLoading(false);
  };
  const viewInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setDetail(d.data); };
  const fiscalizeDraft = async (id) => {
    const d = await api(`/invoices/fiscalize/${id}`, { method: "POST" });
    if (d.success) { showToast("Fiskalizovano!"); loadInvoices(); if (detail) viewInvoice(id); } else showToast(d.message, "error");
  };
  const handleStorno = async (id, reason) => {
    const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true }) });
    if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setDetail(null); loadInvoices(); } else showToast(d.message, "error");
  };
  const handleEmail = async (id, email) => {
    const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) });
    if (d.success) showToast("Email poslat na " + email);
    else showToast(d.message || "Greška pri slanju", "error");
    setEmailModal(null);
  };

  const loadCorrInvoice = async () => {
    if (!corrSearch.trim()) return;
    const tcr = getTcr();
    // Try search by invoice number first
    let d = await api(`/invoices?search=${encodeURIComponent(corrSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success && d.data?.invoices?.length > 0 ? d.data.invoices.find(inv => inv.status === "fiscalized" && !inv.stornoed_by_invoice_id && !inv.corrected_by_invoice_id && inv.is_storno != 1) : null;
    if (!found && d.success && d.data?.invoices?.length > 0) found = d.data.invoices[0];
    if (!found) {
      // Try by ID
      const idNum = parseInt(corrSearch.trim());
      if (idNum > 0) {
        const d2 = await api(`/invoices/show/${idNum}`);
        if (d2.success && d2.data?.invoice) found = d2.data.invoice;
      }
    }
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) {
        const full = det.data;
        setCorrInvoice(full);
        setCorrPayment(full.invoice?.payment_method || "cash");
        setCorrBuyerName(full.invoice?.buyer_name || "");
        setCorrBuyerTin(full.invoice?.buyer_pib || "");
        // Load items
        const items = (full.items || []).map(it => ({
          product_id: it.product_id || null,
          name: it.item_name || it.name || "Item",
          quantity: Math.abs(parseFloat(it.quantity || 1)),
          unit_price: parseFloat(it.unit_price || 0),
          vat_rate: parseFloat(it.vat_rate || 21),
          unit: it.unit || "kom",
          discount_type: it.discount_type || "none",
          discount_value: parseFloat(it.discount_value || 0)
        }));
        setCorrItems(items);
      }
    } else showToast("Račun nije pronađen", "error");
  };

  const submitCorrection = async () => {
    if (!corrInvoice || corrItems.length === 0) return;
    setCorrSubmitting(true);
    const d = await api(`/invoices/correct/${corrInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({
      items: corrItems, payment_method: corrPayment,
      buyer_name: corrBuyerName, buyer_tin: corrBuyerTin,
      correction_reason: corrReason
    }) });
    setCorrSubmitting(false);
    if (d.success) {
      showToast(`Korekcija uspješna! Novi račun: ${d.data?.corrected_invoice?.invoice_number || ""}`);
      setCorrInvoice(null); setCorrItems([]); setCorrSearch("");
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  const submitAdvance = async () => {
    if (advItems.length === 0 || advItems.every(i => !i.name)) return;
    setAdvSubmitting(true);
    const tcr = getTcr();
    const d = await api("/invoices/advance", { method: "POST", body: JSON.stringify({
      items: advItems, payment_method: advPayment, tcr_code: tcr,
      buyer_name: advBuyerName, buyer_tin: advBuyerTin
    }) });
    setAdvSubmitting(false);
    if (d.success) {
      showToast(`Avansni račun kreiran! ${d.data?.invoice?.invoice_number || ""}`);
      setAdvItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
      setAdvBuyerName(""); setAdvBuyerTin("");
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  const loadAdvInvoice = async () => {
    if (!advSearch.trim()) return;
    const tcr = getTcr();
    let d = await api(`/invoices?search=${encodeURIComponent(advSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success ? (d.data?.invoices || []).find(inv => inv.is_advance == 1 && !inv.finalized_by_invoice_id) : null;
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) {
        setAdvInvoice(det.data);
        const items = (det.data.items || []).map(it => ({
          name: it.item_name || it.name || "Item",
          quantity: Math.abs(parseFloat(it.quantity || 1)),
          unit_price: parseFloat(it.unit_price || 0),
          vat_rate: parseFloat(it.vat_rate || 21), unit: it.unit || "kom"
        }));
        setAdvFinalItems(items);
      }
    } else showToast("Avansni račun nije pronađen", "error");
  };

  const submitFinalizeAdvance = async () => {
    if (!advInvoice || advFinalItems.length === 0) return;
    setAdvSubmitting(true);
    const d = await api(`/invoices/finalizeAdvance/${advInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({
      items: advFinalItems, payment_method: advFinalPayment,
      buyer_name: advBuyerName, buyer_tin: advBuyerTin
    }) });
    setAdvSubmitting(false);
    if (d.success) {
      showToast(`Finalni račun kreiran! Avans: ${fmt(d.data?.advance_used || 0)}€, Ostatak: ${fmt(d.data?.remaining_paid || 0)}€`);
      setAdvInvoice(null); setAdvSearch("");
      setAdvFinalItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  // Detail view
  if (detail) {
    const inv = detail.invoice; const items = detail.items || [];
    const isStorno = inv.is_storno == 1;
    return (
      <div style={{ height: "100%", overflow: "auto", background: S.bg }}>
        <div style={{ padding: "14px 20px", background: isStorno ? "#fef2f2" : "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setDetail(null)} style={{ padding: 8, background: "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", display: "flex" }}><ArrowLeft size={17} /></button>
          <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isStorno && "⊖ "}{inv.invoice_number}</h2><span style={{ fontSize: 13, color: "#9ca3af" }}>{fmtDateTime(inv.invoice_date)}</span></div>
          <Badge status={inv.status} />
          <button onClick={async () => { await printReceipt(inv.id) }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={13} />Štampaj</button>
          <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} />PDF</button>
          <button onClick={() => setEmailModal(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Mail size={13} />Email</button>
          {inv.status === "draft" && <button onClick={() => fiscalizeDraft(inv.id)} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} />Fiskalizuj</button>}
          {detail.can_storno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} />Storniraj</button>}
        </div>
        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>
          {isStorno && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><RotateCcw size={16} color={S.red} /><span style={{ fontSize: 14, color: S.red, fontWeight: 600 }}>Ovo je storno/korektivni račun (negativne stavke).</span></div>}
          {detail.storno_invoice && detail.storno_invoice.status === "draft" && !isStorno && <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><AlertCircle size={16} color="#92400e" /><span style={{ fontSize: 14, color: "#92400e", fontWeight: 600 }}>Storno kreiran ali fiskalizacija nije uspjela!</span></div><div style={{ display: "flex", gap: 8 }}><button onClick={() => fiscalizeDraft(detail.storno_invoice.id)} style={{ ...S.btnSm(S.green, "white") }}><Zap size={14} />Ponovo fiskalizuj storno</button><button onClick={() => viewInvoice(detail.storno_invoice.id)} style={S.btnSm("#f3f4f6", S.muted)}>Pogledaj storno →</button></div></div>}
          {detail.storno_invoice && detail.storno_invoice.status !== "draft" && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><RotateCcw size={16} color={S.red} /><span style={{ fontSize: 14, color: S.red }}>Ovaj račun je storniran</span></div><button onClick={() => viewInvoice(detail.storno_invoice.id)} style={S.btnSm("#fef2f2", S.red)}>Pogledaj storno →</button></div>}
          {inv.fic && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}><Check size={17} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 15 }}>Fiskalizovano</span></div>
              <Row label="FIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, wordBreak: "break-all" }}>{inv.fic}</span>} />
              <Row label="IIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, wordBreak: "break-all" }}>{inv.iic || "-"}</span>} />
              {inv.iic && (() => {
                const crtd = inv.invoice_date ? inv.invoice_date.replace(' ', 'T') + '+01:00' : '';
                const ord = inv.invoice_number ? (inv.invoice_number.split('/')[1] || '') : '';
                const vUrl = `https://efitest.tax.gov.me/ic/#/verify?iic=${inv.iic}&tin=${inv.company_pib || ''}&crtd=${encodeURIComponent(crtd)}&ord=${ord}&bu=${inv.business_unit_code || ''}&cr=${inv.tcr_code || ''}&sw=yx011yw877&prc=${parseFloat(inv.total_amount || 0).toFixed(2)}`;
                return <div style={{ marginTop: 12, textAlign: "center" }}><img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(vUrl)}`} alt="QR" style={{ width: 160 }} /><div style={{ fontSize: 12, color: S.muted, marginTop: 4 }}>Verifikacioni QR kod</div></div>;
              })()}
            </div>
          )}
          {(inv.buyer_name || inv.partner_name) && (
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Building size={16} />Kupac / Partner</div>
              <Row label="Naziv" value={inv.partner_name || inv.buyer_name} />
              {(inv.partner_tax_id || inv.buyer_pib) && <Row label="PIB" value={inv.partner_tax_id || inv.buyer_pib} />}
              {inv.buyer_address && <Row label="Adresa" value={inv.buyer_address} />}
              {inv.buyer_city && <Row label="Grad" value={inv.buyer_city} />}
              {inv.payment_due && <Row label="Rok plaćanja" value={inv.payment_due === "end_of_month" ? "Kraj mjeseca" : inv.payment_due === "avans" ? "Avans" : inv.payment_due} />}
            </div>
          )}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, fontWeight: 700, fontSize: 15 }}>Stavke</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "center" }}>Kol.</th><th style={{ ...th, textAlign: "right" }}>Cijena</th><th style={{ ...th, textAlign: "right" }}>Ukupno</th></tr></thead>
              <tbody>{items.map((it, i) => <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={td}>{it.item_name || it.name}</td><td style={{ ...td, textAlign: "center" }}>{it.quantity}</td><td style={{ ...td, textAlign: "right" }}>{fmt(it.unit_price)} €</td><td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(it.total_amount || it.quantity * it.unit_price)} €</td></tr>)}</tbody>
            </table>
          </div>
          {inv.is_summary_invoice == 1 && inv.summary_order_ids && (() => {
            const refIds = typeof inv.summary_order_ids === "string" ? JSON.parse(inv.summary_order_ids) : inv.summary_order_ids;
            return refIds && refIds.length > 0 ? (
              <div style={{ background: "#f0f9ff", borderRadius: 10, border: "1px solid #bae6fd", padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><FileText size={16} color="#0284c7" />Referentne narudžbe ({refIds.length})</div>
                <div style={{ fontSize: 13, color: S.muted, marginBottom: 8 }}>Ovaj zbirni račun obuhvata sljedeće narudžbe:</div>
                {refIds.map((rid, i) => (
                  <button key={rid} onClick={() => viewInvoice(rid)} style={{ display: "block", width: "100%", padding: "8px 12px", marginBottom: 4, background: "white", border: `1px solid ${S.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", fontSize: 13, fontFamily: S.font }}>
                    Narudžba #{i + 1} — ID: {rid} <span style={{ color: S.accent, marginLeft: 4 }}>→ Pogledaj</span>
                  </button>
                ))}
              </div>
            ) : null;
          })()}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} />
            {(() => { const vg = {}; (detail?.items || []).forEach(it => { const r = parseFloat(it.vat_rate || 21); const gross = parseFloat(it.total_amount || it.quantity * it.unit_price); const net = gross / (1 + r/100); if (!vg[r]) vg[r] = 0; vg[r] += gross - net; }); return Object.entries(vg).map(([r, v]) => <Row key={r} label={`PDV ${r}%`} value={`${fmt(v)} €`} />); })()}
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={`-${fmt(inv.discount_amount)} €`} />}
            <Row label="Plaćanje" value={inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method} />
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: isStorno ? S.red : S.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  // List view
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 20px", background: "white", borderBottom: `1px solid ${S.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div><h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Računi</h2></div>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "racuni", l: "Računi" }, { id: "korekcije", l: "Korekcije" }, { id: "avansni", l: "Avansni" }].map(t => (
              <button key={t.id} onClick={() => setInvTab(t.id)} style={{ padding: "7px 16px", border: `1px solid ${invTab === t.id ? S.dark : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: invTab === t.id ? S.dark : "white", color: invTab === t.id ? "white" : S.muted }}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Korekcije tab content */}
        {invTab === "korekcije" && (
          <div style={{ padding: "10px 0" }}>
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
              Korekcija računa: sistem automatski kreira negativni (storno) račun i novi ispravljeni račun. Oba se šalju na Poresku upravu.
            </div>

            {!corrInvoice ? (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <input value={corrSearch} onChange={e => setCorrSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadCorrInvoice()} placeholder="Unesite broj računa..." style={{ ...S.input, flex: 1 }} />
                  <button onClick={loadCorrInvoice} style={S.btn(S.accent)}>Traži</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>Korekcija: {corrInvoice.invoice?.invoice_number}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>{fmtDateTime(corrInvoice.invoice?.invoice_date)} · {fmt(corrInvoice.invoice?.total_amount)} €</div>
                  </div>
                  <button onClick={() => { setCorrInvoice(null); setCorrItems([]); }} style={S.btnSm("#f3f4f6", S.muted)}><X size={16} /></button>
                </div>

                {/* Reason */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Razlog korekcije</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "CORRECTIVE", l: "Korekcija (kupac)" }, { v: "ERROR_CORRECTIVE", l: "Greška operatera" }].map(r => (
                      <button key={r.v} onClick={() => setCorrReason(r.v)} style={{ padding: "6px 14px", border: `1px solid ${corrReason === r.v ? S.accent : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: corrReason === r.v ? S.accent : "white", color: corrReason === r.v ? "white" : S.muted }}>{r.l}</button>
                    ))}
                  </div>
                </div>

                {/* Buyer info */}
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Kupac (ime)</label><input value={corrBuyerName} onChange={e => setCorrBuyerName(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB kupca</label><input value={corrBuyerTin} onChange={e => setCorrBuyerTin(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                </div>

                {/* Payment */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Način plaćanja</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }, { v: "virman", l: "Virman" }].map(p => (
                      <button key={p.v} onClick={() => setCorrPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${corrPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: corrPayment === p.v ? S.green : "white", color: corrPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                    ))}
                  </div>
                </div>

                {/* Editable items */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 700 }}>Stavke (uredite po potrebi)</label>
                  <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                      <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                    </div>
                    {corrItems.map((item, idx) => (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                        <input value={item.name} onChange={e => { const c = [...corrItems]; c[idx].name = e.target.value; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" value={item.quantity} onChange={e => { const c = [...corrItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...corrItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" value={item.vat_rate} onChange={e => { const c = [...corrItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <button onClick={() => setCorrItems(corrItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                      <button onClick={() => setCorrItems([...corrItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21, unit: "kom", discount_type: "none", discount_value: 0 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                    </div>
                  </div>
                </div>

                {/* Total preview */}
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  {(() => {
                    let totalGross = 0, totalVat = 0;
                    corrItems.forEach(it => {
                      const line = it.quantity * it.unit_price;
                      const vat = line * (it.vat_rate / 100);
                      totalGross += line + vat;
                      totalVat += vat;
                    });
                    return <>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 4 }}>
                        <span>Bez PDV:</span><span>{fmt(totalGross - totalVat)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 6 }}>
                        <span>PDV:</span><span>{fmt(totalVat)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, borderTop: "1px solid #e5e7eb", paddingTop: 6 }}>
                        <span>Novi ukupan iznos:</span>
                        <span style={{ color: S.green }}>{fmt(totalGross)} €</span>
                      </div>
                    </>;
                  })()}
                </div>

                {/* Submit */}
                <button onClick={submitCorrection} disabled={corrSubmitting || corrItems.length === 0} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                  {corrSubmitting ? <Loader2 size={17} className="spin" /> : <CheckCircle size={17} />}
                  <span style={{ marginLeft: 8 }}>{corrSubmitting ? "Obrađujem..." : "Potvrdi korekciju"}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Avansni tab content */}
        {invTab === "avansni" && (
          <div style={{ padding: "10px 0" }}>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, color: "#1e40af" }}>
              Avansni račun: kreira se kad kupac unaprijed plati. Kada se isporuči roba/usluga, kreira se finalni račun koji referencira avansno plaćanje.
            </div>

            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[{ id: "create", l: "Kreiraj avans" }, { id: "finalize", l: "Finaliziraj avans" }].map(t => (
                <button key={t.id} onClick={() => { setAdvMode(t.id); setAdvInvoice(null); }} style={{ padding: "7px 16px", border: `1px solid ${advMode === t.id ? S.accent : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advMode === t.id ? S.accent : "white", color: advMode === t.id ? "white" : S.muted }}>{t.l}</button>
              ))}
            </div>

            {advMode === "create" && (
              <div>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Kupac</label><input value={advBuyerName} onChange={e => setAdvBuyerName(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB</label><input value={advBuyerTin} onChange={e => setAdvBuyerTin(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Plaćanje</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }, { v: "virman", l: "Virman" }].map(p => (
                      <button key={p.v} onClick={() => setAdvPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${advPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advPayment === p.v ? S.green : "white", color: advPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                    ))}
                  </div>
                </div>
                <label style={{ fontSize: 13, fontWeight: 700 }}>Stavke avansa</label>
                <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                    <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                  </div>
                  {advItems.map((item, idx) => (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                      <input value={item.name} onChange={e => { const c = [...advItems]; c[idx].name = e.target.value; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} placeholder="Opis avansa" />
                      <input type="number" value={item.quantity} onChange={e => { const c = [...advItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <input type="number" value={item.vat_rate} onChange={e => { const c = [...advItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <button onClick={() => setAdvItems(advItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                    <button onClick={() => setAdvItems([...advItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                  </div>
                </div>
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 14 }}>
                  {(() => { let tot = 0, vat = 0; advItems.forEach(i => { const n = i.quantity * i.unit_price; vat += n * (i.vat_rate / 100); tot += n + n * (i.vat_rate / 100); }); return <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16 }}><span>Avansni iznos:</span><span style={{ color: S.green }}>{fmt(tot)} €</span></div>; })()}
                </div>
                <button onClick={submitAdvance} disabled={advSubmitting} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                  {advSubmitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}
                  <span style={{ marginLeft: 8 }}>{advSubmitting ? "Obrađujem..." : "Kreiraj avansni račun"}</span>
                </button>
              </div>
            )}

            {advMode === "finalize" && (
              <div>
                {!advInvoice ? (
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input value={advSearch} onChange={e => setAdvSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadAdvInvoice()} placeholder="Unesite broj avansnog računa..." style={{ ...S.input, flex: 1 }} />
                    <button onClick={loadAdvInvoice} style={S.btn(S.accent)}>Traži</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Avans: {advInvoice.invoice?.invoice_number}</div>
                        <div style={{ fontSize: 12, color: S.muted }}>Iznos avansa: <strong style={{ color: S.green }}>{fmt(advInvoice.invoice?.total_amount)} €</strong></div>
                      </div>
                      <button onClick={() => { setAdvInvoice(null); setAdvSearch(""); }} style={S.btnSm("#f3f4f6", S.muted)}><X size={16} /></button>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600 }}>Plaćanje ostatka</label>
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }].map(p => (
                          <button key={p.v} onClick={() => setAdvFinalPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${advFinalPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advFinalPayment === p.v ? S.green : "white", color: advFinalPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                        ))}
                      </div>
                    </div>
                    <label style={{ fontSize: 13, fontWeight: 700 }}>Finalne stavke</label>
                    <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                        <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                      </div>
                      {advFinalItems.map((item, idx) => (
                        <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                          <input value={item.name} onChange={e => { const c = [...advFinalItems]; c[idx].name = e.target.value; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" value={item.quantity} onChange={e => { const c = [...advFinalItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advFinalItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" value={item.vat_rate} onChange={e => { const c = [...advFinalItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <button onClick={() => setAdvFinalItems(advFinalItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                        <button onClick={() => setAdvFinalItems([...advFinalItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                      </div>
                    </div>
                    <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 14 }}>
                      {(() => {
                        let tot = 0, vat = 0;
                        advFinalItems.forEach(i => { const n = i.quantity * i.unit_price; vat += n * (i.vat_rate / 100); tot += n + n * (i.vat_rate / 100); });
                        const advAmt = parseFloat(advInvoice.invoice?.total_amount || 0);
                        const remaining = Math.max(0, tot - advAmt);
                        return <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 4 }}><span>Ukupno:</span><span>{fmt(tot)} €</span></div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#1e40af", marginBottom: 4 }}><span>Avans:</span><span>-{fmt(advAmt)} €</span></div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, borderTop: "1px solid #e5e7eb", paddingTop: 6 }}><span>Za naplatu:</span><span style={{ color: S.green }}>{fmt(remaining)} €</span></div>
                        </>;
                      })()}
                    </div>
                    <button onClick={submitFinalizeAdvance} disabled={advSubmitting} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                      {advSubmitting ? <Loader2 size={17} className="spin" /> : <CheckCircle size={17} />}
                      <span style={{ marginLeft: 8 }}>{advSubmitting ? "Obrađujem..." : "Kreiraj finalni račun"}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Racuni filters — only show when on racuni tab */}
        {invTab === "racuni" && <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af" }}>{pg.total} ukupno{getTcr() ? ` · TCR: ${getTcr()}` : ""}</p>
          <div style={{ display: "flex", gap: 5 }}>
            {[{ v: "", l: "Sve" }, { v: "fiscalized", l: "Fiskalizovane" }, { v: "draft", l: "Draft" }, { v: "storno", l: "Storno" }, { v: "corrective", l: "Korigovani" }, { v: "advance", l: "Avansni" }].map(s => (
              <button key={s.v} onClick={() => { setStatusFilter(s.v); setPg(p => ({ ...p, page: 1 })); }} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: S.font, borderColor: statusFilter === s.v ? S.dark : S.border, background: statusFilter === s.v ? S.dark : "white", color: statusFilter === s.v ? "white" : S.muted }}>{s.l}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: S.muted, fontWeight: 600 }}>Period:</span>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...S.input, padding: "5px 10px", fontSize: 13, width: 140 }} />
          <span style={{ fontSize: 13, color: S.muted }}>—</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...S.input, padding: "5px 10px", fontSize: 13, width: 140 }} />
          {[{ l: "Danas", d: 0 }, { l: "7 dana", d: 7 }, { l: "30 dana", d: 30 }].map(p => (
            <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setDateFrom(f.toISOString().slice(0,10)); setDateTo(t.toISOString().slice(0,10)); setPg(pr => ({ ...pr, page: 1 })); }} style={{ padding: "5px 10px", border: `1px solid ${S.border}`, borderRadius: 5, fontSize: 12, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
          ))}
        </div>
        </>}
      </div>
      {invTab === "racuni" && <><div style={{ flex: 1, overflow: "auto", padding: "0 20px" }}>
        {loading ? <div style={{ textAlign: "center", padding: 50 }}><Loader2 size={28} className="spin" /></div>
        : invoices.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><FileText size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>Nema faktura</p></div>
        : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginTop: 8 }}>
          <thead><tr><th style={th}>Broj</th><th style={th}>Datum</th><th style={{ ...th, textAlign: "center" }}>Plaćanje</th><th style={{ ...th, textAlign: "right" }}>Iznos</th><th style={{ ...th, textAlign: "center" }}>Status</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
          <tbody>{invoices.map(inv => {
            const isStorno = inv.is_storno == 1 || inv.status === "storno";
            const wasStornoed = inv.stornoed_by_invoice_id || inv.stornoed_at;
            return (
              <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6", background: isStorno ? "#fef2f2" : wasStornoed ? "#fff7ed" : "white" }}>
                <td style={td}>
                  <span style={{ fontFamily: S.mono, fontSize: 12, fontWeight: 600, color: isStorno ? S.red : S.dark }}>
                    {isStorno && "⊖ "}{inv.invoice_number}
                    {inv.is_summary_invoice == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#0284c7", fontWeight: 700 }}>ZBIRNI</span>}
                    {inv.is_corrective == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#9333ea", fontWeight: 700 }}>KOREKCIJA</span>}
                    {inv.corrected_by_invoice_id && <span style={{ marginLeft: 4, fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>KORIGOVANO</span>}
                    {inv.is_advance == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#1e40af", fontWeight: 700 }}>AVANS</span>}
                    {inv.finalized_by_invoice_id && <span style={{ marginLeft: 4, fontSize: 10, color: "#15803d", fontWeight: 700 }}>ISKORIŠTEN</span>}
                  </span>
                </td>
                <td style={{ ...td, color: S.muted }}>{fmtDate(inv.invoice_date)}</td>
                <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, color: isStorno ? S.red : S.dark }}>{fmt(inv.total_amount)} €</td>
                <td style={{ ...td, textAlign: "center" }}><Badge status={isStorno ? "storno" : wasStornoed ? "cancelled" : inv.status} /></td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button onClick={() => viewInvoice(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Eye size={13} /></button>
                    {inv.status === "draft" && <button onClick={() => fiscalizeDraft(inv.id)} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} /></button>}
                    {inv.status === "fiscalized" && !wasStornoed && !isStorno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} /></button>}
                  </div>
                </td>
              </tr>
            );
          })}</tbody>
        </table>}
      </div>
      {pg.total_pages > 1 && <Pagination pg={pg} onChange={p => setPg(prev => ({ ...prev, page: p }))} />}
      </>}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
    </div>
  );
}

// ═══ BLAGAJNA ═══
function BlagajnaView({ user, showToast }) {
  const [depositAmt, setDepositAmt] = useState("");
  const [actualCash, setActualCash] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(null);
  const [depositExists, setDepositExists] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [closingInfo, setClosingInfo] = useState(null);
  const [unclosedSession, setUnclosedSession] = useState(null);
  const [histTab, setHistTab] = useState("openings");
  const [continueMode, setContinueMode] = useState(false);
  const [todaySales, setTodaySales] = useState(null);
  const [showCloseAny, setShowCloseAny] = useState(false);
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalDay, setInternalDay] = useState(null);
  const [internalSales, setInternalSales] = useState(null);
  const [blagajnaTab, setBlagajnaTab] = useState("depozit");
  const [internalHistory, setInternalHistory] = useState([]);
  const [showCloseByInvoice, setShowCloseByInvoice] = useState(false);
  const [histDateFrom, setHistDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 14); return d.toISOString().slice(0, 10); });
  const [histDateTo, setHistDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [todayWithdrawn, setTodayWithdrawn] = useState(0);
  const [dayInvoices, setDayInvoices] = useState([]);
  const [closeByInvSearch, setCloseByInvSearch] = useState("");
  const [closeAnyDate, setCloseAnyDate] = useState("");
  const [closeAnyTcr, setCloseAnyTcr] = useState("");
  const [closeAnyCash, setCloseAnyCash] = useState("");

  useEffect(() => { load(); }, []);
  const tcr = getTcr();

  const load = async () => {
    if (!tcr) { setLoading(false); return; }
    const [dep, sess, hist, unclosed, idSettings, idStatus, idHist] = await Promise.all([
      api(`/deposits/check/${tcr}`),
      api(`/cashregister/active/${tcr}`),
      api(`/cashregister?tcr_code=${tcr}`),
      api(`/cashregister/unclosed/${tcr}`),
      api("/internalday/settings"),
      api(`/internalday/status/${tcr}`),
      api(`/internalday/history/${tcr}`)
    ]);
    if (dep.success) setDepositExists(dep.data.has_deposit);
    if (sess.success) setActive(sess.data.has_active_session ? sess.data : null);
    if (hist.success) setSessions(hist.data.sessions || []);
    if (unclosed.success && unclosed.data.has_unclosed) setUnclosedSession(unclosed.data);
    else setUnclosedSession(null);
    if (idSettings.success) setInternalEnabled(!!idSettings.data?.internal_day_enabled);
    if (idStatus.success) { setInternalDay(idStatus.data?.day || null); setInternalSales(idStatus.data?.sales || null); }
    if (idHist.success) setInternalHistory(idHist.data?.days || []);
    // Load withdrawals
    const wd = await api(`/deposits/withdrawals/${tcr}`);
    if (wd.success) { setWithdrawals(wd.data?.withdrawals || []); setTodayWithdrawn(wd.data?.today_total || 0); }
    setLoading(false);
  };

  const doWithdraw = async () => {
    if (!withdrawAmt || parseFloat(withdrawAmt) <= 0) { showToast("Unesite iznos", "error"); return; }
    setSubmitting(true);
    const d = await api("/deposits/withdraw", { method: "POST", body: JSON.stringify({ tcr_code: tcr, amount: parseFloat(withdrawAmt), reason: withdrawReason }) });
    setSubmitting(false);
    if (d.success) {
      showToast(`Podizanje ${withdrawAmt} € registrovano! FIC: ${d.data?.fic || ""}`);
      setWithdrawAmt(""); setWithdrawReason("");
      // Auto-print withdrawal receipt (non-blocking)
      const wid = d.data?.withdraw_id;
      if (wid && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        setTimeout(async () => {
          try {
            if (isElectron && window.electronAPI) {
              const token = localStorage.getItem("pos_token");
              const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${wid}`, { headers: { "Authorization": `Bearer ${token}` } });
              if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); }
            }
          } catch(e) {}
        }, 500);
      }
      load();
    } else showToast(d.message || "Greška", "error");
  };

  const doOpenInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/open", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) { showToast("Interni dan otvoren!"); load(); } else showToast(d.message, "error");
  };

  const doCloseInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) {
      showToast("Interni dan zatvoren!");
      // Auto-print internal day closing (non-blocking)
      const closedDayId = d.data?.day?.id;
      if (closedDayId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        (async () => {
          try {
            const token = localStorage.getItem("pos_token");
            if (isElectron && window.electronAPI) {
              const r = await fetch(`${API_BASE}/internalday/closingText/${closedDayId}`, { headers: { "Authorization": `Bearer ${token}` } });
              if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); }
            }
          } catch(e) {}
        })();
      }
      load();
    } else showToast(d.message, "error");
  };

  const doCloseByInvoice = async (invId) => {
    setSubmitting(true);
    const d = await api("/internalday/closeByInvoice", { method: "POST", body: JSON.stringify({ tcr_code: tcr, invoice_id: invId }) });
    setSubmitting(false);
    if (d.success) { showToast("Dan zatvoren po racunu!"); setShowCloseByInvoice(false); load(); } else showToast(d.message, "error");
  };

  const loadDayInvoices = async () => {
    const d = await api(`/internalday/invoices/${tcr}`);
    if (d.success) { setDayInvoices(d.data?.invoices || []); setShowCloseByInvoice(true); }
  };

  const doDeposit = async () => {
    setSubmitting(true);
    const d = await api("/deposits/register", { method: "POST", body: JSON.stringify({ tcr_code: tcr, deposit_amount: parseFloat(depositAmt) || 0 }) });
    setSubmitting(false);
    if (d.success) {
      showToast("Depozit registrovan!");
      setDepositAmt("");
      // Auto-print deposit receipt (non-blocking, delay to ensure session exists)
      if (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width")) {
        setTimeout(async () => {
          try {
            const sess = await api(`/cashregister/active/${tcr}`);
            if (sess.success && sess.data?.session?.id) {
              await printRegisterReport(sess.data.session.id, "opening");
            }
          } catch(e) {}
        }, 500);
      }
      load();
    } else showToast(d.message, "error");
  };

  const doClose = async (targetDate) => {
    setSubmitting(true);
    const d = await api("/cashregister/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr, actual_cash: parseFloat(actualCash) || 0, notes, target_date: targetDate || "today" }) });
    setSubmitting(false);
    if (d.success) {
      setClosingInfo(d.data); setActualCash(""); setNotes(""); load(); showToast("Blagajna zatvorena!");
      // Auto-print closing report (non-blocking)
      const sessId = d.data?.session?.id;
      if (sessId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printRegisterReport(sessId, "closing").catch(() => {});
      }
    } else showToast(d.message, "error");
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;
  if (!tcr) return <div style={{ padding: 50, textAlign: "center", color: S.muted }}><AlertCircle size={40} /><p style={{ marginTop: 12, fontSize: 16 }}>Izaberite TCR kod u Podešavanjima</p></div>;

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div><h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Blagajna</h2><p style={{ fontSize: 14, color: S.muted, margin: 0 }}>TCR: <span style={{ fontFamily: S.mono, fontWeight: 600 }}>{tcr}</span></p></div>
        <button onClick={load} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} />Osvježi</button>
      </div>

      {/* Tabs: Depozit / Otvaranja / Zatvaranja (if internal day enabled) */}
      {internalEnabled && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {[{ id: "depozit", l: "Depozit (Poreska)" }, { id: "podizanje", l: "Podizanje" }, { id: "otvaranja", l: "Otvaranja (Interno)" }, { id: "zatvaranja", l: "Zatvaranja (Interno)" }].map(t => (
            <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: blagajnaTab === t.id ? S.dark : S.border, background: blagajnaTab === t.id ? S.dark : "white", color: blagajnaTab === t.id ? "white" : S.muted }}>{t.l}</button>
          ))}
        </div>
      )}

      {/* Internal Day Alert (when enabled but not opened) */}
      {internalEnabled && !internalDay && blagajnaTab === "depozit" && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={18} color="#92400e" /><span style={{ fontWeight: 700, color: "#92400e" }}>Interni dan nije otvoren!</span></div>
          <p style={{ fontSize: 13, color: "#78350f", margin: "6px 0 0" }}>Otvorite interni dan u tabu "Otvaranja" prije izdavanja racuna.</p>
        </div>
      )}

      {/* Internal Day Active Banner */}
      {internalEnabled && internalDay && blagajnaTab === "depozit" && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 14, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14 }}>Interni dan otvoren</div>
            <div style={{ fontSize: 12, color: "#166534" }}>Od: {internalDay.opened_at?.slice(0, 16).replace("T"," ")} · Promet: {fmt(internalSales?.total_sales || 0)} €</div>
          </div>
        </div>
      )}

      {/* OTVARANJA TAB */}
      {internalEnabled && blagajnaTab === "otvaranja" && (
        <div style={{ maxWidth: 520 }}>
          {!internalDay ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, textAlign: "center" }}>
              <Clock size={40} color={S.muted} />
              <p style={{ fontWeight: 600, fontSize: 17, margin: "12px 0 16px" }}>Otvorite interni dan</p>
              <p style={{ fontSize: 13, color: S.muted, marginBottom: 20 }}>Svi racuni od otvaranja do zatvaranja ce se racunati kao jedan radni dan.</p>
              <button onClick={doOpenInternalDay} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? "..." : "Otvori dan"}</button>
            </div>
          ) : (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Dan je otvoren</span></div>
              <Row label="Otvoreno" value={internalDay.opened_at?.slice(0, 16).replace("T", " ")} />
              {internalSales && <>
                <Row label="Gotovina" value={`${fmt(internalSales.cash_sales)} €`} />
                <Row label="Kartica" value={`${fmt(internalSales.card_sales)} €`} />
                <Row label="Ukupno" value={`${fmt(internalSales.total_sales)} €`} />
                <Row label="Racuni" value={internalSales.transaction_count || 0} />
              </>}
            </div>
          )}
          {/* History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "20px 0 10px", color: S.muted }}>Istorija otvaranja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(f.toISOString().slice(0,10)); setHistDateTo(t.toISOString().slice(0,10)); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {internalHistory.filter(d => (d.status === "open" || d.opened_at) && d.opened_at?.slice(0,10) >= histDateFrom && d.opened_at?.slice(0,10) <= histDateTo).slice(0, 30).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.opened_at?.slice(0, 16).replace("T", " ")}</div>
                <div style={{ fontSize: 12, color: S.muted }}>{d.status === "open" ? "Otvoren" : `Zatvoren: ${d.closed_at?.slice(0, 16).replace("T", " ")} · ${fmt(d.total_sales)} €`}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/internalday/closingText/${d.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ZATVARANJA TAB */}
      {internalEnabled && blagajnaTab === "zatvaranja" && (
        <div style={{ maxWidth: 520 }}>
          {internalDay ? (
            <div>
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori interni dan</h3>
                {internalSales && <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <Row label="Gotovina" value={`${fmt(internalSales.cash_sales)} €`} />
                  <Row label="Kartica" value={`${fmt(internalSales.card_sales)} €`} />
                  <Row label="Ukupno" value={`${fmt(internalSales.total_sales)} €`} />
                  <Row label="Racuni" value={internalSales.transaction_count || 0} />
                  {todayWithdrawn > 0 && <Row label="Podignuto" value={`-${fmt(todayWithdrawn)} €`} />}
                </div>}
                <button onClick={doCloseInternalDay} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, marginBottom: 10 }}><XCircle size={17} />{submitting ? "..." : "Zatvori dan sada"}</button>
                <button onClick={loadDayInvoices} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 11, fontSize: 14 }}><Clock size={15} /><span style={{ marginLeft: 6 }}>Zatvori po racunu (zaboravljen)</span></button>
              </div>
            </div>
          ) : (
            <div style={{ background: "#f9fafb", borderRadius: 12, padding: 24, textAlign: "center" }}>
              <CheckCircle size={40} color={S.green} />
              <p style={{ fontWeight: 600, margin: "12px 0" }}>Nema otvorenog dana</p>
            </div>
          )}
          {/* History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "20px 0 10px", color: S.muted }}>Istorija zatvaranja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(f.toISOString().slice(0,10)); setHistDateTo(t.toISOString().slice(0,10)); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {internalHistory.filter(d => d.status === "closed" && d.closed_at?.slice(0,10) >= histDateFrom && d.closed_at?.slice(0,10) <= histDateTo).slice(0, 30).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.opened_at?.slice(0, 10)} · {fmt(d.total_sales)} €</div>
                <div style={{ fontSize: 12, color: S.muted }}>Od {d.opened_at?.slice(11, 16)} do {d.closed_at?.slice(11, 16)} · {d.transaction_count} racuna{d.is_official ? "" : " (privremeno)"}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/internalday/closingText/${d.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Close by Invoice Modal */}
      {showCloseByInvoice && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCloseByInvoice(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 28, width: 440, maxHeight: "80vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}><h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Izaberite posljednji racun</h3><button onClick={() => setShowCloseByInvoice(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button></div>
            <p style={{ fontSize: 13, color: S.muted, marginBottom: 14 }}>Unesite broj racuna koji ce biti posljednji za ovaj interni dan:</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input value={closeByInvSearch} onChange={e => setCloseByInvSearch(e.target.value)} placeholder="Broj racuna..." style={{ ...S.input, flex: 1 }} />
              <button onClick={async () => { if (!closeByInvSearch) return; const d = await api(`/invoices?search=${closeByInvSearch}&per_page=5&tcr_code=${tcr}`); if (d.success) setDayInvoices(d.data?.invoices || []); }} style={S.btn(S.dark)}>Trazi</button>
            </div>
            {dayInvoices.length === 0 ? <p style={{ color: S.muted, textAlign: "center", padding: 16, fontSize: 13 }}>Unesite broj racuna i kliknite Trazi</p> : dayInvoices.map(inv => (
              <div key={inv.id} onClick={() => doCloseByInvoice(inv.id)} style={{ padding: "12px 14px", borderRadius: 8, border: `1px solid ${S.border}`, marginBottom: 6, cursor: "pointer", background: "#f9fafb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number}</div><div style={{ fontSize: 12, color: S.muted }}>{fmtDateTime(inv.invoice_date)}</div></div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{fmt(inv.total_amount)} €</div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* ═══ DEPOZIT TAB CONTENT (Internal Day ON) ═══ */}
      {internalEnabled && blagajnaTab === "depozit" && (
        <div style={{ maxWidth: 520 }}>
          {!depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Registruj depozit</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Poreska prijava depozita za danas</p>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos depozita (€)</label>
              <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
              <button onClick={doDeposit} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj depozit"}</button>
            </div>
          ) : (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Depozit registrovan za danas</span></div>
              <div style={{ fontSize: 13, color: "#166534", marginTop: 6 }}>Depozit: {fmt(active?.initial_deposit || 0)} €</div>
            </div>
          )}
          {/* Deposit History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>Istorija depozita</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(f.toISOString().slice(0,10)); setHistDateTo(t.toISOString().slice(0,10)); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {sessions.filter(s => s.tcr_code === tcr && s.opened_at?.slice(0,10) >= histDateFrom && s.opened_at?.slice(0,10) <= histDateTo).slice(0, 30).map(s => (
            <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(s.opened_at)}</div>
                <div style={{ fontSize: 12, color: S.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={async () => { await printRegisterReport(s.id, "opening"); showToast("Štampano!"); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                <button onClick={() => openPdf(`/cashregister/openingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PODIZANJE (WITHDRAWAL) TAB ═══ */}
      {(internalEnabled ? blagajnaTab === "podizanje" : blagajnaTab === "podizanje") && (
        <div style={{ maxWidth: 520 }}>
          {/* Withdrawal form — only if deposit exists today */}
          {depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Podizanje gotovine</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Registruj podizanje gotovine iz kase (šalje se na Poresku)</p>
              {todayWithdrawn > 0 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
                  Danas podignuto: <strong>{fmt(todayWithdrawn)} €</strong>
                </div>
              )}
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos (€)</label>
              <input type="number" step="0.01" value={withdrawAmt} onChange={e => setWithdrawAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, marginBottom: 12, marginTop: 6 }} />
              <label style={{ fontSize: 14, fontWeight: 600 }}>Razlog (opciono)</label>
              <input value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} placeholder="npr. kupovina namirnica..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
              <button onClick={doWithdraw} disabled={submitting} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, color: "white" }}>
                {submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj podizanje"}
              </button>
            </div>
          ) : (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 16 }}>
              <AlertCircle size={30} color="#92400e" />
              <p style={{ fontWeight: 600, margin: "10px 0 0", color: "#92400e" }}>Depozit nije registrovan</p>
              <p style={{ fontSize: 13, color: "#78350f", margin: "6px 0 0" }}>Prvo registrujte depozit u tab "Depozit (Poreska)"</p>
            </div>
          )}
          {/* Withdrawal History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>Istorija podizanja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(f.toISOString().slice(0,10)); setHistDateTo(t.toISOString().slice(0,10)); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {withdrawals.filter(w => w.issue_date?.slice(0,10) >= histDateFrom && w.issue_date?.slice(0,10) <= histDateTo).length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>Nema podizanja u odabranom periodu</div>
          ) : withdrawals.filter(w => w.issue_date?.slice(0,10) >= histDateFrom && w.issue_date?.slice(0,10) <= histDateTo).map(w => (
            <div key={w.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDateTime(w.issue_date)} · <span style={{ color: "#f59e0b" }}>-{fmt(w.deposit_amount)} €</span></div>
                <div style={{ fontSize: 12, color: S.muted }}>{w.fic ? `FIC: ${w.fic.slice(0,20)}...` : w.status}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${w.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ DEPOZIT TAB CONTENT (Internal Day OFF) ═══ */}
      {!internalEnabled && <>
      <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
        {[{ id: "depozit", l: "Depozit (Poreska)" }, { id: "podizanje", l: "Podizanje" }].map(t => (
          <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "8px 18px", border: `1px solid ${blagajnaTab === t.id ? S.dark : S.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", background: blagajnaTab === t.id ? S.dark : "white", color: blagajnaTab === t.id ? "white" : S.muted, fontFamily: S.font }}>{t.l}</button>
        ))}
      </div>
      {blagajnaTab === "depozit" && <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 380px", maxWidth: 520 }}>
          {/* Alert: unclosed previous session */}
          {unclosedSession && (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><AlertCircle size={18} color="#92400e" /><span style={{ fontWeight: 700, color: "#92400e", fontSize: 15 }}>Nezatvorena prethodna sesija!</span></div>
              <p style={{ fontSize: 14, color: "#78350f", margin: "0 0 12px" }}>Imate otvorenu sesiju od {fmtDate(unclosedSession.session?.opened_at)}. Morate je zatvoriti prije nastavka.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, color: "#78350f" }}>Stvarna gotovina (€)</label><input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="0.00" style={{ ...S.input, padding: "8px 12px" }} /></div>
                <button onClick={() => doClose("oldest")} disabled={submitting} style={{ ...S.btn("#92400e"), height: 42 }}>{submitting ? "..." : "Zatvori staru sesiju"}</button>
              </div>
            </div>
          )}

          {closingInfo && (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 16, fontWeight: 700 }}>Izvještaj zatvaranja</h3>
              <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14 }}>
                <Row label="Gotovina" value={`${fmt(closingInfo.cash_sales)} €`} /><Row label="Kartica" value={`${fmt(closingInfo.card_sales)} €`} /><Row label="Ukupan promet" value={`${fmt(closingInfo.total_sales)} €`} /><Row label="Transakcije" value={closingInfo.transaction_count || 0} /><Row label="Očekivana got." value={`${fmt(closingInfo.expected_cash)} €`} /><Row label="Stvarna got." value={`${fmt(closingInfo.actual_cash)} €`} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px solid #e5e7eb" }}><span style={{ fontWeight: 700 }}>Razlika</span><span style={{ fontWeight: 700, color: parseFloat(closingInfo.difference || 0) === 0 ? S.green : S.red }}>{fmt(closingInfo.difference)} €</span></div>
              </div>
              <button onClick={() => setClosingInfo(null)} style={{ ...S.btnSm("#f3f4f6", S.muted), width: "100%", justifyContent: "center", marginTop: 12 }}>OK</button>
            </div>
          )}

          {!unclosedSession && !depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Otvori blagajnu</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Registrujte depozit za danas</p>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos depozita (€)</label>
              <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
              <button onClick={doDeposit} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj depozit"}</button>
            </div>
          ) : !unclosedSession && active ? (
            <div>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Blagajna otvorena</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Stat label="Depozit" value={`${fmt(active.initial_deposit)} €`} /><Stat label="Gotovina" value={`${fmt(active.sales?.cash_sales || 0)} €`} /><Stat label="Kartica" value={`${fmt(active.sales?.card_sales || 0)} €`} />
                  <Stat label="Ukupan promet" value={`${fmt(active.sales?.total_sales || 0)} €`} /><Stat label="Računi" value={active.sales?.transaction_count || 0} />{todayWithdrawn > 0 && <Stat label="Podignuto" value={`-${fmt(todayWithdrawn)} €`} />}<Stat label="Očekivana got." value={`${fmt(active.expected_cash)} €`} />
                </div>
              </div>
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
                {internalEnabled ? (
                  <div style={{ textAlign: "center", color: S.muted }}>
                    <Clock size={30} color={S.muted} />
                    <p style={{ fontWeight: 600, margin: "10px 0 4px" }}>Koristite interni dan</p>
                    <p style={{ fontSize: 13, margin: 0 }}>Zatvaranje blagajne se vrsi kroz tab "Zatvaranja (Interno)"</p>
                  </div>
                ) : (<>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori blagajnu</h3>
                <label style={{ fontSize: 14, fontWeight: 600 }}>Stvarna gotovina (€)</label>
                <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder={fmt(active.expected_cash)} style={{ ...S.input, marginBottom: 14, marginTop: 6 }} />
                <label style={{ fontSize: 14, fontWeight: 600 }}>Napomena</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="opciono..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
                <button onClick={() => doClose("today")} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}><XCircle size={17} />{submitting ? "..." : "Zatvori blagajnu"}</button>
                </>)}
              </div>
            </div>
          ) : !unclosedSession ? (
            continueMode && todaySales ? (
              <div>
                <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><RefreshCw size={18} color={S.accent} /><span style={{ fontWeight: 700, color: S.accent, fontSize: 16 }}>Nastavak rada</span></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <Stat label="Gotovina" value={`${fmt(todaySales.cash_sales || 0)} €`} />
                    <Stat label="Kartica" value={`${fmt(todaySales.card_sales || 0)} €`} />
                    <Stat label="Ukupan promet" value={`${fmt(todaySales.total_sales || 0)} €`} />
                    <Stat label="Računi" value={todaySales.transaction_count || 0} />
                  </div>
                </div>
                <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
                  {internalEnabled ? (
                    <div style={{ textAlign: "center", color: S.muted }}>
                      <Clock size={30} color={S.muted} />
                      <p style={{ fontWeight: 600, margin: "10px 0 4px" }}>Koristite interni dan</p>
                      <p style={{ fontSize: 13, margin: 0 }}>Zatvaranje se vrsi kroz tab "Zatvaranja (Interno)"</p>
                    </div>
                  ) : (<>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori blagajnu ponovo</h3>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Stvarna gotovina (€)</label>
                  <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="0.00" style={{ ...S.input, marginBottom: 14, marginTop: 6 }} />
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Napomena</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="opciono..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
                  <button onClick={() => doClose("today")} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}><XCircle size={17} />{submitting ? "..." : "Zatvori blagajnu"}</button>
                  </>)}
                </div>
              </div>
            ) : (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 24, textAlign: "center" }}>
                <CheckCircle size={44} color={S.green} />
                <p style={{ fontWeight: 600, marginTop: 12, fontSize: 17, margin: "12px 0 16px" }}>Blagajna zatvorena za danas</p>
                <button onClick={async () => { const d = await api(`/cashregister/todaySales/${tcr}`); if (d.success) { setTodaySales(d.data.sales || d.data); setContinueMode(true); } else showToast("Greška pri učitavanju", "error"); }} style={{ ...S.btn(S.accent), justifyContent: "center" }}><RefreshCw size={15} />Nastavi opet</button>
              </div>
            )
          ) : null}
        </div>

        <div style={{ flex: "1 1 300px" }}>
          {(() => {
            const tcrSessions = sessions.filter(s => s.tcr_code === tcr);
            const openings = tcrSessions.filter(s => s.opened_at?.slice(0,10) >= histDateFrom && s.opened_at?.slice(0,10) <= histDateTo);
            const closings = tcrSessions.filter(s => s.closed_at && s.closed_at?.slice(0,10) >= histDateFrom && s.closed_at?.slice(0,10) <= histDateTo);
            return (<div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
                <span style={{ fontSize: 12, color: S.muted }}>—</span>
                <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
                {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
                  <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(f.toISOString().slice(0,10)); setHistDateTo(t.toISOString().slice(0,10)); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
                <button onClick={() => setHistTab("openings")} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: histTab === "openings" ? S.dark : S.border, background: histTab === "openings" ? S.dark : "white", color: histTab === "openings" ? "white" : S.muted }}>Otvaranja</button>
                <button onClick={() => setHistTab("closings")} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: histTab === "closings" ? S.dark : S.border, background: histTab === "closings" ? S.dark : "white", color: histTab === "closings" ? "white" : S.muted }}>Zatvaranja</button>
              </div>
              {histTab === "openings" && (openings.length === 0 ? <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}><BookOpen size={30} /><p style={{ fontSize: 14, marginTop: 8 }}>Nema otvaranja</p></div>
              : openings.slice(0, 20).map(s => (
                <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div><div style={{ fontWeight: 600, fontSize: 14 }}>{fmtDate(s.opened_at)}</div><div style={{ fontSize: 12, color: S.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div></div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={async () => { await printRegisterReport(s.id, "opening"); showToast("Štampano!") }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                      <button onClick={() => openPdf(`/cashregister/openingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
                    </div>
                  </div>
                </div>
              )))}
              {histTab === "closings" && (closings.length === 0 ? <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}><BookOpen size={30} /><p style={{ fontSize: 14, marginTop: 8 }}>Nema zatvaranja</p></div>
              : closings.slice(0, 20).map(s => (
                <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtDate(s.closed_at)}</div>
                      <div style={{ fontSize: 12, color: S.muted }}>Promet: {fmt(s.total_sales)} € · Razlika: <span style={{ color: parseFloat(s.difference || 0) === 0 ? "#15803d" : S.red }}>{fmt(s.difference)} €</span></div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={async () => { await printRegisterReport(s.id, "closing"); showToast("Štampano!") }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                      <button onClick={() => openPdf(`/cashregister/closingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
                    </div>
                  </div>
                </div>
              )))}
              <button onClick={() => setShowCloseAny(true)} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", marginTop: 12, padding: "10px 16px", fontSize: 14 }}><Clock size={15} /><span style={{ marginLeft: 6 }}>Zatvori blagajnu za bilo koji dan</span></button>
            </div>);
          })()}
        </div>
      </div>
      }</>}

      {/* Close Any Day Modal */}
      {showCloseAny && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCloseAny(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 28, width: 420, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Zatvori blagajnu za odabrani dan</h3>
              <button onClick={() => setShowCloseAny(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Datum</label>
            <input type="date" value={closeAnyDate} onChange={e => setCloseAnyDate(e.target.value)} style={{ ...S.input, marginBottom: 14 }} />
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>TCR kod</label>
            <select value={closeAnyTcr} onChange={e => setCloseAnyTcr(e.target.value)} style={{ ...S.input, marginBottom: 14 }}>
              <option value={tcr}>{tcr} (trenutni)</option>
            </select>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Stvarna gotovina (€)</label>
            <input type="number" step="0.01" value={closeAnyCash} onChange={e => setCloseAnyCash(e.target.value)} placeholder="0.00" style={{ ...S.input, marginBottom: 18 }} />
            <button disabled={submitting || !closeAnyDate} onClick={async () => {
              setSubmitting(true);
              const d = await api("/cashregister/closeAnyDate", { method: "POST", body: JSON.stringify({ tcr_code: closeAnyTcr || tcr, target_date: closeAnyDate, actual_cash: parseFloat(closeAnyCash) || 0, notes: "Zatvoreno retroaktivno" }) });
              setSubmitting(false);
              if (d.success) { showToast("Blagajna zatvorena za " + closeAnyDate + "!"); setShowCloseAny(false); setCloseAnyDate(""); setCloseAnyCash(""); load(); } else showToast(d.message, "error");
            }} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, color: "white" }}>{submitting ? "..." : "Zatvori"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ DASHBOARD ═══
function InspectorSection({ tcr, showToast, inspReport, setInspReport, inspType, setInspType, inspFrom, setInspFrom, inspTo, setInspTo, inspLoading, setInspLoading, ikofPib, setIkofPib, ikofDate, setIkofDate, ikofInvNum, setIkofInvNum, ikofBu, setIkofBu, ikofTcr, setIkofTcr, ikofSoft, setIkofSoft, ikofAmount, setIkofAmount, ikofResult, setIkofResult }) {

  const fetchInsp = async () => {
    setInspLoading(true);
    const tcrParam = tcr ? `&tcr_code=${tcr}` : "";
    const d = await api(`/invoices/inspectorReport?type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`);
    setInspLoading(false);
    if (d.success) setInspReport(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const recreateIkof = async () => {
    const d = await api("/invoices/recreateIkof", { method: "POST", body: JSON.stringify({ pib: ikofPib, date: ikofDate, inv_num: ikofInvNum, bu_code: ikofBu, tcr_code: ikofTcr, soft_code: ikofSoft, total: ikofAmount }) });
    if (d.success) setIkofResult(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const printInspReport = async () => {
    if (!inspReport || !isElectron || !window.electronAPI) return;
    const token = localStorage.getItem("pos_token");
    const tcrParam = tcr ? `&tcr_code=${tcr}` : "";
    const r = await fetch(`${API_BASE}/invoices/inspectorReportText?type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`, { headers: { "Authorization": `Bearer ${token}` } });
    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
  };

  const exportCsv = () => {
    if (!inspReport) return;
    const q = v => `"${String(v || "").replace(/"/g, '""')}"`;
    let csv = "Izvještaj;" + q(inspReport.title) + "\nTCR;" + q(inspReport.tcr_code) + "\nPeriod;" + q(inspReport.period) + "\n\n";
    if (inspReport.summary) {
      const s = inspReport.summary;
      csv += "Ukupno računa;" + (s.total_invoices || 0) + "\n";
      csv += "Gotovina;" + fmt(s.cash_revenue || 0) + "\n";
      csv += "Kartica;" + fmt(s.card_revenue || 0) + "\n";
      if (parseFloat(s.other_revenue || 0) > 0) csv += "Ostalo;" + fmt(s.other_revenue) + "\n";
      csv += "Bez PDV;" + fmt(s.total_without_vat || 0) + "\n";
      csv += "PDV;" + fmt(s.total_vat || 0) + "\n";
      csv += "UKUPNO;" + fmt(s.total_revenue || 0) + "\n\n";
    }
    if (inspReport.invoices?.length > 0) {
      const hasItems = inspReport.invoices.some(inv => inv.items?.length > 0);
      if (hasItems) {
        csv += "Broj računa;Datum;Iznos;Način plaćanja;FIC;Stavka;Kol.;Cijena;Ukupno;PDV%;PDV iznos\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + ";;;;;\n";
          if (inv.items) inv.items.forEach(item => {
            csv += ";;;;;" + q(item.name) + ";" + item.quantity + ";" + fmt(item.unit_price || 0) + ";" + fmt(item.total_price || 0) + ";" + (item.vat_rate || 0) + "%;" + fmt(item.vat_amount || 0) + "\n";
          });
        });
      } else {
        csv += "Broj računa;Datum;Iznos;Način plaćanja;FIC\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + "\n";
        });
      }
    }
    if (inspReport.daily_subtotals?.length > 0) {
      csv += "\nDatum;Broj računa;Promet\n";
      inspReport.daily_subtotals.forEach(ds => { csv += q(ds.day) + ";" + ds.count + ";" + fmt(ds.revenue) + "\n"; });
    }
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `inspekcija_${inspType}_${inspFrom}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: "#92400e" }}>
        Ovi izvještaji su namijenjeni za poresku inspekciju u skladu sa Zakonom o fiskalizaciji. Svi podaci uključuju oznaku ENU (TCR) uređaja.
      </div>

      <div style={{ display: "flex", gap: 5, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { id: "status", l: "Trenutno stanje" },
          { id: "daily", l: "Dnevni fiskalni" },
          { id: "periodic", l: "Periodični" },
          { id: "journal", l: "Elektronski žurnal" },
          { id: "offline", l: "Offline žurnal" },
          { id: "corrective", l: "Korektivni žurnal" },
          { id: "ikof", l: "IKOF rekreacija" },
        ].map(t => (
          <button key={t.id} onClick={() => { setInspType(t.id); setInspReport(null); setIkofResult(null); }} style={{ padding: "6px 12px", border: `1px solid ${inspType === t.id ? "#92400e" : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: inspType === t.id ? "#fef3c7" : "white", color: inspType === t.id ? "#92400e" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {inspType === "ikof" ? (
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 14px" }}>IKOF rekreacija</h3>
          <p style={{ fontSize: 13, color: S.muted, margin: "0 0 14px" }}>Unesite parametre računa za provjeru IKOF-a</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>PIB</label><input value={ikofPib} onChange={e => setIkofPib(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="PIB poreskog obveznika" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Datum i vrijeme</label><input value={ikofDate} onChange={e => setIkofDate(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="2026-03-14T10:30:00+01:00" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Broj računa</label><input value={ikofInvNum} onChange={e => setIkofInvNum(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="1/2026/ne548er229" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod poslovne jedinice</label><input value={ikofBu} onChange={e => setIkofBu(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="xx123xx456" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod ENU (TCR)</label><input value={ikofTcr} onChange={e => setIkofTcr(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="ne548er229" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod softvera</label><input value={ikofSoft} onChange={e => setIkofSoft(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="ss123ss456" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Ukupan iznos</label><input value={ikofAmount} onChange={e => setIkofAmount(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="43.56" /></div>
          </div>
          <button onClick={recreateIkof} style={{ ...S.btn(S.dark), marginTop: 14, justifyContent: "center", padding: "10px 24px" }}>Generiši IKOF</button>
          {ikofResult && (
            <div style={{ marginTop: 14, background: "#f0fdf4", borderRadius: 8, padding: 14, border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d", marginBottom: 6 }}>Generisani IKOF:</div>
              <div style={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all", color: S.dark }}>{ikofResult.iic}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d", marginTop: 10, marginBottom: 4 }}>IIC Potpis:</div>
              <div style={{ fontFamily: "monospace", fontSize: 10, wordBreak: "break-all", color: S.muted }}>{ikofResult.iic_signature}</div>
            </div>
          )}
        </div>
      ) : (
        <>
          {inspType !== "status" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
              <input type="date" value={inspFrom} onChange={e => setInspFrom(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
              {inspType !== "daily" && <>
                <span style={{ color: S.muted }}>—</span>
                <input type="date" value={inspTo} onChange={e => setInspTo(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
              </>}
            </div>
          )}
          <button onClick={fetchInsp} disabled={inspLoading} style={{ ...S.btn("#92400e"), marginBottom: 16, justifyContent: "center", padding: "10px 20px", color: "white" }}>
            {inspLoading ? <Loader2 size={15} className="spin" /> : <FileText size={15} />}
            <span style={{ marginLeft: 6 }}>{inspLoading ? "Učitavanje..." : "Generiši izvještaj"}</span>
          </button>

          {inspReport && (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{inspReport.title}</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={printInspReport} style={S.btnSm("#f3f4f6", S.muted)} title="Štampaj"><Printer size={14} /></button>
                  <button onClick={exportCsv} style={S.btnSm("#f3f4f6", S.muted)} title="Export CSV"><Download size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 12 }}>TCR: {inspReport.tcr_code} · Period: {inspReport.period}</div>

              {inspReport.summary && (
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <Row label="Ukupno računa" value={inspReport.summary.total_invoices || 0} />
                  <Row label="Gotovina" value={`${fmt(inspReport.summary.cash_revenue)} €`} />
                  <Row label="Kartica" value={`${fmt(inspReport.summary.card_revenue)} €`} />
                  {parseFloat(inspReport.summary.other_revenue || 0) > 0 && <Row label="Ostalo" value={`${fmt(inspReport.summary.other_revenue)} €`} />}
                  <Row label="Bez PDV" value={`${fmt(inspReport.summary.total_without_vat)} €`} />
                  <Row label="PDV" value={`${fmt(inspReport.summary.total_vat)} €`} />
                  <div style={{ borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 800 }}>UKUPNO</span>
                    <span style={{ fontWeight: 800, color: S.green }}>{fmt(inspReport.summary.total_revenue)} €</span>
                  </div>
                </div>
              )}

              {inspReport.invoices?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 8px", color: S.muted }}>Stavke ({inspReport.invoices.length})</h4>
                  <div style={{ maxHeight: 400, overflow: "auto", border: `1px solid ${S.border}`, borderRadius: 8 }}>
                    {inspReport.invoices.map((inv, idx) => (
                      <div key={idx} style={{ padding: "8px 12px", borderBottom: `1px solid ${S.border}`, fontSize: 12, background: idx % 2 === 0 ? "#f9fafb" : "white" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 600 }}>{inv.invoice_number}</span>
                          <span style={{ fontWeight: 700 }}>{fmt(inv.total_amount)} €</span>
                        </div>
                        <div style={{ color: S.muted, fontSize: 11 }}>
                          {inv.invoice_date} · {inv.payment_method} · FIC: {inv.fic?.slice(0, 16) || "—"}...
                        </div>
                        {inv.items && inv.items.map((item, j) => (
                          <div key={j} style={{ paddingLeft: 12, fontSize: 11, color: "#6b7280" }}>
                            {item.quantity}x {item.name} @ {fmt(item.unit_price)} = {fmt(item.total_price)} €
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {inspReport.daily_subtotals?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 8px", color: S.muted }}>Dnevni podzbirovi</h4>
                  {inspReport.daily_subtotals.map((ds, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: idx % 2 === 0 ? "#f9fafb" : "white", borderRadius: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{ds.day}</span>
                      <span>{ds.count} rač. · {fmt(ds.revenue)} €</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DashboardView({ user, showToast }) {
  const [stats, setStats] = useState(null); const [monthStats, setMonthStats] = useState(null); const [yearStats, setYearStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmounts, setShowAmounts] = useState(false);
  const [reportTab, setReportTab] = useState("pregled");
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalHistory, setInternalHistory] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const tcr = getTcr();

  // Detailed report state
  const [detailedType, setDetailedType] = useState("daily"); // daily, monthly, yearly
  const [detailedDate, setDetailedDate] = useState(new Date().toISOString().slice(0, 10));
  const [detailedFrom, setDetailedFrom] = useState(new Date().toISOString().slice(0, 10));
  const [detailedTo, setDetailedTo] = useState(new Date().toISOString().slice(0, 10));
  const [detailedMonth, setDetailedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [detailedYear, setDetailedYear] = useState(new Date().getFullYear().toString());
  const [detailedRange, setDetailedRange] = useState(false); // single day vs range
  const [detailedData, setDetailedData] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);

  // Inspector report state
  const [inspReport, setInspReport] = useState(null);
  const [inspType, setInspType] = useState("status");
  const [inspFrom, setInspFrom] = useState(new Date().toISOString().slice(0, 10));
  const [inspTo, setInspTo] = useState(new Date().toISOString().slice(0, 10));
  const [inspLoading, setInspLoading] = useState(false);
  const [ikofPib, setIkofPib] = useState("");
  const [ikofDate, setIkofDate] = useState("");
  const [ikofInvNum, setIkofInvNum] = useState("");
  const [ikofBu, setIkofBu] = useState("");
  const [ikofTcr, setIkofTcr] = useState(tcr || "");
  const [ikofSoft, setIkofSoft] = useState("");
  const [ikofAmount, setIkofAmount] = useState("");
  const [ikofResult, setIkofResult] = useState(null);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    const tcrParam = tcr ? `&tcr_code=${tcr}` : '';
    const [t, m, y, idSet, idHist, invs] = await Promise.all([
      api(`/invoices/stats?period=today${tcrParam}`),
      api(`/invoices/stats?period=month${tcrParam}`),
      api(`/invoices/stats?period=year${tcrParam}`),
      api("/internalday/settings"),
      api(`/internalday/history/${tcr || "all"}`),
      api(`/invoices?per_page=10${tcr ? "&tcr_code=" + tcr : ""}`)
    ]);
    if (t.success) setStats(t.data.stats);
    if (m.success) setMonthStats(m.data.stats);
    if (y.success) setYearStats(y.data.stats);
    if (idSet.success) setInternalEnabled(!!idSet.data?.internal_day_enabled);
    if (idHist.success) setInternalHistory(idHist.data?.days || []);
    if (invs.success) setRecentInvoices(invs.data?.invoices || []);
    setLoading(false);
  };

  const fetchDetailed = async () => {
    setDetailedLoading(true);
    const tcrParam = tcr ? `&tcr_code=${tcr}` : '';
    let url = `/invoices/detailedReport?type=${detailedType}${tcrParam}`;
    if (detailedType === "daily") {
      if (detailedRange) url += `&date_from=${detailedFrom}&date_to=${detailedTo}`;
      else url += `&date=${detailedDate}`;
    } else if (detailedType === "monthly") {
      url += `&month=${detailedMonth}`;
    } else {
      url += `&year=${detailedYear}`;
    }
    const d = await api(url);
    setDetailedLoading(false);
    if (d.success) setDetailedData(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const printDetailedReport = async () => {
    if (!detailedData || !isElectron || !window.electronAPI) return;
    const s = detailedData.summary;
    const w = 48;
    const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r;
    const f = n => Number(n || 0).toFixed(2).replace(".", ",");
    let out = "\x18\x1B\x61\x00\x1B\x45\x00\x1D\x21\x00\x1B\x74\x00\x1B\x4D\x00";
    out += "\x1B\x61\x01\x1B\x45\x01";
    const typeLabels = { daily: "DNEVNI IZVJESTAJ", monthly: "MJESECNI IZVJESTAJ", yearly: "GODISNJI IZVJESTAJ" };
    out += (typeLabels[detailedData.type] || "IZVJESTAJ") + "\n";
    out += "\x1B\x45\x00";
    out += detailedData.label + "\n";
    out += "=".repeat(w) + "\n\x1B\x61\x00";
    out += lr("Racuni:", (s.total_invoices || 0) + "") + "\n";
    out += lr("Gotovina:", f(s.cash_revenue) + " EUR") + "\n";
    out += lr("Kartica:", f(s.card_revenue) + " EUR") + "\n";
    if (parseFloat(s.other_revenue || 0) > 0) out += lr("Ostalo:", f(s.other_revenue) + " EUR") + "\n";
    out += "-".repeat(w) + "\n";
    out += lr("Bez PDV:", f(s.total_without_vat) + " EUR") + "\n";
    out += lr("PDV:", f(s.total_vat) + " EUR") + "\n";
    out += "=".repeat(w) + "\n";
    out += "\x1B\x45\x01\x1D\x21\x11";
    out += lr("UKUPNO:", f(s.total_revenue) + " EUR") + "\n";
    out += "\x1D\x21\x00\x1B\x45\x00";
    out += "=".repeat(w) + "\n";
    if (detailedData.storno && parseInt(detailedData.storno.cnt) > 0) {
      out += lr("Stornirano:", detailedData.storno.cnt + " (" + f(detailedData.storno.amount) + " EUR)") + "\n";
    }
    // Breakdown
    const items = detailedData.days || detailedData.months || [];
    if (items.length > 1) {
      out += "\n" + "-".repeat(w) + "\n";
      out += "\x1B\x45\x01" + "DETALJI\n" + "\x1B\x45\x00";
      items.forEach(item => {
        const key = item.day || item.month;
        out += lr(key + " (" + item.invoices + ")", f(item.revenue) + " EUR") + "\n";
        out += "  Got: " + f(item.cash) + "  Kart: " + f(item.card) + "\n";
      });
    }
    out += "\n\x1B\x61\x01\nPowered by EFIS.PRO\n\n\n\n\n\x1D\x56\x41\x03\n\n\n\n\n\n\n\n";
    await window.electronAPI.printText(out);
    showToast("Štampano!");
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Izvještaji</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowAmounts(!showAmounts)} style={{ ...S.btn(showAmounts ? S.dark : "#e5e7eb"), padding: "8px 14px", fontSize: 13, color: showAmounts ? "white" : S.muted }}>
            <Eye size={15} /><span style={{ marginLeft: 6 }}>{showAmounts ? "Sakrij" : "Prikaži"}</span>
          </button>
          <button onClick={loadReports} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* Report Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[{ id: "pregled", l: "Pregled" }, { id: "detaljni", l: "Detaljni izvještaji" }, ...(internalEnabled ? [{ id: "interni", l: "Interni dani" }] : []), { id: "inspekcija", l: "Poreski inspektor" }].map(t => (
          <button key={t.id} onClick={() => setReportTab(t.id)} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: reportTab === t.id ? S.dark : S.border, background: reportTab === t.id ? S.dark : "white", color: reportTab === t.id ? "white" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {/* PREGLED TAB — Overview */}
      {reportTab === "pregled" && <>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Danas</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
          <StatCard icon={Receipt} label="Računi" value={stats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={Euro} label="Promet" value={`${fmt(stats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(stats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(stats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ovaj mjesec</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
          <StatCard icon={Receipt} label="Računi" value={monthStats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={TrendingUp} label="Promet" value={`${fmt(monthStats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(monthStats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(monthStats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ova godina</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <StatCard icon={Receipt} label="Računi" value={yearStats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={TrendingUp} label="Promet" value={`${fmt(yearStats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(yearStats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(yearStats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
      </>}

      {/* DETALJNI TAB — Detailed reports */}
      {reportTab === "detaljni" && (
        <div>
          {/* Type selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[{ id: "daily", l: "Dnevni" }, { id: "monthly", l: "Mjesečni" }, { id: "yearly", l: "Godišnji" }].map(t => (
              <button key={t.id} onClick={() => { setDetailedType(t.id); setDetailedData(null); }} style={{ padding: "7px 16px", border: `1px solid ${detailedType === t.id ? S.accent : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: detailedType === t.id ? S.accent : "white", color: detailedType === t.id ? "white" : S.muted }}>{t.l}</button>
            ))}
          </div>

          {/* Date pickers */}
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 18, marginBottom: 16 }}>
            {detailedType === "daily" && <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={detailedRange} onChange={e => setDetailedRange(e.target.checked)} style={{ marginRight: 6 }} />
                  Period (raspon dana)
                </label>
              </div>
              {detailedRange ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="date" value={detailedFrom} onChange={e => setDetailedFrom(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
                  <span style={{ color: S.muted }}>—</span>
                  <input type="date" value={detailedTo} onChange={e => setDetailedTo(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
                </div>
              ) : (
                <input type="date" value={detailedDate} onChange={e => setDetailedDate(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 170 }} />
              )}
            </>}
            {detailedType === "monthly" && (
              <input type="month" value={detailedMonth} onChange={e => setDetailedMonth(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 170 }} />
            )}
            {detailedType === "yearly" && (
              <select value={detailedYear} onChange={e => setDetailedYear(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 120 }}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            <button onClick={fetchDetailed} disabled={detailedLoading} style={{ ...S.btn(S.accent), marginTop: 12, justifyContent: "center", padding: "10px 24px", fontSize: 14 }}>
              {detailedLoading ? <Loader2 size={15} className="spin" /> : <BarChart3 size={15} />}
              <span style={{ marginLeft: 6 }}>{detailedLoading ? "Učitavanje..." : "Generiši izvještaj"}</span>
            </button>
          </div>

          {/* Report Results */}
          {detailedData && (() => {
            const s = detailedData.summary;
            return (
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                    {detailedData.type === "daily" ? "Dnevni" : detailedData.type === "monthly" ? "Mjesečni" : "Godišnji"} izvještaj — {detailedData.label}
                  </h3>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={printDetailedReport} style={S.btnSm("#f3f4f6", S.muted)} title="Štampaj"><Printer size={14} /></button>
                    <button onClick={() => {
                      const s = detailedData.summary;
                      let csv = `Izvještaj;${detailedData.type === "daily" ? "Dnevni" : detailedData.type === "monthly" ? "Mjesečni" : "Godišnji"}\nPeriod;${detailedData.label}\n\n`;
                      csv += `Ukupno računa;${s.total_invoices || 0}\nGotovina;${fmt(s.cash_revenue || 0)}\nKartica;${fmt(s.card_revenue || 0)}\n`;
                      if (parseFloat(s.other_revenue || 0) > 0) csv += `Ostalo;${fmt(s.other_revenue)}\n`;
                      csv += `Bez PDV;${fmt(s.total_without_vat || 0)}\nPDV;${fmt(s.total_vat || 0)}\nUKUPNO;${fmt(s.total_revenue || 0)}\n`;
                      if (detailedData.storno && parseInt(detailedData.storno.cnt) > 0) csv += `\nStornirano;${detailedData.storno.cnt};${fmt(detailedData.storno.amount || 0)}\n`;
                      const items = detailedData.days || detailedData.months || [];
                      if (items.length > 0) {
                        csv += "\nDatum;Računi;Gotovina;Kartica;Ukupno\n";
                        items.forEach(i => { csv += `${i.day || i.month};${i.invoices};${fmt(i.cash)};${fmt(i.card)};${fmt(i.revenue)}\n`; });
                      }
                      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
                      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `izvjestaj_${detailedData.type}_${detailedData.label}.csv`; a.click(); URL.revokeObjectURL(url);
                    }} style={S.btnSm("#f3f4f6", S.muted)} title="Export CSV"><Download size={14} /></button>
                  </div>
                </div>

                {/* Summary */}
                <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <Row label="Ukupno računa" value={s.total_invoices || 0} />
                  <Row label="Gotovina" value={showAmounts ? `${fmt(s.cash_revenue)} €` : "••••••"} />
                  <Row label="Kartica" value={showAmounts ? `${fmt(s.card_revenue)} €` : "••••••"} />
                  {parseFloat(s.other_revenue || 0) > 0 && <Row label="Ostalo (virman)" value={showAmounts ? `${fmt(s.other_revenue)} €` : "••••••"} />}
                  <div style={{ borderTop: `1px solid ${S.border}`, marginTop: 8, paddingTop: 8 }}>
                    <Row label="Bez PDV" value={showAmounts ? `${fmt(s.total_without_vat)} €` : "••••••"} />
                    <Row label="PDV" value={showAmounts ? `${fmt(s.total_vat)} €` : "••••••"} />
                  </div>
                  <div style={{ borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>UKUPNO</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: S.green }}>{showAmounts ? `${fmt(s.total_revenue)} €` : "••••••"}</span>
                  </div>
                </div>

                {/* Storno info */}
                {detailedData.storno && parseInt(detailedData.storno.cnt) > 0 && (
                  <div style={{ background: "#fef2f2", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: "#991b1b" }}>
                    Stornirano: <strong>{detailedData.storno.cnt} računa</strong> ({fmt(detailedData.storno.amount)} €)
                  </div>
                )}

                {/* Daily breakdown (for monthly/yearly/range) */}
                {(detailedData.days?.length > 0 || detailedData.months?.length > 0) && (
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>
                      {detailedData.months ? "Po mjesecima" : "Po danima"}
                    </h4>
                    <div style={{ fontSize: 12, color: S.muted, display: "grid", gridTemplateColumns: "1fr 60px 80px 80px 80px", gap: 4, padding: "4px 8px", fontWeight: 600 }}>
                      <span>Datum</span><span style={{ textAlign: "right" }}>Rač.</span><span style={{ textAlign: "right" }}>Gotovina</span><span style={{ textAlign: "right" }}>Kartica</span><span style={{ textAlign: "right" }}>Ukupno</span>
                    </div>
                    {(detailedData.days || detailedData.months || []).map((item, idx) => (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 60px 80px 80px 80px", gap: 4, padding: "8px", borderRadius: 6, background: idx % 2 === 0 ? "#f9fafb" : "white", fontSize: 13 }}>
                        <span style={{ fontWeight: 600 }}>{item.day || item.month}</span>
                        <span style={{ textAlign: "right" }}>{item.invoices}</span>
                        <span style={{ textAlign: "right" }}>{showAmounts ? `${fmt(item.cash)} €` : "••"}</span>
                        <span style={{ textAlign: "right" }}>{showAmounts ? `${fmt(item.card)} €` : "••"}</span>
                        <span style={{ textAlign: "right", fontWeight: 700 }}>{showAmounts ? `${fmt(item.revenue)} €` : "••"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* INTERNI DANI TAB */}
      {reportTab === "interni" && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 12px", color: S.muted }}>Istorija internih dana</h3>
          {internalHistory.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: S.muted }}><Clock size={36} /><p style={{ marginTop: 10 }}>Nema podataka</p></div>
          : internalHistory.filter(d => d.status === "closed" && d.is_official).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{d.opened_at?.slice(0, 10)}</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: S.green }}>{showAmounts ? `${fmt(d.total_sales)} €` : "••••••"}</div>
              </div>
              <div style={{ fontSize: 12, color: S.muted }}>
                {d.opened_at?.slice(11, 16)} - {d.closed_at?.slice(11, 16)} · {d.transaction_count} računa ·
                Got: {showAmounts ? `${fmt(d.cash_sales)} €` : "••"} · Kart: {showAmounts ? `${fmt(d.card_sales)} €` : "••"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PORESKI INSPEKTOR TAB */}
      {reportTab === "inspekcija" && <InspectorSection tcr={tcr} showToast={showToast}
        inspReport={inspReport} setInspReport={setInspReport} inspType={inspType} setInspType={setInspType}
        inspFrom={inspFrom} setInspFrom={setInspFrom} inspTo={inspTo} setInspTo={setInspTo}
        inspLoading={inspLoading} setInspLoading={setInspLoading}
        ikofPib={ikofPib} setIkofPib={setIkofPib} ikofDate={ikofDate} setIkofDate={setIkofDate}
        ikofInvNum={ikofInvNum} setIkofInvNum={setIkofInvNum} ikofBu={ikofBu} setIkofBu={setIkofBu}
        ikofTcr={ikofTcr} setIkofTcr={setIkofTcr} ikofSoft={ikofSoft} setIkofSoft={setIkofSoft}
        ikofAmount={ikofAmount} setIkofAmount={setIkofAmount} ikofResult={ikofResult} setIkofResult={setIkofResult}
      />}
    </div>
  );
}
// ═══ PRINTER SETTINGS COMPONENT ═══
function PrinterSettingsTab({ showToast }) {
  const [printerType, setPrinterType] = useState(() => localStorage.getItem("pos_printer_type") || (isElectron ? "usb" : "system"));
  const [printerAddr, setPrinterAddr] = useState(() => localStorage.getItem("pos_printer_addr") || "");
  const [paperWidth, setPaperWidth] = useState(() => localStorage.getItem("pos_paper_width") || "80");
  const [serverStatus, setServerStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [systemPrinters, setSystemPrinters] = useState([]);

  useEffect(() => {
    if (isElectron && window.electronAPI?.listPrinters) {
      window.electronAPI.listPrinters().then(p => setSystemPrinters(p || []));
      window.electronAPI.getSettings().then(s => {
        if (s.interface) setPrinterAddr(s.interface);
      });
    }
  }, []);

  const savePrinter = async () => {
    localStorage.setItem("pos_printer_type", printerType);
    localStorage.setItem("pos_printer_addr", printerAddr);
    localStorage.setItem("pos_paper_width", paperWidth);

    // If Electron, also save to main process
    if (isElectron && window.electronAPI) {
      const widthChars = paperWidth === "58" || paperWidth === "57" ? 32 : 48;
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: widthChars
      });
    }
    showToast("Postavke printera sacuvane!");
  };

  const testConnection = async () => {
    setTesting(true);
    setServerStatus(null);

    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.testConnection();
      setServerStatus({ ok: r.success, msg: r.message || (r.success ? "Povezan!" : "Greska") });
    } else {
      // Browser — just check if system print is available
      setServerStatus({ ok: true, msg: "Sistem za stampanje je dostupan. Koristice se sistemski dijalog za izbor printera." });
    }
    setTesting(false);
  };

  const testPrint = async () => {
    setTesting(true);
    const testText = "\n================================\n     EFIS.PRO - Test Print\n================================\nPrinter: OK | Sirina: " + paperWidth + "mm\n" + new Date().toLocaleString("sr-Latn-ME") + "\n================================\n\n\n";
    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.printText(testText);
      if (r.success) showToast("Stampano!"); else showToast(r.message || "Greska", "error");
    } else if (printerType === "network" && printerAddr) {
      try {
        const printUrl = (printerAddr || "").endsWith("/") ? printerAddr.slice(0,-1) + "/print/text" : printerAddr + "/print/text";
        const r = await fetch(printUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: testText }) });
        const result = await r.json();
        if (result.success) showToast("Stampano!"); else showToast(result.message || "Greska", "error");
      } catch (e) { showToast("Ne mogu se povezati na print server: " + e.message, "error"); }
    } else {
      const w = window.open("", "test", "width=350,height=400");
      if (w) {
        w.document.write('<html><head><style>@page{size:' + paperWidth + 'mm auto;margin:2mm;}body{font-family:monospace;text-align:center;padding:10mm;}</style></head><body><h2>EFIS.PRO</h2><p>Test Print</p><p>Sirina: ' + paperWidth + 'mm</p><p>' + new Date().toLocaleString("sr-Latn-ME") + '</p><hr><p>Stampanje radi!</p><script>setTimeout(function(){window.print();},500);<\/script></body></html>');
        w.document.close();
      }
    }
    setTesting(false);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginBottom: 16 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 16px" }}>Termalni printer</h3>

        {/* Platform indicator */}
        <div style={{ padding: "8px 14px", background: "#f0fdf4", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#15803d", display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={16} />
          {isElectron ? "Windows desktop — direktno stampanje" : isCapacitor ? "Mobilna aplikacija — sistemski printer" : "Web pretraživač — sistemski dijalog za štampu"}
        </div>

        {isElectron ? <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Printer adresa / interfejs</label>
          <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="tcp://192.168.1.100:9100 ili USB printer ime" style={{ ...S.input, marginBottom: 8, fontFamily: S.mono }} />
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 16 }}>
            Mrežni: tcp://IP:9100 · USB (Windows): \\localhost\ImeStampaca · USB (direktno): /dev/usb/lp0
          </div>
          {systemPrinters.length > 0 && <>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Ili izaberite sistemski printer</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {systemPrinters.map(p => (
                <button key={p.name} onClick={() => setPrinterAddr(p.name)} style={{ padding: "10px 14px", borderRadius: 8, border: `2px solid ${printerAddr === p.name ? S.green : S.border}`, background: printerAddr === p.name ? S.green + "10" : "white", textAlign: "left", cursor: "pointer", fontFamily: S.font, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>
                  {p.isDefault && <span style={{ marginLeft: 8, fontSize: 11, color: S.green }}>(default)</span>}
                </button>
              ))}
            </div>
          </>}
        </> : <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Način štampanja</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => { setPrinterType("system"); localStorage.setItem("pos_printer_type", "system"); }} style={{ flex: 1, padding: "12px", borderRadius: 8, border: `2px solid ${printerType === "system" ? S.green : S.border}`, background: printerType === "system" ? "#f0fdf4" : "white", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: S.font, color: printerType === "system" ? S.green : S.muted }}>Sistemski dijalog</button>
            <button onClick={() => { setPrinterType("network"); localStorage.setItem("pos_printer_type", "network"); }} style={{ flex: 1, padding: "12px", borderRadius: 8, border: `2px solid ${printerType === "network" ? S.green : S.border}`, background: printerType === "network" ? "#f0fdf4" : "white", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: S.font, color: printerType === "network" ? S.green : S.muted }}>Mrežni print server</button>
          </div>
          {printerType === "network" ? <>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Print server URL</label>
            <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="http://192.168.1.100:9100" style={{ ...S.input, marginBottom: 8, fontFamily: S.mono }} />
            <div style={{ fontSize: 12, color: S.muted, marginBottom: 16 }}>
              URL vašeg print servera. Emulator: http://10.0.2.2:9100
            </div>
          </> : <>
            <div style={{ padding: 16, background: "#f0f9ff", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#1d4ed8" }}>
              Stampanje koristi sistemski dijalog za izbor printera. Kad kliknete "Stampaj", otvara se prozor gdje birate printer.
            </div>
          </>}
        </>}

        <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Sirina papira</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {["57", "58", "76", "80"].map(w => (
            <button key={w} onClick={() => setPaperWidth(w)} style={{ padding: "10px 20px", borderRadius: 8, border: `2px solid ${paperWidth === w ? S.green : S.border}`, background: paperWidth === w ? S.green + "15" : "white", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: S.mono, color: paperWidth === w ? S.green : S.muted }}>{w}mm</button>
          ))}
        </div>

        <button onClick={savePrinter} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 12, fontSize: 15, marginBottom: 10 }}><Save size={16} /><span style={{ marginLeft: 8 }}>Sacuvaj postavke</span></button>
      </div>

      {/* Test Connection */}
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Testiranje</h3>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <button onClick={testConnection} disabled={testing} style={{ ...S.btn("#f3f4f6", S.dark), flex: 1, justifyContent: "center", padding: 11 }}>{testing ? "..." : "Provjeri konekciju"}</button>
          <button onClick={testPrint} disabled={testing} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center", padding: 11 }}><Printer size={15} /><span style={{ marginLeft: 6 }}>Test print</span></button>
        </div>
        {serverStatus && (
          <div style={{ padding: 14, borderRadius: 8, background: serverStatus.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${serverStatus.ok ? "#bbf7d0" : "#fecaca"}`, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
            {serverStatus.ok ? <CheckCircle size={18} color={S.green} /> : <XCircle size={18} color={S.red} />}
            <span style={{ color: serverStatus.ok ? "#15803d" : S.red }}>{serverStatus.msg}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ KASA SETTINGS COMPONENT ═══
function KasaSettingsTab({ showToast }) {
  const [idEnabled, setIdEnabled] = useState(false);
  const [liveReport, setLiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const tcr = getTcr();

  useEffect(() => {
    api("/internalday/settings").then(r => { if (r.success) setIdEnabled(!!r.data?.internal_day_enabled); });
  }, []);

  const fetchLive = async () => {
    if (!tcr) return showToast("Izaberite TCR", "error");
    setLoadingReport(true);
    const d = await api(`/internalday/liveReport/${tcr}`);
    setLoadingReport(false);
    if (d.success) setLiveReport(d.data);
    else showToast(d.message, "error");
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Interni pocetak/kraj dana</div>
            <div style={{ fontSize: 13, color: S.muted }}>Za objekte koji rade preko ponoci</div>
          </div>
          <button onClick={async () => {
            const nv = !idEnabled;
            setIdEnabled(nv);
            await api("/internalday/settings", { method: "POST", body: JSON.stringify({ internal_day_enabled: nv ? 1 : 0 }) });
            showToast(nv ? "Interni dan uključen" : "Interni dan isključen");
          }} style={{ width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: idEnabled ? S.green : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
            <div style={{ width: 22, height: 22, borderRadius: 11, background: "white", position: "absolute", top: 3, left: idEnabled ? 27 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
      </div>

      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Trenutno stanje kase</div>
        <button onClick={fetchLive} disabled={loadingReport} style={{ ...S.btn(S.dark), width: "100%", justifyContent: "center", padding: 12, fontSize: 15 }}>
          {loadingReport ? <Loader2 size={16} className="spin" /> : <BarChart3 size={16} />}
          <span style={{ marginLeft: 8 }}>{loadingReport ? "Ucitavanje..." : "Prikazi stanje"}</span>
        </button>
        {liveReport && (
          <div style={{ marginTop: 16, background: "#f9fafb", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>TCR: {tcr} · {liveReport.date}</div>
            <Row label="Depozit" value={`${fmt(liveReport.deposit)} €`} />
            {liveReport.total_withdrawn > 0 && <Row label="Podignuto" value={`-${fmt(liveReport.total_withdrawn)} €`} />}
            <Row label="Gotovina" value={`${fmt(liveReport.today_sales?.cash_sales || 0)} €`} />
            <Row label="Kartica" value={`${fmt(liveReport.today_sales?.card_sales || 0)} €`} />
            <Row label="Ukupan promet" value={`${fmt(liveReport.today_sales?.total_sales || 0)} €`} />
            <Row label="Racuni" value={liveReport.today_sales?.transaction_count || 0} />
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700 }}>Ocekivana gotovina</span>
              <span style={{ fontWeight: 700, color: S.green }}>{fmt(liveReport.expected_cash)} €</span>
            </div>
            {liveReport.internal_day && (
              <div style={{ marginTop: 10, padding: 10, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Interni dan otvoren od {liveReport.internal_day.opened_at?.slice(11, 16)}</div>
                <div style={{ fontSize: 13 }}>Promet internog dana: {fmt(liveReport.internal_day.sales?.total_sales || 0)} €</div>
              </div>
            )}
            <button onClick={async () => {
              if (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width")) {
                try {
                  const token = localStorage.getItem("pos_token");
                  const r = await fetch(`${API_BASE}/cashregister/liveReportText/${tcr}`, { headers: { "Authorization": `Bearer ${token}` } });
                  if (r.ok && isElectron && window.electronAPI) {
                    const text = await r.text();
                    await window.electronAPI.printText(text);
                    showToast("Štampano!");
                  } else {
                    showToast("Koristite Electron za štampu", "error");
                  }
                } catch(e) { showToast("Greška pri štampi", "error"); }
              } else showToast("Podesite štampač prvo", "error");
            }} style={{ ...S.btn("#f3f4f6", S.muted), width: "100%", justifyContent: "center", marginTop: 10 }}><Printer size={14} />Štampaj stanje</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ SETTINGS VIEW ═══
function SettingsView({ user, showToast, onTablesToggle }) {
  const [tcrs, setTcrs] = useState([]);
  const [selectedTcr, setSelectedTcr] = useState(getTcr());
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");
  const [fastMenu, setFastMenu] = useState(() => { try { return JSON.parse(localStorage.getItem("pos_fast_menu") || "[]"); } catch { return []; } });
  const [editProd, setEditProd] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editPartner, setEditPartner] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  // Tables settings
  const [tablesEnabled, setTablesEnabled] = useState(false);
  const [rTables, setRTables] = useState([]);
  const [editTable, setEditTable] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [cfgData, prodData, catData, partData, tablesSettings, tablesData] = await Promise.all([
      api("/auth/company-config"),
      api("/products"),
      api("/products/categories"),
      api("/partners"),
      api("/tables/settings"),
      api("/tables"),
    ]);
    if (cfgData.success) {
      const allTcrs = cfgData.data.all_tcrs || [];
      setTcrs(allTcrs);
      const storedTcr = localStorage.getItem("pos_tcr");
      const validTcr = allTcrs.find(t => t.tcr_code === storedTcr);
      if (validTcr) {
        setSelectedTcr(validTcr.tcr_code);
      } else if (allTcrs.length > 0) {
        setSelectedTcr(allTcrs[0].tcr_code);
        localStorage.setItem("pos_tcr", allTcrs[0].tcr_code);
      } else {
        setSelectedTcr("");
        localStorage.removeItem("pos_tcr");
      }
    }
    if (prodData.success) setProducts(prodData.data?.products || []);
    if (catData.success) setCategories(catData.data?.categories || []);
    if (partData.success) setPartners(partData.data?.partners || []);
    if (tablesSettings.success) setTablesEnabled(!!tablesSettings.data?.tables_enabled);
    if (tablesData.success) setRTables(tablesData.data?.tables || []);
    setLoading(false);
  };

  const handleTcrChange = (tcr) => { setSelectedTcr(tcr); localStorage.setItem("pos_tcr", tcr); showToast("TCR: " + tcr); };
  const toggleFastMenu = (pid) => { setFastMenu(prev => { const n = prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]; localStorage.setItem("pos_fast_menu", JSON.stringify(n)); return n; }); };
  const moveFastItem = (pid, dir) => { setFastMenu(prev => { const idx = prev.indexOf(pid); if (idx === -1) return prev; const n = [...prev]; const si = dir === "up" ? idx - 1 : idx + 1; if (si < 0 || si >= n.length) return prev; [n[idx], n[si]] = [n[si], n[idx]]; localStorage.setItem("pos_fast_menu", JSON.stringify(n)); return n; }); };

  // Product CRUD
  const saveProduct = async () => {
    if (!editProd.name || !editProd.price) return showToast("Naziv i cijena su obavezni", "error");
    setSaving(true);
    const isNew = !editProd.id;
    const url = isNew ? "/products" : `/products/update/${editProd.id}`;
    const payload = { ...editProd, track_stock: editProd.track_stock ? 1 : 0 };
    const d = await api(url, { method: "POST", body: JSON.stringify(payload) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Proizvod kreiran!" : "Proizvod ažuriran!"); setEditProd(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Deaktivirati ovaj proizvod?")) return;
    const d = await api(`/products/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Proizvod deaktiviran"); loadAll(); } else showToast(d.message, "error");
  };

  // Category CRUD
  const saveCategory = async () => {
    if (!editCat.name) return showToast("Naziv kategorije je obavezan", "error");
    setSaving(true);
    const isNew = !editCat.id;
    const url = isNew ? "/products/categories" : `/products/updateCategory/${editCat.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editCat) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Kategorija kreirana!" : "Kategorija ažurirana!"); setEditCat(null); loadAll(); }
    else showToast(d.message, "error");
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Obrisati kategoriju?")) return;
    const d = await api(`/products/deleteCategory/${id}`, { method: "POST" });
    if (d.success) { showToast("Kategorija obrisana"); loadAll(); } else showToast(d.message, "error");
  };

  // Partner CRUD
  const savePartner = async () => {
    if (!editPartner.name) return showToast("Naziv partnera je obavezan", "error");
    setSaving(true);
    const isNew = !editPartner.id;
    const url = isNew ? "/partners" : `/partners/update/${editPartner.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editPartner) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Partner kreiran!" : "Partner ažuriran!"); setEditPartner(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };
  const deletePartner = async (id) => {
    if (!window.confirm("Obrisati partnera?")) return;
    const d = await api(`/partners/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Partner obrisan"); loadAll(); } else showToast(d.message, "error");
  };

  const loadPartnerDetail = async (partner) => {
    const d = await api(`/partners/stats/${partner.id}`);
    if (d.success) setPartnerDetail({ ...partner, stats: d.data });
    else setPartnerDetail({ ...partner, stats: null });
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  const inputRow = (label, field, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: S.dark, display: "block", marginBottom: 4 }}>{label}</label>
      <input type={type} step={type === "number" ? "0.01" : undefined} value={editProd?.[field] ?? ""} onChange={e => setEditProd(prev => ({ ...prev, [field]: type === "number" ? e.target.value : e.target.value }))} placeholder={placeholder} style={S.input} />
    </div>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 16px" }}>Podešavanja</h2>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {[{ id: "general", l: "TCR Kasa" }, { id: "kasa", l: "💰 Kasa" }, { id: "products", l: "Proizvodi" }, { id: "categories", l: "Kategorije" }, { id: "partners", l: "Partneri" }, { id: "fastmenu", l: "⚡ Brzi Meni" }, { id: "stolovi", l: "🍽 Stolovi" }, { id: "printer", l: "🖨 Printer" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 18px", border: "1px solid", borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: tab === t.id ? S.dark : S.border, background: tab === t.id ? S.dark : "white", color: tab === t.id ? "white" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {/* ═══ TCR TAB ═══ */}
      {tab === "general" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>TCR Kod (Elektronska Kasa)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tcrs.map(t => (
                <label key={t.tcr_code} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 8, cursor: "pointer", background: selectedTcr === t.tcr_code ? "#f0fdf4" : "#f9fafb", border: `2px solid ${selectedTcr === t.tcr_code ? S.green : S.border}` }}>
                  <input type="radio" name="tcr" checked={selectedTcr === t.tcr_code} onChange={() => handleTcrChange(t.tcr_code)} style={{ accentColor: S.green, width: 18, height: 18 }} />
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontFamily: S.mono, fontSize: 15 }}>{t.tcr_code}</div>{t.location_name && <div style={{ fontSize: 13, color: S.muted }}>{t.location_name}</div>}</div>
                  {selectedTcr === t.tcr_code && <CheckCircle size={20} color={S.green} />}
                </label>
              ))}
              {tcrs.length === 0 && <div style={{ padding: 20, textAlign: "center", color: S.muted }}>Nema TCR kodova. Dodajte ih u web aplikaciji.</div>}
            </div>
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Info</h3>
            <Row label="Operater" value={user.name} /><Row label="Kompanija" value={user.company_name} /><Row label="Email" value={user.email} last />
          </div>
        </div>
      )}

      {/* ═══ PRODUCTS TAB ═══ */}
      {/* ═══ KASA SETTINGS TAB ═══ */}
      {tab === "kasa" && <KasaSettingsTab showToast={showToast} />}

      {/* ═══ PRINTER TAB ═══ */}
      {tab === "printer" && <PrinterSettingsTab showToast={showToast} />}

      {tab === "products" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{products.length} proizvoda</span>
            <button onClick={() => setEditProd({ name: "", price: "", vat_rate: 21, unit: "kom", category_id: "", sku: "", barcode: "", description: "", track_stock: true, stock_quantity: 0 })} style={S.btn(S.green)}><PlusCircle size={15} />Novi proizvod</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "white", borderRadius: 10, overflow: "hidden", border: `1px solid ${S.border}` }}>
            <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "right" }}>Neto cijena</th><th style={{ ...th, textAlign: "center" }}>PDV</th><th style={{ ...th, textAlign: "center" }}>Kategorija</th><th style={{ ...th, textAlign: "center" }}>Stanje</th><th style={{ ...th, textAlign: "center" }}>Brzi</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
            <tbody>{products.map(p => {
              const cat = categories.find(c => String(c.id) === String(p.category_id));
              return (
                <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ ...td, fontWeight: 600 }}>{p.name}<br/><span style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>{p.sku || ""}</span></td>
                  <td style={{ ...td, textAlign: "right" }}>{fmt(p.price)} €</td>
                  <td style={{ ...td, textAlign: "center" }}>{p.vat_rate}%</td>
                  <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{cat ? cat.name : "-"}</td>
                  <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{p.track_stock == 1 ? <span style={{ fontWeight: 600, color: parseFloat(p.stock_quantity || 0) <= parseFloat(p.low_stock_alert || 10) ? S.red : S.green }}>{p.stock_quantity ?? 0}</span> : <span style={{ color: "#d1d5db" }}>—</span>}</td>
                  <td style={{ ...td, textAlign: "center" }}><button onClick={() => toggleFastMenu(p.id)} style={{ ...S.btnSm(fastMenu.includes(p.id) ? "#f0fdf4" : "#f3f4f6", fastMenu.includes(p.id) ? "#15803d" : S.muted), padding: "3px 8px" }}>{fastMenu.includes(p.id) ? <Check size={12} /> : <Plus size={12} />}</button></td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={() => setEditProd({ id: p.id, name: p.name, price: p.price, vat_rate: p.vat_rate, unit: p.unit, category_id: p.category_id || "", sku: p.sku || "", barcode: p.barcode || "", description: p.description || "", stock_quantity: p.stock_quantity || 0, track_stock: p.track_stock == 1 })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                      <button onClick={() => deleteProduct(p.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {/* ═══ CATEGORIES TAB ═══ */}
      {tab === "categories" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{categories.length} kategorija</span>
            <button onClick={() => setEditCat({ name: "", description: "", sort_order: 0 })} style={S.btn(S.green)}><PlusCircle size={15} />Nova kategorija</button>
          </div>
          {categories.map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "white", borderRadius: 8, border: `1px solid ${S.border}`, marginBottom: 6 }}>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>{c.description && <div style={{ fontSize: 13, color: S.muted }}>{c.description}</div>}</div>
              <button onClick={() => setEditCat({ id: c.id, name: c.name, description: c.description || "", sort_order: c.sort_order || 0, is_active: c.is_active })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} />Uredi</button>
              <button onClick={() => deleteCategory(c.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ FAST MENU TAB ═══ */}
      {tab === "fastmenu" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>⚡ Brzi Meni</h3>
            <p style={{ fontSize: 14, color: S.muted, margin: "0 0 16px" }}>Proizvodi koji se pojavljuju na prvom tabu POS ekrana</p>
            {fastMenu.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: S.muted, background: "#f9fafb", borderRadius: 8 }}><Package size={30} /><p style={{ marginTop: 8 }}>Prazan. Dodajte iz tab-a Proizvodi.</p></div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {fastMenu.map((id, idx) => { const p = products.find(pr => pr.id === id); if (!p) return null; const gross = parseFloat(p.price) * 1.21; return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, border: `1px solid ${S.border}` }}>
                  <span style={{ fontSize: 14, color: S.muted, fontWeight: 600, minWidth: 24 }}>#{idx + 1}</span>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div><div style={{ fontSize: 13, color: S.muted }}>{fmt(gross)} €</div></div>
                  <button onClick={() => moveFastItem(id, "up")} disabled={idx === 0} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveFastItem(id, "down")} disabled={idx === fastMenu.length - 1} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === fastMenu.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => toggleFastMenu(id)} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>
                </div>
              ); })}
            </div>}
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Dodaj u brzi meni</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {products.filter(p => !fastMenu.includes(p.id)).map(p => <button key={p.id} onClick={() => toggleFastMenu(p.id)} style={{ ...S.btnSm("#f3f4f6", S.dark), borderRadius: 20 }}><PlusCircle size={13} />{p.name}</button>)}
              {products.filter(p => !fastMenu.includes(p.id)).length === 0 && <span style={{ fontSize: 14, color: S.muted }}>Svi su dodani</span>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ PARTNERS TAB ═══ */}
      {tab === "partners" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{partners.length} partnera</span>
            <button onClick={() => setEditPartner({ name: "", tax_id: "", partner_type: "customer", email: "", phone: "", address: "", city: "", postal_code: "", country: "Montenegro", bank_name: "", bank_account: "", notes: "" })} style={S.btn(S.green)}><PlusCircle size={15} />Novi partner</button>
          </div>

          {partnerDetail && (
            <div style={{ background: "white", border: `1px solid ${S.border}`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div><span style={{ fontWeight: 700, fontSize: 16 }}>{partnerDetail.name}</span>{partnerDetail.tax_id && <span style={{ marginLeft: 8, fontSize: 13, color: S.muted, fontFamily: S.mono }}>PIB: {partnerDetail.tax_id}</span>}</div>
                <button onClick={() => setPartnerDetail(null)} style={S.btnSm("#f3f4f6", S.muted)}><X size={14} /></button>
              </div>
              {partnerDetail.stats ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {[
                    { l: "Ovaj mjesec", c: partnerDetail.stats.month_count, v: partnerDetail.stats.month_total },
                    { l: "Ova godina", c: partnerDetail.stats.year_count, v: partnerDetail.stats.year_total },
                    { l: "Sve vrijeme", c: partnerDetail.stats.all_count, v: partnerDetail.stats.all_total }
                  ].map((s, i) => (
                    <div key={i} style={{ background: "#f9fafb", borderRadius: 8, padding: 12, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, marginBottom: 4 }}>{s.l}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: S.green }}>{fmt(s.v || 0)} €</div>
                      <div style={{ fontSize: 12, color: S.muted }}>{s.c || 0} računa</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ textAlign: "center", color: S.muted, fontSize: 13 }}>Nema podataka</div>}
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "white", borderRadius: 10, overflow: "hidden", border: `1px solid ${S.border}` }}>
            <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={th}>PIB</th><th style={th}>Grad</th><th style={th}>Email</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
            <tbody>{partners.map(p => (
              <tr key={p.id} onClick={() => loadPartnerDetail(p)} style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: partnerDetail?.id === p.id ? "#eff6ff" : "white" }}>
                <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...td, fontFamily: S.mono, fontSize: 13 }}>{p.tax_id || "-"}</td>
                <td style={{ ...td, color: S.muted }}>{p.city || "-"}</td>
                <td style={{ ...td, color: S.muted, fontSize: 13 }}>{p.email || "-"}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button onClick={() => setEditPartner({ id: p.id, name: p.name, tax_id: p.tax_id || "", partner_type: p.partner_type || "customer", email: p.email || "", phone: p.phone || "", address: p.address || "", city: p.city || "", postal_code: p.postal_code || "", country: p.country || "Montenegro", bank_name: p.bank_name || "", bank_account: p.bank_account || "", notes: p.notes || "" })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                    <button onClick={() => deletePartner(p.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* ═══ PRODUCT EDIT MODAL ═══ */}
      {editProd && <Modal onClose={() => setEditProd(null)} title={editProd.id ? "Uredi proizvod" : "Novi proizvod"}>
        {inputRow("Naziv *", "name", "text", "npr. Coca Cola 0.33L")}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Neto cijena (€) *</label><input type="number" step="0.01" value={editProd.price} onChange={e => setEditProd(p => ({ ...p, price: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PDV %</label><input type="number" value={editProd.vat_rate} onChange={e => setEditProd(p => ({ ...p, vat_rate: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Jedinica</label><select value={editProd.unit} onChange={e => setEditProd(p => ({ ...p, unit: e.target.value }))} style={S.input}><option value="kom">kom</option><option value="kg">kg</option><option value="l">l</option><option value="m">m</option><option value="sub">sub</option></select></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Kategorija</label><select value={editProd.category_id} onChange={e => setEditProd(p => ({ ...p, category_id: e.target.value }))} style={S.input}><option value="">-- Bez kategorije --</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={!!editProd.track_stock} onChange={e => setEditProd(p => ({ ...p, track_stock: e.target.checked }))} style={{ width: 18, height: 18, accentColor: S.accent }} />
            <span style={{ fontWeight: 600 }}>Praćenje zaliha</span>
          </label>
          <span style={{ fontSize: 12, color: S.muted }}>Automatski oduzima sa lagera</span>
        </div>
        {editProd.track_stock && <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Količina na stanju</label><input type="number" step="1" value={editProd.stock_quantity ?? 0} onChange={e => setEditProd(p => ({ ...p, stock_quantity: e.target.value }))} style={S.input} /></div>}
        {inputRow("SKU", "sku", "text")}
        {inputRow("Barkod", "barcode", "text")}
        {inputRow("Opis", "description", "text")}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditProd(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveProduct} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editProd.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ CATEGORY EDIT MODAL ═══ */}
      {editCat && <Modal onClose={() => setEditCat(null)} title={editCat.id ? "Uredi kategoriju" : "Nova kategorija"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editCat.name} onChange={e => setEditCat(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Opis</label><input value={editCat.description} onChange={e => setEditCat(p => ({ ...p, description: e.target.value }))} style={S.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Redoslijed</label><input type="number" value={editCat.sort_order} onChange={e => setEditCat(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditCat(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveCategory} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editCat.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ PARTNER EDIT MODAL ═══ */}
      {editPartner && <Modal onClose={() => setEditPartner(null)} title={editPartner.id ? "Uredi partnera" : "Novi partner"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editPartner.name} onChange={e => setEditPartner(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PIB</label><input value={editPartner.tax_id} onChange={e => setEditPartner(p => ({ ...p, tax_id: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editPartner.partner_type} onChange={e => setEditPartner(p => ({ ...p, partner_type: e.target.value }))} style={S.input}><option value="customer">Kupac</option><option value="supplier">Dobavljač</option><option value="both">Oba</option></select></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label><input value={editPartner.email} onChange={e => setEditPartner(p => ({ ...p, email: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Telefon</label><input value={editPartner.phone} onChange={e => setEditPartner(p => ({ ...p, phone: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresa</label><input value={editPartner.address} onChange={e => setEditPartner(p => ({ ...p, address: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Grad</label><input value={editPartner.city} onChange={e => setEditPartner(p => ({ ...p, city: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Banka</label><input value={editPartner.bank_name} onChange={e => setEditPartner(p => ({ ...p, bank_name: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Žiro račun</label><input value={editPartner.bank_account} onChange={e => setEditPartner(p => ({ ...p, bank_account: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Napomena</label><input value={editPartner.notes} onChange={e => setEditPartner(p => ({ ...p, notes: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditPartner(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={savePartner} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editPartner.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ STOLOVI (TABLES) TAB ═══ */}
      {tab === "stolovi" && (
        <div>
          {/* Toggle */}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div><div style={{ fontWeight: 700, fontSize: 16 }}>Koristi stolove</div><div style={{ fontSize: 13, color: S.muted }}>Prikaži Stolovi sektor u navigaciji</div></div>
              <button onClick={async () => {
                const nv = !tablesEnabled;
                if (!nv) {
                  if (!window.confirm("Isključivanjem stolova izgubićete sve informacije o stolovima. Da li ste sigurni?")) return;
                }
                setTablesEnabled(nv);
                onTablesToggle && onTablesToggle(nv);
                await api("/tables/settings", { method: "POST", body: JSON.stringify({ tables_enabled: nv ? 1 : 0 }) });
                showToast(nv ? "Stolovi uključeni" : "Stolovi isključeni");
              }} style={{ width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: tablesEnabled ? S.green : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
                <div style={{ width: 22, height: 22, borderRadius: 11, background: "white", position: "absolute", top: 3, left: tablesEnabled ? 27 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </button>
            </div>
          </div>

          {tablesEnabled && <>
            {/* Quick add */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Brzo dodaj stolove</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { type: "table", label: "Sto", icon: "🟫", seats: 4 },
                  { type: "bar", label: "Bar", icon: "🍸", seats: 2 },
                  { type: "couch", label: "Kauč", icon: "🛋", seats: 3 },
                  { type: "vip", label: "VIP", icon: "⭐", seats: 6 },
                  { type: "outdoor", label: "Terasa", icon: "☀", seats: 4 },
                  { type: "chair", label: "Stolica", icon: "🪑", seats: 0 },
                  { type: "wall", label: "Zid/Pregrada", icon: "🧱", seats: 0 },
                ].map(t => (
                  <button key={t.type} onClick={async () => {
                    const count = rTables.filter(tb => tb.table_type === t.type).length;
                    const name = t.type === "wall" ? `Zid ${count + 1}` : t.type === "chair" ? `Stolica ${count + 1}` : `${t.label} ${count + 1}`;
                    const row = Math.floor(count / 5);
                    const col = count % 5;
                    const isWall = t.type === "wall";
                    const isChair = t.type === "chair";
                    const sizes = { S: { w: 8, h: 7 }, M: { w: 14, h: 12 }, L: { w: 20, h: 16 } };
                    const sz = sizes.M;
                    const d = await api("/tables", { method: "POST", body: JSON.stringify({ name, table_type: t.type, seats: t.seats, x: 5 + col * 18, y: 5 + row * 16, width: isWall ? 22 : isChair ? 5 : sz.w, height: isWall ? 3 : isChair ? 5 : sz.h, shape: t.type === "outdoor" ? "circle" : "rect", color: t.type === "vip" ? "#eab308" : t.type === "bar" ? "#8b5cf6" : t.type === "outdoor" ? "#22c55e" : t.type === "couch" ? "#f97316" : t.type === "wall" ? "#78716c" : t.type === "chair" ? "#a1a1aa" : "#4f46e5" }) });
                    if (d.success) { showToast(`${name} dodat`); loadAll(); }
                  }} style={{ ...S.btn("#f3f4f6", S.dark), fontSize: 13 }}>{t.icon} + {t.label}</button>
                ))}
              </div>
            </div>

            {/* Tables list */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{rTables.length} stolova</span>
              </div>
              {rTables.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: S.muted }}>Nema stolova. Koristite dugmad iznad za brzo dodavanje.</div>
              : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={th}>Tip</th><th style={th}>Mjesta</th><th style={th}>Boja</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
                <tbody>{rTables.map(t => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ ...td, fontWeight: 600 }}>{t.name}</td>
                    <td style={td}>{t.table_type === "table" ? "Sto" : t.table_type === "bar" ? "Bar" : t.table_type === "couch" ? "Kauč" : t.table_type === "vip" ? "VIP" : t.table_type === "wall" ? "Zid" : t.table_type === "chair" ? "Stolica" : "Terasa"}</td>
                    <td style={td}>{t.seats}</td>
                    <td style={td}><div style={{ width: 22, height: 22, borderRadius: 6, background: t.color || "#4f46e5" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => setEditTable({ ...t })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                        <button onClick={async () => { if (!window.confirm("Obrisati " + t.name + "?")) return; const d = await api(`/tables/delete/${t.id}`, { method: "POST" }); if (d.success) { showToast("Obrisano"); loadAll(); } else showToast(d.message, "error"); }} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
            </div>
          </>}
        </div>
      )}

      {/* ═══ TABLE EDIT MODAL ═══ */}
      {editTable && <Modal onClose={() => setEditTable(null)} title="Uredi sto">
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editTable.name} onChange={e => setEditTable(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editTable.table_type} onChange={e => setEditTable(p => ({ ...p, table_type: e.target.value }))} style={S.input}><option value="table">Sto</option><option value="bar">Bar</option><option value="couch">Kauč</option><option value="vip">VIP</option><option value="outdoor">Terasa</option><option value="chair">Stolica</option><option value="wall">Zid/Pregrada</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Mjesta</label><input type="number" value={editTable.seats} onChange={e => setEditTable(p => ({ ...p, seats: parseInt(e.target.value) || 0 }))} style={S.input} /></div>
        </div>
        {/* Size S/M/L */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Veličina</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "S", l: "S — Mali", w: 8, h: 7 }, { id: "M", l: "M — Srednji", w: 14, h: 12 }, { id: "L", l: "L — Veliki", w: 20, h: 16 }].map(sz => {
              const isH = (editTable.width || 14) > (editTable.height || 12);
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isActive = (Math.abs(curW - (isH ? sz.w : sz.h)) < 3 && Math.abs(curH - (isH ? sz.h : sz.w)) < 3) || (Math.abs(curW - sz.w) < 3 && Math.abs(curH - sz.h) < 3);
              return <button key={sz.id} onClick={() => {
                const isHoriz = (editTable.width || 14) >= (editTable.height || 12);
                setEditTable(p => ({ ...p, width: isHoriz ? sz.w : sz.h, height: isHoriz ? sz.h : sz.w }));
              }} style={{ flex: 1, padding: "8px 6px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "center" }}>{sz.id}<div style={{ fontSize: 10, fontWeight: 400, color: S.muted, marginTop: 2 }}>{sz.l}</div></button>;
            })}
          </div>
        </div>
        {/* Direction H/V */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Smjer</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "H", l: "↔ Horizontalno" }, { id: "V", l: "↕ Vertikalno" }].map(dir => {
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isH = curW >= curH;
              const isActive = (dir.id === "H" && isH) || (dir.id === "V" && !isH);
              return <button key={dir.id} onClick={() => {
                const newRot = dir.id === "H" ? 0 : 90;
                setEditTable(p => ({ ...p, width: p.height, height: p.width, rotation: newRot }));
              }} style={{ flex: 1, padding: "9px 8px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{dir.l}</button>;
            })}
          </div>
        </div>
        {/* Facing direction (for couch, chair, bar) */}
        {(editTable.table_type === "couch" || editTable.table_type === "chair" || editTable.table_type === "bar") && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Okretanje (strana)</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ rot: 0, l: "⬆ Gore" }, { rot: 90, l: "➡ Desno" }, { rot: 180, l: "⬇ Dole" }, { rot: 270, l: "⬅ Lijevo" }].map(f => {
                const curRot = parseInt(editTable.rotation) || 0;
                const isActive = curRot === f.rot;
                return <button key={f.rot} onClick={() => {
                  setEditTable(p => ({ ...p, rotation: f.rot }));
                }} style={{ flex: 1, padding: "8px 4px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{f.l}</button>;
              })}
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Oblik</label><select value={editTable.shape} onChange={e => setEditTable(p => ({ ...p, shape: e.target.value }))} style={S.input}><option value="rect">Pravougaonik</option><option value="circle">Krug</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Boja</label><input type="color" value={editTable.color || "#4f46e5"} onChange={e => setEditTable(p => ({ ...p, color: e.target.value }))} style={{ ...S.input, padding: 4, height: 42 }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditTable(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={async () => {
            setSaving(true);
            const d = await api(`/tables/update/${editTable.id}`, { method: "POST", body: JSON.stringify(editTable) });
            setSaving(false);
            if (d.success) { showToast("Sto ažuriran"); setEditTable(null); loadAll(); } else showToast(d.message, "error");
          }} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />Sačuvaj</>}</button>
        </div>
      </Modal>}
    </div>
  );
}

// ═══ STOLOVI VIEW (Restaurant tables) ═══
function StoloviView({ user, showToast, onOrderForTable }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState("layout"); // layout | editor | history
  const [selectedTable, setSelectedTable] = useState(null);
  const [openModal, setOpenModal] = useState(null); // table to open session
  const [guestCount, setGuestCount] = useState(2);
  const [addItemsSession, setAddItemsSession] = useState(null); // session for adding items
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [prodSearch, setProdSearch] = useState("");
  const [sessionCart, setSessionCart] = useState([]);
  const [closeModal, setCloseModal] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [processing, setProcessing] = useState(false);
  const [transferModal, setTransferModal] = useState(null);
  // New: order-based system
  const [sessionOrders, setSessionOrders] = useState([]); // fiscalized orders for selected table
  const [expandedOrder, setExpandedOrder] = useState(null); // expanded order in sidebar
  const [closedHistory, setClosedHistory] = useState([]); // closed tables last 24h
  const [orderDiscount, setOrderDiscount] = useState({ type: "none", value: 0 });
  // Editor drag state
  const [dragging, setDragging] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const gridRef = useRef(null);

  const loadTables = async () => {
    const d = await api("/tables");
    if (d.success) {
      const newTables = d.data?.tables || [];
      setTables(newTables);
      // Update selectedTable with fresh data if one is selected
      if (selectedTable) {
        const updated = newTables.find(t => t.id === selectedTable.id);
        if (updated) setSelectedTable(updated);
        else setSelectedTable(null);
      }
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    const [p, c] = await Promise.all([api("/products"), api("/products/categories")]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
  };

  useEffect(() => { loadTables(); loadProducts(); }, []);
  // Auto refresh only in layout mode (NOT in editor mode — would reset drag positions)
  useEffect(() => {
    if (subView === "editor") return;
    const iv = setInterval(loadTables, 15000);
    return () => clearInterval(iv);
  }, [subView]);

  const openSession = async (table) => {
    setProcessing(true);
    const d = await api("/tables/openSession", { method: "POST", body: JSON.stringify({ table_id: table.id, guest_count: guestCount, tcr_code: getTcr() }) });
    setProcessing(false);
    if (d.success) { showToast(`${table.name} otvoren`); setOpenModal(null); loadTables(); }
    else showToast(d.message || "Greška", "error");
  };

  const addItemsToSession = async (sessionId) => {
    if (sessionCart.length === 0) return;
    setProcessing(true);
    // Check internal day gate
    const orderTcr = getTcr();
    if (orderTcr) {
      try {
        const idCheck = await api("/internalday/settings");
        if (idCheck.success && idCheck.data?.internal_day_enabled) {
          const idStatus = await api(`/internalday/status/${orderTcr}`);
          if (idStatus.success && !idStatus.data?.day) {
            setProcessing(false);
            showToast("Interni dan nije otvoren! Otvorite dan u Blagajni.", "error");
            return;
          }
        }
      } catch(e) {}
    }
    const items = sessionCart.map(c => ({
      product_id: c.product_id || c.id,
      name: c.name,
      quantity: c.quantity,
      unit_price: c.custom_price !== null ? c.custom_price : c.price,
      vat_rate: c.vat_rate || 21,
      unit: c.unit || "kom",
      discount_type: c.discount_type || "none",
      discount_value: c.discount_value || 0,
    }));
    const d = await api(`/tables/orderFiscalize/${sessionId}`, { method: "POST", body: JSON.stringify({
      items,
      location_id: user?.location_id || 1,
      tcr_code: getTcr(),
      discount_type: orderDiscount.type,
      discount_value: orderDiscount.value
    }) });
    setProcessing(false);
    if (d.success) {
      showToast("Narudžba #" + (d.data?.order_number || "") + " fiskalizovana!");
      setSessionCart([]);
      setAddItemsSession(null);
      setOrderDiscount({ type: "none", value: 0 });
      // Auto-print the order receipt (non-blocking)
      const invoiceId = d.data?.invoice?.id;
      if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printReceipt(invoiceId).catch(() => {});
      }
      // Reload tables and orders
      loadTables();
      if (selectedTable?.session) loadSessionOrders(sessionId);
    }
    else showToast(d.message || "Greška pri fiskalizaciji narudžbe", "error");
  };

  const loadSessionOrders = async (sessionId) => {
    const d = await api(`/tables/orderHistory/${sessionId}`);
    if (d.success) setSessionOrders(d.data?.orders || []);
  };

  const loadClosedHistory = async () => {
    const d = await api("/tables/closedHistory");
    if (d.success) setClosedHistory(d.data?.sessions || []);
  };

  const removeItem = async (sessionId, itemId) => {
    const d = await api(`/tables/sessionRemove/${sessionId}`, { method: "POST", body: JSON.stringify({ item_id: itemId }) });
    if (d.success) loadTables();
    else showToast(d.message, "error");
  };

  const closeSession = async (sessionId) => {
    setProcessing(true);
    try {
      // Use new closeFiscalize endpoint — creates summary invoice with SumInv refs
      const d = await api(`/tables/closeFiscalize/${sessionId}`, { method: "POST", body: JSON.stringify({
        payment_method: payMethod,
        ...(payMethod === "split" && { split_cash: parseFloat(splitCash) || 0, split_card: parseFloat(splitCard) || 0 }),
      }) });

      if (!d.success) {
        showToast(d.message || "Greška pri zatvaranju stola", "error");
        setProcessing(false);
        return;
      }

      const inv = d.data?.invoice;
      showToast(`Sto zatvoren! Zbirni račun ${inv?.invoice_number || ""} fiskalizovan!`);

      // Auto-print summary receipt (non-blocking)
      const invoiceId = inv?.id;
      if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printReceipt(invoiceId).catch(() => {});
      }

      setCloseModal(null); setSelectedTable(null); setSessionOrders([]); loadTables();
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setProcessing(false);
  };

  const transferSession = async (sessionId, newTableId) => {
    const d = await api(`/tables/sessionTransfer/${sessionId}`, { method: "POST", body: JSON.stringify({ new_table_id: newTableId }) });
    if (d.success) { showToast("Premješteno!"); setTransferModal(null); setSelectedTable(null); loadTables(); }
    else showToast(d.message, "error");
  };

  const cancelSession = async (sessionId) => {
    if (!window.confirm("Otkazati sesiju? Svi artikli će biti izgubljeni.")) return;
    const d = await api(`/tables/sessionCancel/${sessionId}`, { method: "POST" });
    if (d.success) { showToast("Sesija otkazana"); setSelectedTable(null); loadTables(); }
    else showToast(d.message || "Greška", "error");
  };

  // Editor: drag handlers
  const handleEditorMouseDown = (e, table) => {
    if (subView !== "editor") return;
    e.preventDefault();
    setDragging(table.id);
    const rect = gridRef.current.getBoundingClientRect();
    setDragStart({ mx: e.clientX, my: e.clientY, ox: table.x, oy: table.y });
  };
  const handleEditorMouseMove = (e) => {
    if (!dragging || !dragStart || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
    const dy = ((e.clientY - dragStart.my) / rect.height) * 100;
    setTables(prev => prev.map(t => t.id == dragging ? { ...t, x: Math.max(0, Math.min(88, dragStart.ox + dx)), y: Math.max(0, Math.min(88, dragStart.oy + dy)) } : t));
  };
  const handleEditorMouseUp = () => { setDragging(null); setDragStart(null); };
  const saveLayout = async () => {
    const payload = tables.map(t => ({ id: t.id, x: t.x, y: t.y, width: t.width, height: t.height, rotation: t.rotation || 0 }));
    const d = await api("/tables/bulkUpdate", { method: "POST", body: JSON.stringify({ tables: payload }) });
    if (d.success) showToast("Raspored sačuvan!"); else showToast("Greška", "error");
  };

  // Calc session total
  const sessionTotal = (session) => {
    if (!session?.items) return 0;
    return session.items.filter(i => i.status !== "cancelled").reduce((sum, i) => {
      const net = i.quantity * i.unit_price;
      let disc = 0;
      if (i.discount_type === "percentage") disc = net * (i.discount_value / 100);
      else if (i.discount_type === "fixed") disc = i.discount_value || 0;
      return sum + (net - disc) * (1 + (i.vat_rate || 21) / 100);
    }, 0);
  };

  const getColor = (table) => {
    if (table.session) return table.session.status === "active" ? "#3b82f6" : "#f59e0b";
    return "#22c55e";
  };
  const getStatus = (table) => {
    if (table.session) return "Zauzet";
    return "Slobodan";
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  // Product picker for adding items
  const filteredProducts = products.filter(p => {
    if (activeCategory !== "all" && p.category_id != activeCategory) return false;
    if (prodSearch && !p.name.toLowerCase().includes(prodSearch.toLowerCase())) return false;
    return true;
  });

  const addToSessionCart = (product) => {
    setSessionCart(prev => {
      const exists = prev.find(c => c.id === product.id);
      if (exists) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { ...product, quantity: 1, custom_price: null, discount_type: "none", discount_value: 0 }];
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>

      {/* Grid area */}
      <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
        {tables.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 20px", color: S.muted }}>
            <Coffee size={48} strokeWidth={1.5} />
            <p style={{ marginTop: 16, fontSize: 16 }}>Nema stolova. Dodajte stolove u Podešavanja → Stolovi.</p>
          </div>
        ) : (
          <div ref={gridRef} onMouseMove={handleEditorMouseMove} onMouseUp={handleEditorMouseUp} onMouseLeave={handleEditorMouseUp}
            style={{ position: "relative", width: "100%", paddingBottom: "60%", background: subView === "editor" ? "#faf9f7" : "#f4f2ee", borderRadius: 12, border: `2px ${subView === "editor" ? "dashed" : "solid"} #d6d0c4`, overflow: "hidden" }}>
            {/* Floor texture */}
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              <defs>
                <pattern id="floor" width="40" height="40" patternUnits="userSpaceOnUse">
                  <rect width="40" height="40" fill={subView === "editor" ? "#faf9f7" : "#f4f2ee"} />
                  <path d="M0 20h40M20 0v40" stroke="#e8e2d8" strokeWidth="0.5" opacity="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#floor)" />
            </svg>

            {tables.map(table => {
              const isOccupied = !!table.session;
              const statusColor = getColor(table);
              const isSelected = selectedTable?.id === table.id;
              const total = isOccupied ? sessionTotal(table.session) : 0;
              const tp = table.table_type || "table";
              const w = table.width || 12;
              const h = table.height || 10;
              const rot = parseInt(table.rotation) || 0; // 0, 90, 180, 270

              // Realistic furniture renderer
              const renderFurniture = () => {
                const fw = "100%", fh = "100%";
                // Apply rotation transform to SVG for direction/facing
                const svgStyle = { width: fw, height: fh, position: "absolute", inset: 0, transform: rot ? `rotate(${rot}deg)` : "none" };
                if (tp === "table") {
                  // Round or rect table with chairs around it
                  const seats = parseInt(table.seats) || 4;
                  const isRound = table.shape === "circle";
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Chairs */}
                      {Array.from({ length: Math.min(seats, 8) }).map((_, i) => {
                        const positions = seats <= 2 ? [[30,4],[90,4]] : seats <= 4 ? [[30,2],[90,2],[30,82],[90,82]] : seats <= 6 ? [[20,2],[60,2],[100,2],[20,82],[60,82],[100,82]] : [[20,2],[50,2],[80,2],[108,50],[80,82],[50,82],[20,82],[2,50]];
                        const pos = positions[i] || [60,50];
                        return <rect key={i} x={pos[0]-8} y={pos[1]} width="16" height="14" rx="4" fill={isOccupied ? "#93c5fd" : "#a8b5a0"} opacity="0.6" />;
                      })}
                      {/* Table surface */}
                      {isRound
                        ? <ellipse cx="60" cy="50" rx="32" ry="26" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                        : <rect x="18" y="18" width="84" height="64" rx="8" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                      }
                      {/* Wood grain */}
                      {!isRound && <>
                        <line x1="30" y1="30" x2="30" y2="70" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                        <line x1="60" y1="22" x2="60" y2="78" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                        <line x1="90" y1="30" x2="90" y2="70" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                      </>}
                    </svg>
                  );
                }
                if (tp === "bar") {
                  // Bar counter with stools
                  const seats = parseInt(table.seats) || 3;
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Bar counter */}
                      <rect x="10" y="10" width="100" height="45" rx="6" fill={isOccupied ? "#c7d2fe" : "#8b7355"} stroke={isOccupied ? "#6366f1" : "#6b5740"} strokeWidth="2" />
                      {/* Counter top shine */}
                      <rect x="14" y="14" width="92" height="8" rx="3" fill="white" opacity="0.15" />
                      {/* Foot rail */}
                      <rect x="15" y="48" width="90" height="3" rx="1" fill={isOccupied ? "#a5b4fc" : "#7a6548"} opacity="0.6" />
                      {/* Bar stools */}
                      {Array.from({ length: Math.min(seats, 6) }).map((_, i) => {
                        const spacing = 100 / (seats + 1);
                        const cx = 10 + spacing * (i + 1);
                        return <g key={i}>
                          <circle cx={cx} cy="75" r="10" fill={isOccupied ? "#93c5fd" : "#a0937c"} stroke={isOccupied ? "#60a5fa" : "#8a7d68"} strokeWidth="1.5" />
                          <line x1={cx} y1="65" x2={cx} y2="58" stroke={isOccupied ? "#93c5fd" : "#a0937c"} strokeWidth="3" strokeLinecap="round" />
                        </g>;
                      })}
                    </svg>
                  );
                }
                if (tp === "couch") {
                  // Booth / couch seating
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Back cushion */}
                      <rect x="5" y="5" width="110" height="25" rx="10" fill={isOccupied ? "#93c5fd" : "#a67c52"} opacity="0.8" />
                      {/* Seat cushions */}
                      <rect x="8" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      <rect x="44" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      <rect x="80" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      {/* Armrests */}
                      <rect x="2" y="8" width="10" height="52" rx="5" fill={isOccupied ? "#60a5fa" : "#8b6440"} opacity="0.7" />
                      <rect x="108" y="8" width="10" height="52" rx="5" fill={isOccupied ? "#60a5fa" : "#8b6440"} opacity="0.7" />
                      {/* Coffee table in front */}
                      <rect x="30" y="68" width="60" height="26" rx="5" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="1.5" />
                    </svg>
                  );
                }
                if (tp === "vip") {
                  // VIP booth - u-shaped seating
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* U-shaped sofa */}
                      <path d="M10,10 L10,85 Q10,95 20,95 L100,95 Q110,95 110,85 L110,10" fill="none" stroke={isOccupied ? "#eab308" : "#7c3aed"} strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                      <path d="M10,10 L10,85 Q10,95 20,95 L100,95 Q110,95 110,85 L110,10" fill="none" stroke={isOccupied ? "#fbbf24" : "#8b5cf6"} strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                      {/* Center table */}
                      <rect x="30" y="25" width="60" height="50" rx="8" fill={isOccupied ? "#fef3c7" : "#ede9fe"} stroke={isOccupied ? "#f59e0b" : "#a78bfa"} strokeWidth="2" />
                      {/* Star / VIP indicator */}
                      <text x="60" y="55" textAnchor="middle" fontSize="18" fill={isOccupied ? "#d97706" : "#7c3aed"} opacity="0.4">★</text>
                    </svg>
                  );
                }
                if (tp === "outdoor") {
                  // Outdoor table with umbrella
                  const seats = parseInt(table.seats) || 4;
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Umbrella shadow */}
                      <ellipse cx="60" cy="50" rx="48" ry="40" fill="#22c55e" opacity="0.08" />
                      {/* Outdoor chairs */}
                      {Array.from({ length: Math.min(seats, 6) }).map((_, i) => {
                        const positions = seats <= 2 ? [[30,8],[90,8]] : seats <= 4 ? [[30,6],[90,6],[30,76],[90,76]] : [[20,6],[60,6],[100,6],[20,76],[60,76],[100,76]];
                        const pos = positions[i] || [60,50];
                        return <rect key={i} x={pos[0]-7} y={pos[1]} width="14" height="16" rx="3" fill={isOccupied ? "#86efac" : "#4ade80"} opacity="0.5" stroke={isOccupied ? "#22c55e" : "#16a34a"} strokeWidth="1" />;
                      })}
                      {/* Table */}
                      <circle cx="60" cy="48" r="22" fill={isOccupied ? "#bbf7d0" : "#dcfce7"} stroke={isOccupied ? "#22c55e" : "#86efac"} strokeWidth="2" />
                      {/* Umbrella pole */}
                      <line x1="60" y1="26" x2="60" y2="70" stroke="#854d0e" strokeWidth="2" opacity="0.3" />
                      {/* Umbrella top */}
                      <path d="M35,28 Q60,10 85,28" fill="none" stroke="#dc2626" strokeWidth="3" opacity="0.4" />
                    </svg>
                  );
                }
                if (tp === "chair") {
                  // Decorative chair - no sessions
                  return (
                    <svg viewBox="0 0 60 60" style={svgStyle}>
                      {/* Seat */}
                      <rect x="10" y="20" width="40" height="30" rx="6" fill="#d4d4d8" stroke="#a1a1aa" strokeWidth="1.5" />
                      {/* Back rest */}
                      <rect x="12" y="5" width="36" height="18" rx="6" fill="#a1a1aa" stroke="#78716c" strokeWidth="1.5" />
                      {/* Legs */}
                      <line x1="16" y1="50" x2="14" y2="58" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" />
                      <line x1="44" y1="50" x2="46" y2="58" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  );
                }
                if (tp === "wall") {
                  // Wall / divider
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      <rect x="2" y="35" width="116" height="30" rx="2" fill="#78716c" stroke="#57534e" strokeWidth="2" />
                      <line x1="20" y1="38" x2="20" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="40" y1="38" x2="40" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="60" y1="38" x2="60" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="80" y1="38" x2="80" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="100" y1="38" x2="100" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                    </svg>
                  );
                }
                // Default fallback
                return (
                  <svg viewBox="0 0 120 100" style={svgStyle}>
                    <rect x="15" y="15" width="90" height="70" rx="8" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                  </svg>
                );
              };

              const isWall = tp === "wall";
              const isChair = tp === "chair";
              const isDecor = isWall || isChair;
              return (
                <div key={table.id}
                  onMouseDown={subView === "editor" ? (e) => handleEditorMouseDown(e, table) : undefined}
                  onClick={() => {
                    if (subView === "editor" || isDecor) return;
                    if (isOccupied) { setSelectedTable(table); if (table.session?.id) loadSessionOrders(table.session.id); }
                    else {
                      // Open session immediately and go to add-items
                      (async () => {
                        setProcessing(true);
                        const d = await api("/tables/openSession", { method: "POST", body: JSON.stringify({ table_id: table.id, guest_count: 1, tcr_code: getTcr() }) });
                        setProcessing(false);
                        if (d.success) {
                          showToast(`${table.name} otvoren`);
                          // Reload and open add-items for this table
                          const td = await api("/tables");
                          if (td.success) {
                            const newTables = td.data?.tables || [];
                            setTables(newTables);
                            const updated = newTables.find(t => t.id === table.id);
                            if (updated) {
                              setSelectedTable(updated);
                              if (updated.session) { setAddItemsSession(updated.session); loadSessionOrders(updated.session.id); }
                            }
                          }
                        } else showToast(d.message || "Greška", "error");
                      })();
                    }
                  }}
                  style={{
                    position: "absolute",
                    left: `${table.x}%`, top: `${table.y}%`,
                    width: `${w}%`, height: `${h}%`,
                    minWidth: isDecor ? 20 : 80, minHeight: isDecor ? 12 : 65,
                    cursor: subView === "editor" ? "grab" : isDecor ? "default" : "pointer",
                    transition: dragging == table.id ? "none" : "transform 0.15s, box-shadow 0.2s",
                    transform: isSelected && !isDecor ? "scale(1.03)" : "scale(1)",
                    filter: isSelected && !isDecor ? "drop-shadow(0 4px 12px rgba(79,70,229,0.25))" : dragging == table.id ? "drop-shadow(0 6px 20px rgba(0,0,0,0.2))" : "none",
                    zIndex: dragging == table.id ? 100 : isSelected ? 50 : isDecor ? 0 : 1,
                    userSelect: "none",
                  }}>
                  {renderFurniture()}
                  {/* Label overlay */}
                  {!isWall && tp !== "chair" && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <div style={{ background: "rgba(255,255,255,0.88)", backdropFilter: "blur(2px)", borderRadius: 6, padding: "3px 7px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: S.dark, lineHeight: 1.2 }}>{table.name}</div>
                        {subView !== "editor" && <>
                          <div style={{ fontSize: 10, fontWeight: 700, color: statusColor, lineHeight: 1.2 }}>{isOccupied ? "● Zauzet" : "○ Slobodan"}</div>
                          {!isOccupied && <div style={{ fontSize: 9, color: S.muted }}>{table.seats} mj.</div>}
                        </>}
                        {subView === "editor" && <div style={{ fontSize: 9, color: S.muted }}>{table.seats} mj.</div>}
                      </div>
                    </div>
                  )}
                  {/* Editor label for walls */}
                  {isWall && subView === "editor" && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <span style={{ fontSize: 9, color: "white", fontWeight: 600, textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>{table.name}</span>
                    </div>
                  )}
                  {/* Selection ring */}
                  {isSelected && !isDecor && <div style={{ position: "absolute", inset: -4, border: `2px solid ${S.accent}`, borderRadius: 14, pointerEvents: "none" }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom toolbar */}
      <div style={{ padding: "10px 20px", borderTop: `1px solid ${S.border}`, background: "white", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setSubView("layout")} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "layout" ? S.accent : S.border, background: subView === "layout" ? S.accent : "white", color: subView === "layout" ? "white" : S.muted }}>
            <Coffee size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Raspored
          </button>
          <button onClick={() => { setSubView("history"); loadClosedHistory(); }} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "history" ? S.accent : S.border, background: subView === "history" ? S.accent : "white", color: subView === "history" ? "white" : S.muted }}>
            <Clock size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Istorija
          </button>
          <button onClick={() => setSubView("editor")} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "editor" ? S.accent : S.border, background: subView === "editor" ? S.accent : "white", color: subView === "editor" ? "white" : S.muted }}>
            <Move size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Uredi raspored
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 13, color: S.muted }}>{tables.filter(t => t.session && t.table_type !== "wall" && t.table_type !== "chair").length}/{tables.filter(t => t.table_type !== "wall" && t.table_type !== "chair").length} zauzeto</div>
          <button onClick={loadTables} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
          {subView === "editor" && <button onClick={saveLayout} style={S.btn(S.green)}><Save size={14} />Sacuvaj raspored</button>}
        </div>
      </div>

      {/* History view */}
      {subView === "history" && (
        <div style={{ position: "absolute", inset: 0, background: "white", zIndex: 800, overflow: "auto", padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Zatvoreni stolovi (24h)</h3>
            <button onClick={() => setSubView("layout")} style={S.btnSm("#f3f4f6", S.muted)}><X size={14} />Zatvori</button>
          </div>
          {closedHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: S.muted }}><Clock size={30} strokeWidth={1.5} /><p style={{ marginTop: 8 }}>Nema zatvorenih stolova u posljednjih 24h</p></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {closedHistory.map(s => (
                <div key={s.id} style={{ background: "#f9fafb", borderRadius: 10, padding: 14, border: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: S.green + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><Check size={20} color={S.green} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{s.table_name}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>Otvoreno: {fmtDateTime(s.opened_at)} • Zatvoreno: {fmtDateTime(s.closed_at)}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>{s.operator_name || ""} • {s.total_orders || 0} narudžbi</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: S.green }}>{fmt(s.summary_total || s.total_amount || 0)} €</div>
                    {s.summary_invoice_number && <div style={{ fontSize: 11, color: S.muted }}>{s.summary_invoice_number}</div>}
                  </div>
                  {s.summary_invoice_id && <button onClick={async () => { await printReceipt(s.summary_invoice_id); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Session detail panel (right side overlay when table selected) */}
      {selectedTable && selectedTable.session && (
        <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 400, background: "white", boxShadow: "-4px 0 20px rgba(0,0,0,0.1)", zIndex: 999, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div><div style={{ fontWeight: 700, fontSize: 17 }}>{selectedTable.name}</div><div style={{ fontSize: 13, color: S.muted }}>Gostiju: {selectedTable.session.guest_count} • {fmtDateTime(selectedTable.session.opened_at)}</div></div>
            <button onClick={() => { setSelectedTable(null); setSessionOrders([]); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color={S.muted} /></button>
          </div>

          {/* Orders list */}
          <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
            {sessionOrders.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: S.muted }}><Package size={30} strokeWidth={1.5} /><p style={{ marginTop: 8 }}>Nema narudžbi</p><p style={{ fontSize: 12 }}>Dodajte prvu narudžbu</p></div>
            ) : sessionOrders.filter(o => !o.storno_of_invoice_id).map((order, idx) => {
              const isStorno = order.is_storno == 1 || order.stornoed_by_invoice_id || order.status === "storno";
              const items = order.items_json ? (typeof order.items_json === "string" ? JSON.parse(order.items_json) : order.items_json) : [];
              return (
                <div key={order.id} onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)} style={{ background: isStorno ? "#fef2f2" : "#f9fafb", borderRadius: 10, padding: 12, marginBottom: 10, border: `1px solid ${isStorno ? "#fecaca" : S.border}`, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Narudžba #{order.order_number || idx + 1}</span>
                      {isStorno && <span style={{ marginLeft: 6, fontSize: 11, color: S.red, fontWeight: 600 }}>STORNO</span>}
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 15, color: isStorno ? S.red : S.green, textDecoration: isStorno ? "line-through" : "none" }}>{fmt(order.total_amount)} €</span>
                  </div>
                  <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>{fmtDateTime(order.invoice_date)} • {order.status === "fiscalized" ? "✓ Fiskalizovano" : order.status}</div>
                  {expandedOrder === order.id && <>
                    {items.length > 0 && <div style={{ margin: "8px 0", padding: "8px 0", borderTop: `1px solid ${isStorno ? "#fecaca" : "#e5e7eb"}` }}>
                      {items.map((it, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
                        <span style={{ color: isStorno ? S.red : S.dark }}>{it.quantity}x {it.name}</span>
                        <span style={{ fontWeight: 600, color: isStorno ? S.red : S.dark }}>{fmt((it.quantity * it.unit_price * (1 + (it.vat_rate || 21) / 100)).toFixed(2))} €</span>
                      </div>)}
                    </div>}
                    {order.fic && <div style={{ fontSize: 10, color: S.muted, marginBottom: 6, wordBreak: "break-all" }}>FIC: {order.fic}</div>}
                    <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button onClick={async () => { await printReceipt(order.id); showToast("Štampano!"); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={11} /></button>
                      <button onClick={() => openPdf(`/invoices/pdf/${order.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={11} /></button>
                      {!isStorno && order.status === "fiscalized" && <button onClick={async () => { if (!window.confirm("Stornirati narudžbu #" + (order.order_number || idx+1) + "?")) return; const d = await api(`/invoices/storno/${order.id}`, { method: "POST", body: JSON.stringify({ storno_reason: "STORNO_OP", fiscalize_now: true }) }); if (d.success) { showToast("Narudžba stornirana!"); loadSessionOrders(selectedTable.session.id); loadTables(); } else showToast(d.message, "error"); }} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={11} />Storno</button>}
                    </div>
                  </>}
                </div>
              );
            })}
          </div>

          {/* Total + actions */}
          <div style={{ borderTop: `2px solid ${S.border}`, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: S.muted }}>Narudžbe: {sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1).length}</span>
              <span style={{ fontSize: 13, color: S.muted }}>{fmtDateTime(selectedTable.session.opened_at)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>Ukupno</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: S.green }}>{fmt(sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1).reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))} €</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => { setAddItemsSession(selectedTable.session); setSessionCart([]); setOrderDiscount({ type: "none", value: 0 }); }} style={{ ...S.btn(S.accent), flex: "1 1 45%", justifyContent: "center", fontSize: 13 }}><Plus size={14} />Nova narudžba</button>
              {sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1).length > 0
                ? <button onClick={() => { setCloseModal(selectedTable.session); setPayMethod("cash"); }} style={{ ...S.btn(S.green), flex: "1 1 45%", justifyContent: "center", fontSize: 13 }}><Zap size={14} />Zatvori & Naplati</button>
                : null}
              <button onClick={() => setTransferModal(selectedTable.session)} style={{ ...S.btnSm("#f3f4f6", S.muted), flex: "1 1 30%", justifyContent: "center" }}><Move size={12} />Premjesti</button>
              <button onClick={() => cancelSession(selectedTable.session.id)} style={{ ...S.btnSm("#fef2f2", S.red), flex: "1 1 30%", justifyContent: "center" }}><XCircle size={12} />Otkaži</button>
            </div>
          </div>
        </div>
      )}

      {/* Open table modal */}
      {openModal && <Modal onClose={() => setOpenModal(null)} title={`Otvori ${openModal.name}`}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#dcfce7", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}><Coffee size={28} color={S.green} /></div>
          <div style={{ fontSize: 15, color: S.muted }}>{openModal.seats} mjesta • {openModal.table_type}</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Broj gostiju</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[1,2,3,4,5,6,7,8].map(n => (
              <button key={n} onClick={() => setGuestCount(n)} style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid", borderColor: guestCount === n ? S.accent : S.border, background: guestCount === n ? S.accent : "white", color: guestCount === n ? "white" : S.dark, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{n}</button>
            ))}
          </div>
        </div>
        <button onClick={() => openSession(openModal)} disabled={processing} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: "12px" }}>
          {processing ? <Loader2 size={16} className="spin" /> : <Check size={16} />}Otvori sto
        </button>
      </Modal>}

      {/* Add items modal (product picker) */}
      {addItemsSession && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", justifyContent: "center" }} onClick={() => {
        // If session has no items and user dismisses without adding, cancel the session
        if (addItemsSession && sessionCart.length === 0) {
          const sess = addItemsSession;
          const tbl = tables.find(t => t.session?.id === sess.id);
          const hasItems = tbl?.session?.items?.filter(i => i.status !== "cancelled")?.length > 0;
          if (!hasItems) {
            api(`/tables/sessionCancel/${sess.id}`, { method: "POST" }).then(() => loadTables());
          }
        }
        setAddItemsSession(null);
      }}>
        <div style={{ background: "white", width: "75%", maxWidth: 1000, margin: "30px auto", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 60px)" }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 17 }}>Dodaj stavke</span>
            <button onClick={() => setAddItemsSession(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color={S.muted} /></button>
          </div>
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Products */}
            <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
              <input placeholder="Pretraži..." value={prodSearch} onChange={e => setProdSearch(e.target.value)} style={{ ...S.input, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
                {categories.map(c => <CatTab key={c.id} active={activeCategory == c.id} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 10 }}>
                {filteredProducts.map(p => (
                  <button key={p.id} onClick={() => addToSessionCart(p)} style={{ padding: 12, background: sessionCart.find(c => c.id === p.id) ? "#eef2ff" : "white", border: `1px solid ${sessionCart.find(c => c.id === p.id) ? S.accent : S.border}`, borderRadius: 8, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: S.green, marginTop: 4 }}>{fmt((p.price * (1 + (p.vat_rate || 21) / 100)))} €</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Cart */}
            <div style={{ width: 240, borderLeft: `1px solid ${S.border}`, padding: 12, overflow: "auto", display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Korpa ({sessionCart.reduce((s,c) => s + c.quantity, 0)})</div>
              <div style={{ flex: 1, overflow: "auto" }}>
              {sessionCart.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                  <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{c.name}</div>
                  <button onClick={() => setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x))} style={{ width: 22, height: 22, border: `1px solid ${S.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={10} /></button>
                  <span style={{ fontWeight: 700, minWidth: 18, textAlign: "center" }}>{c.quantity}</span>
                  <button onClick={() => setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, quantity: x.quantity + 1 } : x))} style={{ width: 22, height: 22, border: `1px solid ${S.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={10} /></button>
                  <button onClick={() => setSessionCart(prev => prev.filter(x => x.id !== c.id))} style={{ padding: 0, background: "none", border: "none", cursor: "pointer" }}><X size={12} color="#d1d5db" /></button>
                </div>
              ))}
              </div>
              {sessionCart.length > 0 && <button onClick={() => addItemsToSession(addItemsSession.id)} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", marginTop: 10, padding: 10 }}><Check size={14} />Potvrdi ({sessionCart.length})</button>}
            </div>
          </div>
        </div>
      </div>}

      {/* Close session / payment modal */}
      {closeModal && <Modal onClose={() => setCloseModal(null)} title="Zatvori račun">
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: S.muted, marginBottom: 6 }}>Ukupno za naplatu:</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: S.green }}>{fmt(sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1).reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))} €</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Način plaćanja</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "cash", l: "Gotovina", icon: Banknote }, { id: "card", l: "Kartica", icon: CreditCard }, { id: "split", l: "Split", icon: Wallet }].map(m => (
              <button key={m.id} onClick={() => setPayMethod(m.id)} style={{ flex: 1, padding: 10, border: "1px solid", borderColor: payMethod === m.id ? S.green : S.border, background: payMethod === m.id ? "#f0fdf4" : "white", color: payMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><m.icon size={14} />{m.l}</button>
            ))}
          </div>
        </div>
        {payMethod === "split" && (
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Gotovina (€)</label><input type="number" step="0.01" value={splitCash} onChange={e => setSplitCash(e.target.value)} style={S.input} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Kartica (€)</label><input type="number" step="0.01" value={splitCard} onChange={e => setSplitCard(e.target.value)} style={S.input} /></div>
          </div>
        )}
        <button onClick={() => closeSession(closeModal.id)} disabled={processing} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>
          {processing ? <Loader2 size={17} className="spin" /> : <Zap size={17} />}Fiskalizuj i zatvori
        </button>
      </Modal>}

      {/* Transfer modal */}
      {transferModal && <Modal onClose={() => setTransferModal(null)} title="Premjesti na drugi sto">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {tables.filter(t => !t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").map(t => (
            <button key={t.id} onClick={() => transferSession(transferModal.id, t.id)} style={{ padding: 14, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: S.muted }}>{t.seats} mj.</div>
            </button>
          ))}
          {tables.filter(t => !t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").length === 0 && (
            <div style={{ gridColumn: "span 3", textAlign: "center", padding: 20, color: S.muted }}>Nema slobodnih stolova</div>
          )}
        </div>
      </Modal>}
    </div>
  );
}


// ═══ HOTEL VIEW ═══

// ═══ HOTEL VIEW ═══
function HotelView({ user, showToast }) {
  const [tab, setTab] = useState("rooms"); // rooms | guests | tax | settings
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxConfig, setTaxConfig] = useState(null);

  // Rooms state
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomFilter, setRoomFilter] = useState("");

  // Guest state
  const [guestModal, setGuestModal] = useState(false);
  const [guestFilter, setGuestFilter] = useState("checked_in");
  const [guestSearch, setGuestSearch] = useState("");
  const [processing, setProcessing] = useState(false);
  const [expandedGuest, setExpandedGuest] = useState(null);
  const [passportLoading, setPassportLoading] = useState(false);

  // Settings state
  const [roomForm, setRoomForm] = useState({ room_number: "", room_type: "double", floor: "", capacity: 2, price_per_night: "" });
  const [editingRoom, setEditingRoom] = useState(null);
  const [taxForm, setTaxForm] = useState({ adult: "", child: "", infant: "" });

  // Tax tab state
  const [taxGuests, setTaxGuests] = useState([]);
  const [taxPeriod, setTaxPeriod] = useState("today");
  const [taxDateFrom, setTaxDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [taxDateTo, setTaxDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [taxStatusFilter, setTaxStatusFilter] = useState("");
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxPage, setTaxPage] = useState(1);

  // Guest check-in form
  const [gf, setGf] = useState({
    first_name: "", last_name: "", date_of_birth: "", gender: "M", nationality: "",
    document_type: "PASSPORT", document_number: "", checkin_date: new Date().toISOString().split("T")[0],
    checkout_date: new Date(Date.now() + 86400000).toISOString().split("T")[0],
    room_id: "", tourist_tax_category: "adult", notes: ""
  });
  const updateGf = (f) => setGf(p => ({ ...p, ...f }));

  // ── Data loading ──
  const loadRooms = useCallback(async (filter) => {
    const f = filter !== undefined ? filter : roomFilter;
    const r = await api("/hotel/rooms?location_id=" + (user.location_id || 1) + (f ? "&status=" + f : ""));
    if (r.success) setRooms(r.data?.rooms || []);
  }, [user.location_id, roomFilter]);

  const loadGuests = useCallback(async () => {
    let qs = "?location_id=" + (user.location_id || 1);
    if (guestFilter) qs += "&status=" + guestFilter;
    if (guestSearch) qs += "&search=" + encodeURIComponent(guestSearch);
    const g = await api("/hotel/guests" + qs);
    if (g.success) setGuests(g.data?.guests || []);
  }, [user.location_id, guestFilter, guestSearch]);

  const loadStats = async () => {
    const s = await api("/hotel/stats");
    if (s.success) setStats(s.data);
  };

  const loadTaxConfig = async () => {
    const t = await api("/hotel/tax");
    if (t.success) {
      const cfg = t.data?.config || t.data || {};
      setTaxConfig(cfg);
      setTaxForm({
        adult: cfg.tax_rate_adult ?? "1.50",
        child: cfg.tax_rate_child_7_18 ?? "0.75",
        infant: cfg.tax_rate_child_under_7 ?? "0.00"
      });
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadRooms(""), loadGuests(), loadStats(), loadTaxConfig()]);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (!loading) loadGuests(); }, [guestFilter]);

  // Auto refresh rooms every 30s
  useEffect(() => {
    if (tab !== "rooms") return;
    const iv = setInterval(() => { loadRooms(); loadStats(); }, 30000);
    return () => clearInterval(iv);
  }, [tab, roomFilter]);

  // ── Tax tab data loading ──
  const loadTaxGuests = async () => {
    setTaxLoading(true);
    let qs = "?location_id=" + (user.location_id || 1);
    if (taxStatusFilter) qs += "&status=" + taxStatusFilter;
    if (taxPeriod === "today") {
      const today = new Date().toISOString().slice(0, 10);
      qs += "&checkin_from=" + today + "&checkin_to=" + today;
    } else if (taxPeriod === "7days") {
      const to = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      qs += "&checkin_from=" + from + "&checkin_to=" + to;
    } else if (taxPeriod === "30days") {
      const to = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      qs += "&checkin_from=" + from + "&checkin_to=" + to;
    } else if (taxPeriod === "custom") {
      if (taxDateFrom) qs += "&checkin_from=" + taxDateFrom;
      if (taxDateTo) qs += "&checkin_to=" + taxDateTo;
    }
    const g = await api("/hotel/guests" + qs);
    if (g.success) setTaxGuests(g.data?.guests || []);
    setTaxLoading(false);
  };

  useEffect(() => { if (tab === "tax") loadTaxGuests(); }, [tab, taxPeriod, taxStatusFilter, taxDateFrom, taxDateTo]);

  // ── Room actions ──
  const saveRoom = async () => {
    setProcessing(true);
    const body = { ...roomForm, location_id: user.location_id || 1 };
    let d;
    if (editingRoom) {
      d = await api(`/hotel/rooms/${editingRoom}`, { method: "PUT", body: JSON.stringify(body) });
    } else {
      d = await api("/hotel/rooms", { method: "POST", body: JSON.stringify(body) });
    }
    setProcessing(false);
    if (d.success) {
      showToast(editingRoom ? "Soba ažurirana!" : "Soba kreirana!");
      setRoomForm({ room_number: "", room_type: "double", floor: "", capacity: 2, price_per_night: "" });
      setEditingRoom(null);
      loadRooms(); loadStats();
    } else showToast(d.message || "Greška", "error");
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Obrisati sobu?")) return;
    const d = await api(`/hotel/rooms/${id}`, { method: "DELETE" });
    if (d.success) { showToast("Soba obrisana"); setSelectedRoom(null); loadRooms(); loadStats(); }
    else showToast(d.message || "Greška", "error");
  };

  const changeRoomStatus = async (id, status) => {
    const d = await api(`/hotel/rooms/${id}`, { method: "PUT", body: JSON.stringify({ status, _status_only: true }) });
    if (d.success) { loadRooms(); loadStats(); }
    else showToast(d.message || "Greška", "error");
  };

  // ── Guest actions ──
  const checkInGuest = async () => {
    if (!gf.first_name || !gf.last_name || !gf.nationality || !gf.document_number || !gf.checkin_date || !gf.checkout_date) {
      showToast("Popunite obavezna polja", "error"); return;
    }
    setProcessing(true);
    const body = {
      ...gf,
      room_id: gf.room_id || null,
      location_id: user.location_id || 1,
      operator_id: user.id || 1,
    };
    const d = await api("/hotel/guests", { method: "POST", body: JSON.stringify(body) });
    setProcessing(false);
    if (d.success) {
      showToast(`Gost prijavljen! Boravišna taksa: €${d.data?.tourist_tax_total || 0}`);
      setGuestModal(false);
      setGf({
        first_name: "", last_name: "", date_of_birth: "", gender: "M", nationality: "",
        document_type: "PASSPORT", document_number: "", checkin_date: new Date().toISOString().split("T")[0],
        checkout_date: new Date(Date.now() + 86400000).toISOString().split("T")[0],
        room_id: "", tourist_tax_category: "adult", notes: ""
      });
      loadGuests(); loadRooms(); loadStats();
    } else showToast(d.message || "Greška pri prijavi", "error");
  };

  const checkoutGuest = async (id) => {
    if (!window.confirm("Odjaviti gosta?")) return;
    setProcessing(true);
    const d = await api(`/hotel/guests/${id}`, { method: "PUT", body: JSON.stringify({ action: "checkout" }) });
    setProcessing(false);
    if (d.success) { showToast("Gost odjavljen!"); loadGuests(); loadRooms(); loadStats(); }
    else showToast(d.message || "Greška", "error");
  };

  const submitRB90 = async (id) => {
    setProcessing(true);
    const d = await api("/hotel/rb90", { method: "POST", body: JSON.stringify({ guest_id: id }) });
    setProcessing(false);
    if (d.success) {
      showToast(`MUP (RB-90): ${d.data?.guest_name || ""} — ${d.data?.rb90_status || "registered"}`);
      loadGuests();
    } else showToast(d.message || "Greška pri RB-90 prijavi", "error");
  };

  const deregisterRB90 = async (id) => {
    setProcessing(true);
    const d = await api(`/hotel/rb90/${id}`, { method: "DELETE" });
    setProcessing(false);
    if (d.success) { showToast("MUP odjava uspješna"); loadGuests(); }
    else showToast(d.message || "Greška", "error");
  };

  const sendToPMS = () => {
    showToast("Hotel PMS nije povezan. Konfiguriše se preko API privatnog ključa u Podešavanjima.", "error");
  };

  const markTaxPaid = async (guestId) => {
    const d = await api(`/hotel/guests/${guestId}`, { method: "PUT", body: JSON.stringify({ tourist_tax_paid: 1 }) });
    if (d.success) { showToast("Taksa označena kao plaćena"); loadTaxGuests(); loadGuests(); }
    else showToast(d.message || "Greška", "error");
  };

  // ── Passport OCR via Claude API (works in artifact environment) ──
  const handlePassportUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPassportLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        const mediaType = file.type || "image/jpeg";
        try {
          const resp = await fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              model: "claude-sonnet-4-20250514",
              max_tokens: 1000,
              messages: [{
                role: "user",
                content: [
                  { type: "image", source: { type: "base64", media_type: mediaType, data: base64 } },
                  { type: "text", text: `Extract passport/ID data from this image. Return ONLY a JSON object with these fields (use empty string if not found):
{"first_name":"","last_name":"","date_of_birth":"YYYY-MM-DD","gender":"M or F","nationality":"3-letter country code like MNE, SRB, ALB, USA etc","document_type":"PASSPORT or ID","document_number":""}
Return ONLY the JSON, no markdown, no explanation.` }
                ]
              }]
            })
          });
          const data = await resp.json();
          const text = data.content?.find(c => c.type === "text")?.text || "";
          const jsonStr = text.replace(/```json|```/g, "").trim();
          const parsed = JSON.parse(jsonStr);
          updateGf({
            first_name: parsed.first_name || gf.first_name,
            last_name: parsed.last_name || gf.last_name,
            date_of_birth: parsed.date_of_birth || gf.date_of_birth,
            gender: parsed.gender || gf.gender,
            nationality: parsed.nationality || gf.nationality,
            document_type: parsed.document_type || gf.document_type,
            document_number: parsed.document_number || gf.document_number,
          });
          showToast("Podaci sa dokumenta učitani!");
        } catch (err) {
          console.error("OCR error:", err);
          showToast("Nije moguće pročitati dokument. Unesite ručno.", "error");
        }
        setPassportLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setPassportLoading(false);
      showToast("Greška pri učitavanju slike", "error");
    }
  };

  // ── Tax config save ──
  const saveTaxConfig = async () => {
    setProcessing(true);
    const rates = [
      { category: "adult", rate: parseFloat(taxForm.adult) || 0 },
      { category: "child", rate: parseFloat(taxForm.child) || 0 },
      { category: "infant", rate: parseFloat(taxForm.infant) || 0 },
    ];
    let ok = true;
    for (const r of rates) {
      const d = await api("/hotel/tax", { method: "POST", body: JSON.stringify(r) });
      if (!d.success) ok = false;
    }
    setProcessing(false);
    if (ok) { showToast("Tarife boravišne takse sačuvane!"); loadAll(); }
    else showToast("Greška pri čuvanju tarifa", "error");
  };

  // ── Helpers ──
  const getRoomColor = (room) => {
    if (room.status === "occupied") return "#3b82f6";
    if (room.status === "maintenance") return "#f59e0b";
    if (room.status === "inactive") return "#6b7280";
    return "#22c55e";
  };
  const getRoomStatusLabel = (s) => ({ available: "Slobodna", occupied: "Zauzeta", maintenance: "Održavanje", inactive: "Neaktivna" }[s] || s);
  const getRoomTypeLabel = (t) => ({ single: "Jednokrevetna", double: "Dvokrevetna", triple: "Trokrevetna", apartment: "Apartman", suite: "Suite", other: "Ostalo" }[t] || t);
  const getRoomIcon = (type) => {
    if (type === "suite" || type === "apartment") return Star;
    return Bed;
  };
  const rb90Badge = (s) => {
    const m = { pending: { bg: "#fef3c7", c: "#92400e", l: "Čeka" }, registered: { bg: "#dcfce7", c: "#15803d", l: "Prijavljeno" }, deregistered: { bg: "#e0e7ff", c: "#4338ca", l: "Odjavljeno" }, failed: { bg: "#fef2f2", c: "#dc2626", l: "Greška" }, not_required: { bg: "#f3f4f6", c: "#6b7280", l: "Nije potrebno" } };
    const v = m[s] || m.pending;
    return <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: v.bg, color: v.c }}>{v.l}</span>;
  };
  const guestStatusBadge = (s) => {
    const m = { checked_in: { bg: "#dcfce7", c: "#15803d", l: "Prijavljen" }, checked_out: { bg: "#f3f4f6", c: "#6b7280", l: "Odjavljen" }, cancelled: { bg: "#fef2f2", c: "#dc2626", l: "Otkazano" }, no_show: { bg: "#fef3c7", c: "#92400e", l: "Nije došao" } };
    const v = m[s] || { bg: "#f3f4f6", c: "#6b7280", l: s };
    return <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: v.bg, color: v.c }}>{v.l}</span>;
  };
  const taxCatLabel = (c) => ({ adult: "Odrasli", child_7_18: "Djeca 7-18", child_under_7: "Djeca do 7", exempt: "Oslobođen", child: "Djeca" }[c] || c || "Odrasli");

  const availableRooms = rooms.filter(r => r.status === "available");

  // Tax calculations
  const taxTotals = useMemo(() => {
    let total = 0, paid = 0, pending = 0, nights = 0;
    taxGuests.forEach(g => {
      const t = parseFloat(g.tourist_tax_total || 0);
      total += t;
      nights += parseInt(g.num_nights || 0);
      if (g.tourist_tax_paid == 1) paid += t;
      else pending += t;
    });
    return { total, paid, pending, nights, count: taxGuests.length };
  }, [taxGuests]);

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>

      {/* Stats bar */}
      {stats && (
        <div style={{ padding: "12px 20px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 30, height: 30, borderRadius: 7, background: "#8b5cf620", display: "flex", alignItems: "center", justifyContent: "center" }}><Hotel size={15} color="#8b5cf6" /></div>
            <div><div style={{ fontSize: 11, color: S.muted }}>Popunjenost</div><div style={{ fontSize: 15, fontWeight: 700 }}>{stats.occupancy_rate}%</div></div>
          </div>
          <div style={{ width: 1, height: 28, background: S.border }} />
          <div><div style={{ fontSize: 11, color: S.muted }}>Sobe</div><div style={{ fontSize: 14, fontWeight: 700 }}><span style={{ color: "#3b82f6" }}>{stats.occupied_rooms}</span>/{stats.total_rooms}</div></div>
          <div><div style={{ fontSize: 11, color: S.muted }}>Gosti</div><div style={{ fontSize: 14, fontWeight: 700, color: S.green }}>{stats.active_guests}</div></div>
          <div><div style={{ fontSize: 11, color: S.muted }}>Danas ulaz</div><div style={{ fontSize: 14, fontWeight: 700, color: "#3b82f6" }}>{stats.today_checkins}</div></div>
          <div><div style={{ fontSize: 11, color: S.muted }}>Danas izlaz</div><div style={{ fontSize: 14, fontWeight: 700, color: S.orange }}>{stats.today_checkouts}</div></div>
          <div><div style={{ fontSize: 11, color: S.muted }}>RB-90 čeka</div><div style={{ fontSize: 14, fontWeight: 700, color: stats.pending_rb90 > 0 ? S.red : S.green }}>{stats.pending_rb90}</div></div>
          <div><div style={{ fontSize: 11, color: S.muted }}>Taksa (mjesec)</div><div style={{ fontSize: 14, fontWeight: 700, color: "#8b5cf6" }}>€{fmt(stats.month_tax_total)}</div></div>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, background: "white", borderBottom: `1px solid ${S.border}`, padding: "0 16px" }}>
        {[
          { id: "rooms", icon: Bed, label: "Sobe" },
          { id: "guests", icon: Users, label: "Gosti" },
          { id: "tax", icon: Euro, label: "Taksa" },
          { id: "settings", icon: Settings, label: "Podešavanja" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "12px 20px", border: "none", borderBottom: `2px solid ${tab === t.id ? "#8b5cf6" : "transparent"}`,
            background: "none", color: tab === t.id ? "#8b5cf6" : S.muted, fontSize: 14, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: S.font
          }}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ═══ ROOMS TAB — Visual Grid ═══ */}
      {tab === "rooms" && (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Room grid */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {rooms.length === 0 && !roomFilter ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: S.muted }}>
                <Bed size={48} strokeWidth={1.5} />
                <p style={{ marginTop: 16, fontSize: 16 }}>Nema soba. Dodajte sobe u Podešavanja.</p>
                <button onClick={() => setTab("settings")} style={{ ...S.btn("#8b5cf6"), marginTop: 12 }}><Plus size={15} />Dodaj sobu</button>
              </div>
            ) : (
              <>
                {/* Filter buttons */}
                <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {[{ v: "", l: "Sve" }, { v: "available", l: "Slobodne" }, { v: "occupied", l: "Zauzete" }, { v: "maintenance", l: "Održavanje" }].map(f => (
                    <button key={f.v} onClick={() => { setRoomFilter(f.v); loadRooms(f.v); }}
                      style={{ padding: "6px 14px", border: "1px solid", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: roomFilter === f.v ? "#8b5cf6" : S.border, background: roomFilter === f.v ? "#8b5cf6" : "white", color: roomFilter === f.v ? "white" : S.muted }}>
                      {f.l}
                    </button>
                  ))}
                  <div style={{ flex: 1 }} />
                  <button onClick={() => { loadRooms(); loadStats(); }} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
                </div>

                {rooms.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: S.muted }}>Nema soba sa ovim filterom</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14 }}>
                    {rooms.map(room => {
                      const color = getRoomColor(room);
                      const isSelected = selectedRoom?.id === room.id;
                      const RIcon = getRoomIcon(room.room_type);
                      const guestsInRoom = guests.filter(g => g.room_id == room.id && g.status === "checked_in");
                      return (
                        <div key={room.id} onClick={() => setSelectedRoom(isSelected ? null : room)}
                          style={{
                            background: "white", borderRadius: 12, border: `2px solid ${isSelected ? "#8b5cf6" : color + "40"}`,
                            padding: 0, cursor: "pointer", overflow: "hidden", transition: "all 0.2s",
                            boxShadow: isSelected ? "0 4px 20px rgba(139,92,246,0.15)" : "0 1px 4px rgba(0,0,0,0.04)",
                          }}>
                          <div style={{ background: color + "15", padding: "14px 14px 10px", position: "relative" }}>
                            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: S.dark, fontFamily: S.mono }}>{room.room_number}</div>
                                <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>{getRoomTypeLabel(room.room_type)}</div>
                              </div>
                              <div style={{ width: 36, height: 36, borderRadius: 8, background: color + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <RIcon size={18} color={color} />
                              </div>
                            </div>
                          </div>
                          <div style={{ padding: "10px 14px 12px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                              <span style={{ fontSize: 11, color: S.muted }}>{room.floor ? `Sprat ${room.floor}` : ""} • {room.capacity || 2} osobe</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: color + "18", color }}>{getRoomStatusLabel(room.status)}</span>
                              {room.price_per_night > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: S.dark }}>€{fmt(room.price_per_night)}</span>}
                            </div>
                            {guestsInRoom.length > 0 && (
                              <div style={{ marginTop: 8, padding: "6px 8px", background: "#f0f9ff", borderRadius: 6, fontSize: 11 }}>
                                {guestsInRoom.map(g => (
                                  <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 4, color: "#1e40af" }}>
                                    <User size={10} />{g.first_name} {g.last_name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Room detail panel (right side) */}
          {selectedRoom && (
            <div style={{ width: 340, borderLeft: `1px solid ${S.border}`, background: "white", overflow: "auto", flexShrink: 0 }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 18, fontFamily: S.mono }}>{selectedRoom.room_number}</div>
                <button onClick={() => setSelectedRoom(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} color={S.muted} /></button>
              </div>
              <div style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: getRoomColor(selectedRoom) + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {(() => { const RI = getRoomIcon(selectedRoom.room_type); return <RI size={22} color={getRoomColor(selectedRoom)} />; })()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{getRoomTypeLabel(selectedRoom.room_type)}</div>
                    <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: getRoomColor(selectedRoom) + "18", color: getRoomColor(selectedRoom) }}>{getRoomStatusLabel(selectedRoom.status)}</span>
                  </div>
                </div>
                <div style={{ fontSize: 14, display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  <Row label="Sprat" value={selectedRoom.floor || "—"} />
                  <Row label="Kapacitet" value={`${selectedRoom.capacity || 2} osobe`} />
                  <Row label="Cijena/noć" value={selectedRoom.price_per_night ? `€${fmt(selectedRoom.price_per_night)}` : "—"} />
                </div>

                {/* Guests in this room */}
                {(() => {
                  const roomGuests = guests.filter(g => g.room_id == selectedRoom.id && g.status === "checked_in");
                  if (roomGuests.length === 0) return <div style={{ padding: 16, background: "#f9fafb", borderRadius: 8, textAlign: "center", color: S.muted, fontSize: 13 }}>Nema gostiju u sobi</div>;
                  return (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: S.muted }}>Gosti ({roomGuests.length})</div>
                      {roomGuests.map(g => (
                        <div key={g.id} style={{ background: "#f0f9ff", borderRadius: 8, padding: 12, marginBottom: 6, border: "1px solid #bae6fd" }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{g.first_name} {g.last_name}</div>
                          <div style={{ fontSize: 12, color: S.muted }}>{g.nationality} • {fmtDate(g.checkin_date)} → {fmtDate(g.checkout_date)}</div>
                          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                            {guestStatusBadge(g.status)}
                            {rb90Badge(g.rb90_status || "pending")}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Room actions */}
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: S.muted, marginBottom: 4 }}>Status sobe</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {[{ v: "available", l: "Slobodna", c: "#22c55e" }, { v: "occupied", l: "Zauzeta", c: "#3b82f6" }, { v: "maintenance", l: "Održavanje", c: "#f59e0b" }, { v: "inactive", l: "Neaktivna", c: "#6b7280" }].map(s => (
                      <button key={s.v} onClick={() => changeRoomStatus(selectedRoom.id, s.v)}
                        style={{ padding: "8px", border: `1px solid ${selectedRoom.status === s.v ? s.c : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", background: selectedRoom.status === s.v ? s.c + "15" : "white", color: selectedRoom.status === s.v ? s.c : S.muted, fontFamily: S.font }}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => { setEditingRoom(selectedRoom.id); setRoomForm({ room_number: selectedRoom.room_number, room_type: selectedRoom.room_type || "double", floor: selectedRoom.floor || "", capacity: selectedRoom.capacity || 2, price_per_night: selectedRoom.price_per_night || "" }); setTab("settings"); }} style={{ ...S.btnSm("#eef2ff", S.accent), justifyContent: "center", marginTop: 6 }}><Edit3 size={12} />Uredi sobu</button>
                  <button onClick={() => deleteRoom(selectedRoom.id)} style={{ ...S.btnSm("#fef2f2", S.red), justifyContent: "center" }}><Trash2 size={12} />Obriši sobu</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ GUESTS TAB ═══ */}
      {tab === "guests" && (
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => setGuestModal(true)} style={{ ...S.btn("#8b5cf6"), padding: "10px 18px" }}><UserPlus size={15} />Prijavi gosta</button>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 6 }}>
              {[{ v: "", l: "Svi" }, { v: "checked_in", l: "Prijavljeni" }, { v: "checked_out", l: "Odjavljeni" }].map(f => (
                <button key={f.v} onClick={() => setGuestFilter(f.v)}
                  style={{ padding: "6px 12px", border: "1px solid", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: guestFilter === f.v ? "#8b5cf6" : S.border, background: guestFilter === f.v ? "#8b5cf6" : "white", color: guestFilter === f.v ? "white" : S.muted }}>
                  {f.l}
                </button>
              ))}
            </div>
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: S.muted }} />
              <input placeholder="Pretraži..." value={guestSearch} onChange={e => setGuestSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadGuests()}
                style={{ ...S.input, padding: "7px 10px 7px 30px", width: 180, fontSize: 13 }} />
            </div>
            <button onClick={loadGuests} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
          </div>

          {guests.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: S.muted }}>
              <Users size={40} strokeWidth={1.5} />
              <p style={{ marginTop: 12 }}>Nema gostiju</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {guests.map(g => {
                const isExpanded = expandedGuest === g.id;
                const isCheckedIn = g.status === "checked_in";
                return (
                  <div key={g.id} style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden" }}>
                    <div onClick={() => setExpandedGuest(isExpanded ? null : g.id)}
                      style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: isCheckedIn ? "#dcfce7" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <User size={18} color={isCheckedIn ? S.green : S.muted} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{g.first_name} {g.last_name}</div>
                        <div style={{ fontSize: 12, color: S.muted, display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                          <span><Globe size={10} style={{ verticalAlign: "middle" }} /> {g.nationality || "—"}</span>
                          <span>{g.document_type === "passport" || g.document_type === "PASSPORT" ? "Pasoš" : "LK"}: {g.document_number || "—"}</span>
                          {(g.room_number || g.room_id) && <span><Bed size={10} style={{ verticalAlign: "middle" }} /> Soba {g.room_number || g.room_id}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 12, color: S.muted }}>{fmtDate(g.checkin_date)} → {fmtDate(g.checkout_date)}</div>
                        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", marginTop: 4 }}>
                          {guestStatusBadge(g.status)}
                          {rb90Badge(g.rb90_status || "pending")}
                        </div>
                      </div>
                      <ChevronDown size={16} color={S.muted} style={{ transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "none" }} />
                    </div>
                    {isExpanded && (
                      <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${S.border}` }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "12px 0", fontSize: 13 }}>
                          <div><span style={{ color: S.muted }}>Datum rođenja:</span> {fmtDate(g.date_of_birth)}</div>
                          <div><span style={{ color: S.muted }}>Pol:</span> {g.gender === "M" ? "Muški" : "Ženski"}</div>
                          <div><span style={{ color: S.muted }}>Noći:</span> {g.num_nights || "—"}</div>
                          <div><span style={{ color: S.muted }}>Taksa kategorija:</span> {taxCatLabel(g.tourist_tax_category)}</div>
                          <div><span style={{ color: S.muted }}>Taksa/noć:</span> €{fmt(g.tourist_tax_per_night)}</div>
                          <div><span style={{ color: S.muted, fontWeight: 600 }}>Ukupno taksa:</span> <strong>€{fmt(g.tourist_tax_total)}</strong> {g.tourist_tax_paid == 1 ? <span style={{ color: S.green, fontSize: 11 }}>✓ Plaćeno</span> : <span style={{ color: S.orange, fontSize: 11 }}>Neplaćeno</span>}</div>
                        </div>

                        {/* RB-90 status detail */}
                        {g.rb90_registered_at && (
                          <div style={{ background: "#f0fdf4", borderRadius: 6, padding: 8, marginBottom: 8, fontSize: 12, color: "#15803d" }}>
                            <Shield size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                            MUP RB-90 prijavljen: {fmtDateTime(g.rb90_registered_at)}
                            {g.rb90_request_uuid && <span style={{ marginLeft: 8, color: S.muted, fontFamily: S.mono, fontSize: 10 }}>UUID: {g.rb90_request_uuid}</span>}
                          </div>
                        )}
                        {g.rb90_deregistered_at && (
                          <div style={{ background: "#e0e7ff", borderRadius: 6, padding: 8, marginBottom: 8, fontSize: 12, color: "#4338ca" }}>
                            <Shield size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                            MUP odjava: {fmtDateTime(g.rb90_deregistered_at)}
                          </div>
                        )}

                        {/* Action buttons */}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                          {isCheckedIn && (
                            <button onClick={(e) => { e.stopPropagation(); checkoutGuest(g.id); }} disabled={processing} style={{ ...S.btnSm("#fef2f2", S.red) }}><UserX size={12} />Odjavi</button>
                          )}
                          {isCheckedIn && (g.rb90_status === "pending" || g.rb90_status === "failed") && (
                            <button onClick={(e) => { e.stopPropagation(); submitRB90(g.id); }} disabled={processing} style={{ ...S.btnSm("#fef3c7", "#92400e") }}><Shield size={12} />Prijavi MUP (RB-90)</button>
                          )}
                          {g.rb90_status === "registered" && g.status === "checked_out" && (
                            <button onClick={(e) => { e.stopPropagation(); deregisterRB90(g.id); }} disabled={processing} style={{ ...S.btnSm("#e0e7ff", "#4338ca") }}><Shield size={12} />Odjavi MUP</button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); sendToPMS(); }} style={{ ...S.btnSm("#f3f4f6", S.muted) }}><Send size={12} />Pošalji PMS</button>
                          {g.tourist_tax_paid != 1 && (
                            <button onClick={(e) => { e.stopPropagation(); markTaxPaid(g.id); }} style={{ ...S.btnSm("#faf5ff", "#8b5cf6") }}><Euro size={12} />Označi taksu plaćenom</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ TAX TAB ═══ */}
      {tab === "tax" && (
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {/* Tax summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>Ukupno gostiju</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: S.dark }}>{taxTotals.count}</div>
            </div>
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>Ukupno noći</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#3b82f6" }}>{taxTotals.nights}</div>
            </div>
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>Ukupna taksa</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#8b5cf6" }}>€{fmt(taxTotals.total)}</div>
            </div>
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>Plaćeno</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: S.green }}>€{fmt(taxTotals.paid)}</div>
            </div>
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>Neplaćeno</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: S.red }}>€{fmt(taxTotals.pending)}</div>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ v: "today", l: "Danas" }, { v: "7days", l: "7 dana" }, { v: "30days", l: "30 dana" }, { v: "custom", l: "Period" }].map(f => (
                <button key={f.v} onClick={() => setTaxPeriod(f.v)}
                  style={{ padding: "6px 14px", border: "1px solid", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: taxPeriod === f.v ? "#8b5cf6" : S.border, background: taxPeriod === f.v ? "#8b5cf6" : "white", color: taxPeriod === f.v ? "white" : S.muted }}>
                  {f.l}
                </button>
              ))}
            </div>
            {taxPeriod === "custom" && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="date" value={taxDateFrom} onChange={e => setTaxDateFrom(e.target.value)} style={{ ...S.input, padding: "5px 8px", fontSize: 12, width: 130 }} />
                <span style={{ color: S.muted }}>—</span>
                <input type="date" value={taxDateTo} onChange={e => setTaxDateTo(e.target.value)} style={{ ...S.input, padding: "5px 8px", fontSize: 12, width: 130 }} />
              </div>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 6 }}>
              {[{ v: "", l: "Svi" }, { v: "checked_in", l: "Prijavljeni" }, { v: "checked_out", l: "Odjavljeni" }].map(f => (
                <button key={f.v} onClick={() => setTaxStatusFilter(f.v)}
                  style={{ padding: "5px 10px", border: "1px solid", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: taxStatusFilter === f.v ? S.dark : S.border, background: taxStatusFilter === f.v ? S.dark : "white", color: taxStatusFilter === f.v ? "white" : S.muted }}>
                  {f.l}
                </button>
              ))}
            </div>
            <button onClick={loadTaxGuests} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
          </div>

          {/* Tax table */}
          {taxLoading ? <div style={{ textAlign: "center", padding: 40 }}><Loader2 size={24} className="spin" /></div>
          : taxGuests.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: S.muted }}><Euro size={36} strokeWidth={1.5} /><p style={{ marginTop: 10 }}>Nema podataka za odabrani period</p></div>
          : (
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th style={th}>Gost</th><th style={th}>Kategorija</th><th style={th}>Check-in</th><th style={th}>Check-out</th>
                    <th style={{ ...th, textAlign: "center" }}>Noći</th><th style={{ ...th, textAlign: "right" }}>Tarifa/noć</th>
                    <th style={{ ...th, textAlign: "right" }}>Ukupno</th><th style={{ ...th, textAlign: "center" }}>Status</th><th style={{ ...th, textAlign: "center" }}>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {taxGuests.slice((taxPage - 1) * 20, taxPage * 20).map(g => (
                    <tr key={g.id} style={{ borderBottom: `1px solid ${S.border}` }}>
                      <td style={td}><strong>{g.first_name} {g.last_name}</strong><br/><span style={{ fontSize: 11, color: S.muted }}>{g.nationality}</span></td>
                      <td style={td}><span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 11, background: "#eef2ff", color: S.accent }}>{taxCatLabel(g.tourist_tax_category)}</span></td>
                      <td style={{ ...td, fontSize: 12 }}>{fmtDate(g.checkin_date)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{fmtDate(g.checkout_date)}</td>
                      <td style={{ ...td, textAlign: "center", fontWeight: 700 }}>{g.num_nights || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>€{fmt(g.tourist_tax_per_night)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#8b5cf6" }}>€{fmt(g.tourist_tax_total)}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        {g.tourist_tax_paid == 1
                          ? <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#15803d" }}>Plaćeno</span>
                          : <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600, background: "#fef3c7", color: "#92400e" }}>Neplaćeno</span>
                        }
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>
                        {g.tourist_tax_paid != 1 && (
                          <button onClick={() => markTaxPaid(g.id)} style={{ ...S.btnSm("#f0fdf4", S.green), padding: "3px 8px", fontSize: 11 }}><Check size={10} />Plaćeno</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {taxGuests.length > 20 && (
                <div style={{ padding: "10px 16px", borderTop: `1px solid ${S.border}`, display: "flex", justifyContent: "center", gap: 4 }}>
                  {Array.from({ length: Math.ceil(taxGuests.length / 20) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setTaxPage(p)} style={{ width: 30, height: 30, border: "1px solid", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, borderColor: taxPage === p ? S.dark : S.border, background: taxPage === p ? S.dark : "white", color: taxPage === p ? "white" : S.muted, fontFamily: S.font }}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tax rates info */}
          {taxConfig && (
            <div style={{ marginTop: 16, background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: S.muted }}>Aktivne tarife</div>
              <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
                <span>Odrasli: <strong>€{fmt(taxConfig.tax_rate_adult)}/noć</strong></span>
                <span>Djeca 7-18: <strong>€{fmt(taxConfig.tax_rate_child_7_18)}/noć</strong></span>
                <span>Djeca do 7: <strong>€{fmt(taxConfig.tax_rate_child_under_7)}/noć</strong></span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ SETTINGS TAB ═══ */}
      {tab === "settings" && (
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 1000 }}>

            {/* Add/Edit Room */}
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Bed size={18} color="#8b5cf6" />{editingRoom ? "Uredi sobu" : "Dodaj sobu"}
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Broj sobe *</label>
                  <input value={roomForm.room_number} onChange={e => setRoomForm(p => ({ ...p, room_number: e.target.value }))} placeholder="101" style={S.input} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip sobe</label>
                  <select value={roomForm.room_type} onChange={e => setRoomForm(p => ({ ...p, room_type: e.target.value }))} style={S.input}>
                    <option value="single">Jednokrevetna</option><option value="double">Dvokrevetna</option>
                    <option value="triple">Trokrevetna</option><option value="apartment">Apartman</option>
                    <option value="suite">Suite</option><option value="other">Ostalo</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Sprat</label><input type="number" value={roomForm.floor} onChange={e => setRoomForm(p => ({ ...p, floor: e.target.value }))} placeholder="1" style={S.input} /></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Kapacitet</label><input type="number" value={roomForm.capacity} onChange={e => setRoomForm(p => ({ ...p, capacity: parseInt(e.target.value) || 1 }))} style={S.input} /></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Cijena/noć (€)</label><input type="number" step="0.01" value={roomForm.price_per_night} onChange={e => setRoomForm(p => ({ ...p, price_per_night: e.target.value }))} placeholder="50.00" style={S.input} /></div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveRoom} disabled={processing || !roomForm.room_number} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center", opacity: roomForm.room_number ? 1 : 0.5 }}>
                  {processing ? <Loader2 size={15} className="spin" /> : <Check size={15} />}{editingRoom ? "Sačuvaj" : "Dodaj sobu"}
                </button>
                {editingRoom && <button onClick={() => { setEditingRoom(null); setRoomForm({ room_number: "", room_type: "double", floor: "", capacity: 2, price_per_night: "" }); }} style={S.btn("#f3f4f6", S.muted)}>Otkaži</button>}
              </div>
            </div>

            {/* Tourist Tax Config */}
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Euro size={18} color="#8b5cf6" />Boravišna taksa (po noći)
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div><label style={{ fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><User size={13} />Odrasli (€/noć)</label><input type="number" step="0.01" value={taxForm.adult} onChange={e => setTaxForm(p => ({ ...p, adult: e.target.value }))} style={S.input} /></div>
                <div><label style={{ fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><User size={13} />Djeca 7-18 (€/noć)</label><input type="number" step="0.01" value={taxForm.child} onChange={e => setTaxForm(p => ({ ...p, child: e.target.value }))} style={S.input} /></div>
                <div><label style={{ fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><User size={13} />Djeca do 7 / Oslobođeni (€/noć)</label><input type="number" step="0.01" value={taxForm.infant} onChange={e => setTaxForm(p => ({ ...p, infant: e.target.value }))} style={S.input} /></div>
              </div>
              <button onClick={saveTaxConfig} disabled={processing} style={{ ...S.btn("#8b5cf6"), width: "100%", justifyContent: "center", marginTop: 14 }}>
                {processing ? <Loader2 size={15} className="spin" /> : <Save size={15} />}Sačuvaj tarife
              </button>
            </div>

            {/* PMS Config placeholder */}
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Send size={18} color="#8b5cf6" />Hotel PMS integracija
              </div>
              <div style={{ background: "#fef3c7", borderRadius: 8, padding: 14, fontSize: 13, color: "#92400e" }}>
                <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                Hotel PMS nije povezan. Integracija se konfiguriše preko API privatnog ključa. Kontaktirajte podršku za aktivaciju.
              </div>
            </div>

            {/* RB-90 Config placeholder */}
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Shield size={18} color="#8b5cf6" />MUP RB-90 konfiguracija
              </div>
              <div style={{ background: "#eff6ff", borderRadius: 8, padding: 14, fontSize: 13, color: "#1e40af" }}>
                <Shield size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                RB-90 prijava gostiju MUP-u. Trenutno radi u ručnom režimu (lokalna registracija). Za automatsku API integraciju sa MUP sistemom potrebna je konfiguracija certifikata.
              </div>
            </div>
          </div>

          {/* Room list for editing */}
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginTop: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Bed size={18} color="#8b5cf6" />Sve sobe ({rooms.length})</div>
              <button onClick={() => loadRooms()} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={12} /></button>
            </div>
            {rooms.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: S.muted, fontSize: 14 }}>Nema soba</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr><th style={th}>Broj</th><th style={th}>Tip</th><th style={th}>Sprat</th><th style={th}>Kapacitet</th><th style={th}>Cijena/noć</th><th style={th}>Status</th><th style={th}>Akcije</th></tr></thead>
                  <tbody>
                    {rooms.map(r => (
                      <tr key={r.id}>
                        <td style={td}><strong>{r.room_number}</strong></td>
                        <td style={td}>{getRoomTypeLabel(r.room_type)}</td>
                        <td style={td}>{r.floor || "—"}</td>
                        <td style={td}>{r.capacity}</td>
                        <td style={td}>{r.price_per_night ? `€${fmt(r.price_per_night)}` : "—"}</td>
                        <td style={td}>
                          <select value={r.status} onChange={e => changeRoomStatus(r.id, e.target.value)}
                            style={{ ...S.input, padding: "4px 6px", fontSize: 12, width: "auto", background: getRoomColor(r) + "12", color: getRoomColor(r), fontWeight: 600, border: `1px solid ${getRoomColor(r)}40` }}>
                            <option value="available">Slobodna</option><option value="occupied">Zauzeta</option>
                            <option value="maintenance">Održavanje</option><option value="inactive">Neaktivna</option>
                          </select>
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => { setEditingRoom(r.id); setRoomForm({ room_number: r.room_number, room_type: r.room_type || "double", floor: r.floor || "", capacity: r.capacity || 2, price_per_night: r.price_per_night || "" }); window.scrollTo(0, 0); }} style={{ ...S.btnSm("#eef2ff", S.accent), padding: "4px 8px" }}><Edit3 size={11} /></button>
                            <button onClick={() => deleteRoom(r.id)} style={{ ...S.btnSm("#fef2f2", S.red), padding: "4px 8px" }}><Trash2 size={11} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ CHECK-IN MODAL ═══ */}
      {guestModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setGuestModal(false)}>
          <div style={{ background: "white", borderRadius: 14, width: 560, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17 }}><UserPlus size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />Prijava gosta</span>
              <button onClick={() => setGuestModal(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color={S.muted} /></button>
            </div>
            <div style={{ padding: 20 }}>
              {/* Passport upload */}
              <div style={{ background: "#faf5ff", border: "2px dashed #c4b5fd", borderRadius: 10, padding: 16, marginBottom: 18, textAlign: "center" }}>
                <Camera size={24} color="#8b5cf6" style={{ marginBottom: 6 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: "#8b5cf6", marginBottom: 4 }}>Skeniraj pasoš / ličnu kartu</div>
                <div style={{ fontSize: 12, color: S.muted, marginBottom: 10 }}>Fotografiši ili uploaduj sliku dokumenta za automatsko popunjavanje</div>
                <label style={{ ...S.btn("#8b5cf6"), cursor: "pointer", display: "inline-flex", fontSize: 13, padding: "8px 16px" }}>
                  {passportLoading ? <><Loader2 size={14} className="spin" />Čitanje...</> : <><Upload size={14} />Izaberi sliku</>}
                  <input type="file" accept="image/*" capture="environment" onChange={handlePassportUpload} style={{ display: "none" }} />
                </label>
              </div>

              {/* Manual form */}
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Ime *</label><input value={gf.first_name} onChange={e => updateGf({ first_name: e.target.value })} placeholder="Muhamed" style={S.input} /></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Prezime *</label><input value={gf.last_name} onChange={e => updateGf({ last_name: e.target.value })} placeholder="Bisha" style={S.input} /></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Datum rođenja</label><input type="date" value={gf.date_of_birth} onChange={e => updateGf({ date_of_birth: e.target.value })} style={S.input} /></div>
                <div style={{ flex: "0 0 100px" }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Pol</label><select value={gf.gender} onChange={e => updateGf({ gender: e.target.value })} style={S.input}><option value="M">Muški</option><option value="F">Ženski</option></select></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Državljanstvo * (3 slova)</label><input value={gf.nationality} onChange={e => updateGf({ nationality: e.target.value.toUpperCase() })} placeholder="MNE" maxLength={3} style={S.input} /></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: "0 0 130px" }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip dokumenta</label><select value={gf.document_type} onChange={e => updateGf({ document_type: e.target.value })} style={S.input}><option value="PASSPORT">Pasoš</option><option value="ID">Lična karta</option><option value="OTHER">Ostalo</option></select></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Broj dokumenta *</label><input value={gf.document_number} onChange={e => updateGf({ document_number: e.target.value })} placeholder="AB123456" style={S.input} /></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Check-in *</label><input type="date" value={gf.checkin_date} onChange={e => updateGf({ checkin_date: e.target.value })} style={S.input} /></div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Check-out *</label><input type="date" value={gf.checkout_date} onChange={e => updateGf({ checkout_date: e.target.value })} style={S.input} /></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Soba</label><select value={gf.room_id} onChange={e => updateGf({ room_id: e.target.value })} style={S.input}><option value="">— Bez sobe —</option>{availableRooms.map(r => <option key={r.id} value={r.id}>Soba {r.room_number} ({getRoomTypeLabel(r.room_type)}) — €{fmt(r.price_per_night)}</option>)}</select></div>
                <div style={{ flex: "0 0 140px" }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Kategorija takse</label><select value={gf.tourist_tax_category} onChange={e => updateGf({ tourist_tax_category: e.target.value })} style={S.input}><option value="adult">Odrasli</option><option value="child_7_18">Djeca 7-18</option><option value="child_under_7">Djeca do 7</option><option value="exempt">Oslobođen</option></select></div>
              </div>
              <div style={{ marginBottom: 14 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Napomene</label><input value={gf.notes} onChange={e => updateGf({ notes: e.target.value })} placeholder="Dodatne napomene..." style={S.input} /></div>
              <button onClick={checkInGuest} disabled={processing} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                {processing ? <Loader2 size={17} className="spin" /> : <UserCheck size={17} />}Prijavi gosta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VirmanView({ user, showToast, online }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [manualPartner, setManualPartner] = useState(null); // { name, tax_id }
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentDue, setPaymentDue] = useState("end_of_month");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [subView, setSubView] = useState("create"); // create | list
  // Non-cash invoices list
  const [ncInvoices, setNcInvoices] = useState([]);
  const [ncLoading, setNcLoading] = useState(false);
  const [ncPg, setNcPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [ncDetail, setNcDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [p, c, pa] = await Promise.all([api("/products"), api("/invoices/categories"), api("/partners")]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
    if (pa.success) setPartners(pa.data?.partners || []);
    setLoading(false);
  };

  const loadNcInvoices = async () => {
    setNcLoading(true);
    const tcr = getTcr();
    const params = new URLSearchParams({ page: ncPg.page, per_page: 20 });
    if (tcr) params.set("tcr_code", tcr);
    const data = await api(`/invoices?${params}`);
    if (data.success) {
      const all = data.data.invoices || [];
      const filtered = all.filter(inv => inv.invoice_type === "non_cash" || inv.type_of_inv === "NONCASH" || inv.payment_method === "bank_transfer" || inv.payment_method === "other");
      setNcInvoices(filtered);
      setNcPg(p => ({ ...p, ...data.data.pagination }));
    }
    setNcLoading(false);
  };

  useEffect(() => { if (subView === "list") loadNcInvoices(); }, [subView, ncPg.page]);

  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === product.id);
      if (ex) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: product.id, name: product.name, price: parseFloat(product.price), quantity: 1, vat_rate: parseFloat(product.vat_rate || 21), unit: product.unit || "kom", discount_type: "none", discount_value: 0, custom_price: null }];
    });
  };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearAll = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setSelectedPartner(null); setManualPartner(null); setPaymentDue("end_of_month"); setPaymentMethod("bank_transfer"); };

  const cartCalc = useMemo(() => {
    let netTotal = 0;
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value;
      netTotal += lineNet - disc;
    });
    let invDisc = 0;
    if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100);
    else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    let vatAmount = 0;
    const vatGroups = {};
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value || 0;
      const afterDisc = lineNet - disc;
      const rate = parseFloat(item.vat_rate || 21);
      const vat = afterDisc * (rate / 100);
      vatAmount += vat;
      if (!vatGroups[rate]) vatGroups[rate] = { base: 0, vat: 0 };
      vatGroups[rate].base += afterDisc;
      vatGroups[rate].vat += vat;
    });
    if (invDisc > 0 && netTotal > 0) {
      const ratio = (netTotal - invDisc) / netTotal;
      vatAmount *= ratio;
    }
    const total = subtotal + vatAmount;
    return { netTotal, invDisc, subtotal, vatAmount, total, vatGroups };
  }, [cart, invoiceDiscount]);

  const buyerName = selectedPartner ? selectedPartner.name : manualPartner?.name || "";
  const buyerPib = selectedPartner ? selectedPartner.tax_id : manualPartner?.tax_id || "";

  const buildPayload = (fiscNow) => ({
    location_id: user.location_id || 1,
    tcr_code: getTcr(),
    payment_method: paymentMethod,
    invoice_type: "non_cash",
    type_of_inv: "NONCASH",
    fiscalize_now: fiscNow,
    discount_type: invoiceDiscount.type,
    discount_value: invoiceDiscount.value,
    partner_id: selectedPartner?.id || null,
    buyer_name: buyerName,
    buyer_pib: buyerPib,
    buyer_address: selectedPartner?.address || "",
    buyer_city: selectedPartner?.city || "",
    payment_due: paymentDue,
    items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, unit_price: item.custom_price !== null ? item.custom_price : item.price, vat_rate: item.vat_rate, unit: item.unit, discount_type: item.discount_type, discount_value: item.discount_value }))
  });

  const handleFiscalize = async () => {
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    setShowConfirm(false); setFiscalizing(true);
    let isReachable = false;
    try { const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]); isReachable = test && test.ok; } catch(e) { isReachable = false; }
    if (!isReachable) { setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}). Fiskalizujte kad budete online.`, "error"); return; }
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) });
      setFiscalizing(false);
      if (data.success) {
        setLastFiscalized({ fic: data.data.fic, invoice: data.data.invoice, total: cartCalc.total }); clearAll(); showToast("Fiskalizacija uspješna!");
        const invoiceId = data.data.invoice?.id;
        if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
          try { await printReceipt(invoiceId); } catch(e) { console.log("Auto-print failed:", e); }
        }
      }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Greška! Draft sačuvan lokalno (${cnt}).`, "error"); }
    } catch(e) { setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt}).`, "error"); }
  };
  const handleSaveDraft = async () => {
    if (!cart.length) return;
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    let isReachable = false;
    try { const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]); isReachable = test && test.ok; } catch(e) { isReachable = false; }
    if (!isReachable) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error"); return; }
    setSaving(true);
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) });
      setSaving(false);
      if (data.success) { clearAll(); showToast("Virman sačuvan! #" + (data.data.invoice?.invoice_number || "")); }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Greška! Draft sačuvan lokalno (${cnt})`, "error"); }
    } catch(e) { setSaving(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "error"); }
  };

  const viewNcInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setNcDetail(d.data); };
  const handleStorno = async (id, reason) => {
    const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true }) });
    if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setNcDetail(null); loadNcInvoices(); } else showToast(d.message, "error");
  };
  const handleEmail = async (id, email) => {
    const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) });
    if (d.success) showToast("Email poslat!"); else showToast(d.message || "Greška", "error");
    setEmailModal(null);
  };

  const filtered = products.filter(p => {
    if (search) return p.name.toLowerCase().includes(search.toLowerCase());
    if (activeCategory === "all") return true;
    return String(p.category_id) === String(activeCategory);
  });

  const filteredPartners = partners.filter(p => !partnerSearch || p.name.toLowerCase().includes(partnerSearch.toLowerCase()) || (p.tax_id && p.tax_id.includes(partnerSearch)));

  const payDueLabel = (v) => ({ end_of_month: "Kraj mjeseca", avans: "AVANS", unspecified: "Neodređeno" }[v] || v);

  if (lastFiscalized) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 40 }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ width: 70, height: 70, background: "#ecfdf5", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}><Check size={36} color={S.green} /></div>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 6px" }}>Virman fiskalizovan!</h2>
        <p style={{ color: S.muted, fontSize: 16, marginBottom: 22 }}>Bezgotovinsi račun registrovan kod Poreske Uprave</p>
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 22, textAlign: "left" }}>
          <Row label="FIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, color: S.green, wordBreak: "break-all" }}>{lastFiscalized.fic}</span>} />
          <Row label="Ukupno" value={<span style={{ fontWeight: 700, fontSize: 22 }}>{fmt(lastFiscalized.total)} €</span>} />
          <Row label="Broj" value={<span style={{ fontFamily: S.mono, fontSize: 13 }}>{lastFiscalized.invoice?.invoice_number || "-"}</span>} last />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => { const id = lastFiscalized.invoice?.id; if (id) openPdf(`/invoices/pdf/${id}?type=a4`); }} style={S.btn("#f3f4f6", S.muted)}><Download size={17} />PDF</button>
          <button onClick={async () => { const id = lastFiscalized.invoice?.id; if (id) { const ok = await printReceipt(id); if (ok) showToast("Štampano!"); else showToast("Štampa nije uspjela", "error"); } }} style={S.btn("#f3f4f6", S.muted)}><Printer size={17} />Štampaj</button>
          <button onClick={() => setLastFiscalized(null)} style={S.btn(S.dark)}><Plus size={17} />Novi virman</button>
        </div>
      </div>
    </div>
  );

  // NC invoice detail
  if (ncDetail) {
    const inv = ncDetail.invoice; const items = ncDetail.items || [];
    const isStorno = inv.is_storno == 1;
    return (
      <div style={{ height: "100%", overflow: "auto", background: S.bg }}>
        <div style={{ padding: "14px 20px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setNcDetail(null)} style={{ padding: 8, background: "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", display: "flex" }}><ArrowLeft size={17} /></button>
          <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isStorno && "⊖ "}{inv.invoice_number}</h2><span style={{ fontSize: 13, color: "#9ca3af" }}>{fmtDateTime(inv.invoice_date)}</span></div>
          <Badge status={inv.status} />
          <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} />PDF</button>
          <button onClick={async () => { await printReceipt(inv.id) }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={13} />Štampaj</button>
          <button onClick={() => setEmailModal(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Mail size={13} />Email</button>
          {inv.status === "draft" && <button onClick={async () => { const d = await api(`/invoices/fiscalize/${inv.id}`, { method: "POST" }); if (d.success) { showToast("Fiskalizovano!"); loadNcInvoices(); viewNcInvoice(inv.id); } else showToast(d.message, "error"); }} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} />Fiskalizuj</button>}
          {ncDetail.can_storno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} />Storniraj</button>}
        </div>
        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>
          {inv.buyer_name && <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Kupac</div>
            <Row label="Naziv" value={inv.buyer_name} />
            {inv.buyer_pib && <Row label="PIB" value={inv.buyer_pib} />}
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} last />}
          </div>}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, fontWeight: 700, fontSize: 15 }}>Stavke</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "center" }}>Kol.</th><th style={{ ...th, textAlign: "right" }}>Cijena</th><th style={{ ...th, textAlign: "right" }}>Popust</th><th style={{ ...th, textAlign: "right" }}>Ukupno</th></tr></thead>
              <tbody>{items.map((it, i) => <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={td}>{it.item_name || it.name}</td><td style={{ ...td, textAlign: "center" }}>{it.quantity}</td><td style={{ ...td, textAlign: "right" }}>{fmt(it.unit_price)} €</td><td style={{ ...td, textAlign: "right", color: parseFloat(it.discount_amount || 0) > 0 ? S.orange : S.muted }}>{parseFloat(it.discount_amount || 0) > 0 ? `-${fmt(it.discount_amount)} €` : "-"}</td><td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(it.total_amount || it.quantity * it.unit_price)} €</td></tr>)}</tbody>
            </table>
          </div>
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} />
            {(() => { const vg = {}; (ncDetail?.items || []).forEach(it => { const r = parseFloat(it.vat_rate || 21); const gross = parseFloat(it.total_amount || it.quantity * it.unit_price); const net = gross / (1 + r/100); if (!vg[r]) vg[r] = 0; vg[r] += gross - net; }); return Object.entries(vg).map(([r, v]) => <Row key={r} label={`PDV ${r}%`} value={`${fmt(v)} €`} />); })()}
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={<span style={{ color: S.orange }}>-{fmt(inv.discount_amount)} €</span>} />}
            <Row label="Plaćanje" value={inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method} />
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} />}
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: isStorno ? S.red : S.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* LEFT: Products top, Partners bottom */}
      <div style={{ flex: "0 0 57%", display: "flex", flexDirection: "column", borderRight: `1px solid ${S.border}` }}>
        {/* Tab: Create | List */}
        <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6 }}>
          <button onClick={() => setSubView("create")} style={{ ...S.btnSm(subView === "create" ? S.dark : "#f3f4f6", subView === "create" ? "white" : S.muted) }}><PlusCircle size={13} />Novi virman</button>
          <button onClick={() => setSubView("list")} style={{ ...S.btnSm(subView === "list" ? S.dark : "#f3f4f6", subView === "list" ? "white" : S.muted) }}><FileText size={13} />Virmani ({getTcr() ? `TCR: ${getTcr().slice(-6)}` : "svi"})</button>
        </div>

        {subView === "list" ? (
          <div style={{ flex: 1, overflow: "auto", padding: "0 14px" }}>
            {ncLoading ? <div style={{ textAlign: "center", padding: 50 }}><Loader2 size={28} className="spin" /></div>
            : ncInvoices.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Building size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>Nema bezgotovinskih faktura</p></div>
            : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginTop: 8 }}>
              <thead><tr><th style={th}>Broj</th><th style={th}>Kupac</th><th style={{ ...th, textAlign: "right" }}>Iznos</th><th style={{ ...th, textAlign: "center" }}>Status</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
              <tbody>{ncInvoices.map(inv => {
                const isStorno = inv.is_storno == 1;
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6", background: isStorno ? "#fef2f2" : "white" }}>
                    <td style={td}><span style={{ fontFamily: S.mono, fontSize: 12, fontWeight: 600 }}>{inv.invoice_number}</span><br/><span style={{ fontSize: 11, color: S.muted }}>{fmtDate(inv.invoice_date)}</span></td>
                    <td style={{ ...td, fontSize: 13 }}>{inv.buyer_name || "-"}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(inv.total_amount)} €</td>
                    <td style={{ ...td, textAlign: "center" }}><Badge status={isStorno ? "storno" : inv.status} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => viewNcInvoice(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Eye size={13} /></button>
                        <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} /></button>
                        {inv.status === "fiscalized" && !inv.stornoed_by_invoice_id && !isStorno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} /></button>}
                      </div>
                    </td>
                  </tr>);
              })}</tbody>
            </table>}
          </div>
        ) : (<>
          {/* Products */}
          <div style={{ flex: "0 0 50%", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..." style={{ ...S.input, paddingLeft: 34, padding: "7px 10px 7px 34px", fontSize: 14 }} />
              </div>
            </div>
            <div style={{ padding: "5px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 4, overflowX: "auto" }}>
              <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
              {categories.map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 10, background: S.bg }}>
              {loading ? <div style={{ textAlign: "center", padding: 30 }}><Loader2 size={24} className="spin" /></div>
              : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                {filtered.map(p => {
                  const inCart = cart.find(c => c.id === p.id);
                  const gross = parseFloat(p.price) * (1 + parseFloat(p.vat_rate || 21) / 100);
                  return (
                    <button key={p.id} onClick={() => addToCart(p)} style={{ position: "relative", padding: "12px 8px", background: inCart ? "#f0fdf4" : "white", border: inCart ? `2px solid ${S.green}` : `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", textAlign: "center", fontFamily: S.font }}>
                      {inCart && <div style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, background: S.green, borderRadius: "50%", color: "white", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{inCart.quantity}</div>}
                      <div style={{ fontWeight: 600, fontSize: 13, color: S.dark, marginBottom: 4, lineHeight: 1.2 }}>{p.name}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: S.green }}>{fmt(gross)} €</div>
                    </button>
                  );
                })}
              </div>}
            </div>
          </div>

          {/* Partners */}
          <div style={{ flex: "0 0 50%", display: "flex", flexDirection: "column", borderTop: `2px solid ${S.border}` }}>
            <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={15} color={S.muted} />
              <span style={{ fontWeight: 700, fontSize: 14, color: S.dark }}>Partner</span>
              {selectedPartner && <span style={{ fontSize: 12, background: "#f0fdf4", color: S.green, padding: "2px 8px", borderRadius: 5, fontWeight: 600 }}>{selectedPartner.name}</span>}
              {!selectedPartner && !manualPartner && <span style={{ fontSize: 12, color: S.red }}>Obavezno</span>}
              <div style={{ flex: 1 }} />
              <button onClick={() => { setSelectedPartner(null); setManualPartner({ name: "", tax_id: "" }); }} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} />Ručni unos</button>
            </div>
            {manualPartner ? (
              <div style={{ padding: 14, background: "#fffbeb", borderBottom: `1px solid ${S.border}` }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 2 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Naziv *</label><input value={manualPartner.name} onChange={e => setManualPartner(p => ({ ...p, name: e.target.value }))} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB *</label><input value={manualPartner.tax_id} onChange={e => setManualPartner(p => ({ ...p, tax_id: e.target.value }))} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} /></div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setManualPartner(null)} style={S.btnSm("#f3f4f6", S.muted)}><X size={12} />Otkaži</button>
                  {manualPartner.name && <span style={{ fontSize: 12, color: S.green, display: "flex", alignItems: "center", gap: 4 }}><Check size={12} />Ručni partner aktivan</span>}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
                <input value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} placeholder="Pretraži partnere..." style={{ ...S.input, padding: "7px 10px", fontSize: 13, marginBottom: 8 }} />
                {filteredPartners.length === 0 ? <div style={{ textAlign: "center", padding: 20, color: "#9ca3af", fontSize: 14 }}>Nema partnera</div>
                : filteredPartners.map(p => (
                  <button key={p.id} onClick={() => { setSelectedPartner(p); setManualPartner(null); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: selectedPartner?.id === p.id ? `2px solid ${S.green}` : `1px solid ${S.border}`, background: selectedPartner?.id === p.id ? "#f0fdf4" : "white", borderRadius: 8, marginBottom: 4, cursor: "pointer", fontFamily: S.font, textAlign: "left" }}>
                    <Building size={15} color={selectedPartner?.id === p.id ? S.green : S.muted} />
                    <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div><div style={{ fontSize: 12, color: S.muted }}>{p.tax_id || "Bez PIB-a"} · {p.city || ""}</div></div>
                    {selectedPartner?.id === p.id && <CheckCircle size={16} color={S.green} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>)}
      </div>

      {/* RIGHT: Cart */}
      <div style={{ flex: "0 0 43%", display: "flex", flexDirection: "column", background: "white" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building size={18} color={S.dark} />
            <span style={{ fontWeight: 700, fontSize: 17, color: S.dark }}>Virman</span>
            <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 5, fontSize: 13, fontWeight: 600, color: S.muted }}>{cart.length}</span>
          </div>
          {cart.length > 0 && <button onClick={clearAll} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={13} />Obriši</button>}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
          {cart.length === 0 ? <div style={{ textAlign: "center", padding: "50px 16px", color: "#9ca3af" }}><Building size={40} strokeWidth={1.5} /><p style={{ marginTop: 12, fontSize: 15 }}>Dodajte stavke</p></div>
          : cart.map(item => {
            const up = item.custom_price !== null ? item.custom_price : item.price;
            const lineNet = item.quantity * up;
            let disc = 0;
            if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
            else if (item.discount_type === "fixed") disc = item.discount_value;
            const lineGross = (lineNet - disc) * (1 + item.vat_rate / 100);
            return (
              <div key={item.id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div><div style={{ fontSize: 13, color: "#9ca3af" }}>{fmt(up)} × {item.quantity}{disc > 0 ? ` − ${fmt(disc)}` : ""}</div></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => updateCartItem(item.id, { quantity: Math.max(1, item.quantity - 1) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={16} /></button>
                    <span style={{ fontSize: 15, fontWeight: 600, minWidth: 26, textAlign: "center" }}>{item.quantity}</span>
                    <button onClick={() => updateCartItem(item.id, { quantity: item.quantity + 1 })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, minWidth: 60, textAlign: "right" }}>{fmt(lineGross)} €</div>
                  <button onClick={() => setEditingItem(item.id === editingItem ? null : item.id)} style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: editingItem === item.id ? S.accent : S.muted }}><Edit3 size={15} /></button>
                  <button onClick={() => removeFromCart(item.id)} style={{ padding: 6, background: "#fef2f2", border: "none", borderRadius: 6, cursor: "pointer", color: "#dc2626" }}><X size={16} /></button>
                </div>
                {editingItem === item.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 90px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Cijena (neto)</label>
                      <input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 70px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Količina</label>
                      <input type="number" value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 110px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Popust</label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, flex: 1 }}><option value="none">Bez</option><option value="percentage">%</option><option value="fixed">€</option></select>
                        {item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, width: 60 }} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ borderTop: `2px solid ${S.border}`, padding: "14px 16px" }}>
            {/* Payment method */}
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "bank_transfer", label: "Virman", icon: Building }, { id: "other", label: "Ostalo", icon: Wallet }].map(m => (
                <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ flex: 1, padding: "12px 8px", border: "2px solid", borderColor: paymentMethod === m.id ? S.green : S.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: S.font }}><m.icon size={14} />{m.label}</button>
              ))}
            </div>
            {/* Payment due */}
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "end_of_month", l: "Kraj mjeseca" }, { id: "avans", l: "AVANS" }, { id: "unspecified", l: "Neodređeno" }].map(d => (
                <button key={d.id} onClick={() => setPaymentDue(d.id)} style={{ flex: 1, padding: "7px", border: "1px solid", borderColor: paymentDue === d.id ? S.accent : S.border, background: paymentDue === d.id ? "#eef2ff" : "white", color: paymentDue === d.id ? S.accent : S.muted, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}><Clock size={11} style={{ marginRight: 3 }} />{d.l}</button>
              ))}
            </div>
            {/* Invoice discount */}
            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
              <button onClick={() => setShowDiscountModal(true)} style={{ ...S.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : S.muted), flex: 1, justifyContent: "center" }}>
                <Percent size={13} />{invoiceDiscount.type !== "none" ? `Popust: ${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Popust na račun"}
              </button>
              {invoiceDiscount.type !== "none" && <button onClick={() => setInvoiceDiscount({ type: "none", value: 0 })} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.muted }}>Neto</span><span>{fmt(cartCalc.subtotal)} €</span></div>
            {cartCalc.vatGroups && Object.entries(cartCalc.vatGroups).map(([rate, g]) => (
              <div key={rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 13 }}><span style={{ color: S.muted }}>PDV {rate}%</span><span>{fmt(g.vat)} €</span></div>
            ))}
            {cartCalc.invDisc > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.orange }}>Popust</span><span style={{ color: S.orange }}>-{fmt(cartCalc.invDisc)} €</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: S.green }}>{fmt(cartCalc.total)} €</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { if (!buyerName) { showToast("Izaberite partnera", "error"); return; } setShowConfirm(true); }} disabled={fiscalizing} style={{ ...S.btn(fiscalizing ? "#6b7280" : S.green), flex: "0 0 63%", justifyContent: "center", padding: "13px", fontSize: 17 }}>
                {fiscalizing ? <Loader2 size={19} className="spin" /> : <Zap size={19} />}{fiscalizing ? "..." : "FISKALIZUJ"}
              </button>
              <button onClick={handleSaveDraft} disabled={saving} style={{ ...S.btn("#3b82f6"), flex: "0 0 35%", justifyContent: "center", padding: "13px", fontSize: 15 }}>
                {saving ? <Loader2 size={15} className="spin" /> : <Archive size={15} />}{saving ? "..." : "SAČUVAJ"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda fiskalizacije virmana">
        <p style={{ color: S.muted, fontSize: 15, marginBottom: 16 }}>Bezgotovinsi račun za: <strong>{buyerName}</strong> {buyerPib && `(PIB: ${buyerPib})`}</p>
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700 }}><span>Ukupno:</span><span style={{ color: S.green }}>{fmt(cartCalc.total)} €</span></div>
          <div style={{ fontSize: 14, color: S.muted, marginTop: 6 }}>Stavke: {cart.length} | Rok: {payDueLabel(paymentDue)}</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setShowConfirm(false)} style={S.btn("#f3f4f6", S.muted)}>Otkaži</button>
          <button onClick={handleFiscalize} style={{ ...S.btn(S.green), padding: "14px 24px", fontSize: 16 }}><Zap size={18} />Fiskalizuj</button>
        </div>
      </Modal>}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust na račun">
        <DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} />
      </Modal>}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
      {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    try {
      const t = localStorage.getItem("pos_token"); const u = localStorage.getItem("pos_user");
      if (t && u && u !== "undefined") return { token: t, user: JSON.parse(u) };
    } catch(e) { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); }
    return null;
  });
  const handleLogin = (token, user) => { localStorage.setItem("pos_token", token); localStorage.setItem("pos_user", JSON.stringify(user)); setAuth({ token, user }); };
  const handleLogout = () => { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); setAuth(null); };
  if (!auth) return <LoginScreen onLogin={handleLogin} />;
  return <POSApp user={auth.user} onLogout={handleLogout} />;
}
